/* ============================================================
   Dathent — Create Post screen
   Flow: compose → generating → done.
   Source: design pack pages/create-post.jsx + Create Post.html.
   ============================================================ */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { DeviceTimezoneLabel } from '../components/DeviceTimezoneLabel.jsx';
import { Icons as I } from '../components/pack-icons';
import { useUploadProgress } from '../components/UploadProgress.jsx';
import { BrandIcons } from '../components/pack-icons/brands';
import { tightestLimits, tightestMaxMedia, getCharacterLimit, getHashtagLimit, platformLabel } from '../components/preview/platformText.js';
import { useActivity } from '../components/AppShell';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { deriveImageAllowance } from '../lib/imageAllowance.js';
import { deriveTextAllowance } from '../lib/textAllowance.js';
import CreationAccessNotice, { creationAccessState } from '../components/billing/CreationAccessNotice.jsx';
import { useContentFeedContext } from '../state/ContentFeedContext.jsx';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';
import { startContentBatchOperation } from '../lib/operations.js';
import { brandKitEnabled, brandTypographyEnabled, videoReelsEnabled, videoReelsGA, videoCinematicEnabled, videoPublishV2Enabled, tiktokDirectPostEnabled, generationLevelPickerEnabled } from '../lib/flags.js';
import { videoQuotaInfo } from '../lib/video-quota.js';
import { VIDEO_ACCEPT, VIDEO_ACCEPT_SET, MAX_VIDEO_BYTES, MAX_VIDEO_SECONDS, readVideoMetaAndPoster } from '../lib/video-upload.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { isVideoAsset, resolvePosterUrl, formatDuration } from '../components/preview/normalizePost.js';
import { notify, friendlyErrorMessage } from '../lib/notifications.js';
import { ActiveJobsWidget } from '../components/dashboard';
import FreeLimitsBanner from '../components/FreeLimitsBanner.jsx';
import { GenerationLevelPicker, useGenerationLevel } from '../components/GenerationLevelPicker.jsx';
import { generationLevelPayloadFields } from '../lib/generationLevel.js';
import { track, trackOnce } from '../lib/funnel.js';
import { getFunnelSource } from '../lib/funnelSource.js';
import NoBrandGuard from '../components/NoBrandGuard.jsx';
import useOwnDraftController from '../components/freePublishing/useOwnDraftController.js';
import FreePublishingAllowance from '../components/freePublishing/FreePublishingAllowance.jsx';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { LockKeyhole } from 'lucide-react';
import { collectCreateRailReferences, effectiveCreateBrandChoice, hasCreateMeaningfulBrandContext, hasCreateVisualBrandContext, needsCreateBrandContext, resolveCreateBrandChoice, resolveCreatePostSubmission } from '../lib/createPostSubmission.js';

const PLATFORM_OPTIONS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'x', name: 'X' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'threads', name: 'Threads' },
  { id: 'telegram', name: 'Telegram' },
];

// Match the implemented publishing paths, not the optimistic registry flag for X.
// LinkedIn personal MP4 and Telegram native video are deployed; X still uploads
// images only. The server remains authoritative for owned media and file limits.
const PLATFORM_SUPPORTS_VIDEO = {
  instagram: true, facebook: true, threads: true, tiktok: true, telegram: true, linkedin: true,
  x: false,
};

// TikTok retains its existing own-video v2/compliance gate. LinkedIn and Telegram
// must not be disabled or silently deselected when the original clip is attached.
const VIDEO_UNSUPPORTED_CHANNELS = new Set(['x', 'tiktok']);

function ownVideoChannelUnsupported(platform, publishV2On) {
  return VIDEO_UNSUPPORTED_CHANNELS.has(platform) && !(publishV2On && platform === 'tiktok');
}

const TIKTOK_PRIVACY_TO_API = {
  PUBLIC_TO_EVERYONE: 'PUBLIC',
  MUTUAL_FOLLOW_FRIENDS: 'FRIENDS',
  FOLLOWER_OF_CREATOR: 'FRIENDS',
  SELF_ONLY: 'PRIVATE',
};

function isTikTokReconnectError(error) {
  const value = `${error?.code || ''} ${error?.message || ''}`;
  return error?.status === 401 || /reconnect|reauthor|revok|expir|invalid[_ ]?token|oauth|access[_ ]?token/i.test(value);
}

// Owner-reported bug fix (2026-07-06): the "Publishes to" chips under the
// format selector used to be four hardcoded platform names regardless of
// what the user actually has connected/selected — misleading (e.g. showing
// "LinkedIn" to someone with no LinkedIn account). Chips are now DERIVED from
// selectedPlatforms (the user's connected+draft platform picks, see
// ComposeForm) filtered by what the current postFormat can actually carry:
//   reel   -> video-capable platforms (PLATFORM_SUPPORTS_VIDEO)
//   slides -> TikTok only (server routes stills to the TikTok photo-deck path)
//   image  -> image/carousel-capable platforms (everything except tiktok,
//             which gets its own dedicated slides lane)
// Returns { chips: [{ id, label }], empty: boolean } so the caller can render
// a "connect an account" hint instead of a fake destination list.
function destChipsFor(postFormat, selectedPlatforms) {
  const picked = new Set(selectedPlatforms || []);
  if (postFormat === 'slides') {
    return picked.has('tiktok')
      ? { chips: [{ id: 'tiktok', label: platformLabel('tiktok') }], empty: false }
      : { chips: [], empty: true };
  }
  if (postFormat === 'reel') {
    const chips = ['instagram', 'tiktok', 'facebook', 'threads', 'linkedin', 'telegram']
      .filter((id) => picked.has(id))
      .map((id) => ({ id, label: id === 'instagram' ? 'Instagram Reels' : platformLabel(id) }));
    return { chips, empty: chips.length === 0 };
  }
  // image / carousel
  const chips = ['instagram', 'facebook', 'linkedin', 'x', 'threads']
    .filter((id) => picked.has(id))
    .map((id) => ({ id, label: platformLabel(id) }));
  return { chips, empty: chips.length === 0 };
}

// V-R9 New Post format selector (§7 C1). Order = tab/arrow-key order.
// Unified-source refactor 2026-07-09: the old POST_FORMATS/AI_POST_FORMATS
// allow-lists are gone. Every format now accepts BOTH sources — AI (generate)
// and My media (use existing) — via the SAME picker; only 'none' (text-only) is
// Image-exclusive, handled inline in the reconciliation effect below.
// Phase 4b — AI slides: server forces a carousel of N images when the body
// carries slideCount (generation-batch-service.ts:2851, 2–5 accepted).
const SLIDES_MIN = 2;
const SLIDES_MAX = 5;
// vpost pack — top-level format-first selector (showReel only): Image/Carousel
// | Video | Slides. The pack demoted Slides to a question-link (HANDBOOK
// "Structural decision"), but the owner overrode that on live UX (2026-07-07):
// the link read as part of the Video help line and the format switch it
// triggered was invisible. Slides is a real — visually muted — third segment
// (.dv-fmt-quiet); arrow-key nav = topFormatKeyNav over this trio.
const TOP_FORMATS = ['image', 'reel', 'slides'];

// Research hold: NO new content generation while a research run is active —
// the foundation is being rebuilt, so generating from the old one is wasted
// or wrong (product decision: the foundation always lands first). Mirrors
// the server guards in content-worker/strategy-worker; read live, flags only.
const RESEARCH_HOLD_STATUSES = ['queued', 'gathering', 'analyzing', 'synthesizing', 'verifying'];

function researchFlagsOn() {
  try {
    return typeof window !== 'undefined' && (
      window.localStorage.getItem('dathent.research.enabled') !== '0'
      || window.localStorage.getItem('dathent.research.onboarding') === '1'
    );
  } catch (_) {
    return false;
  }
}

function useResearchHold() {
  const [hold, setHold] = useState(false);
  useEffect(() => {
    if (!researchFlagsOn()) return undefined;
    let cancelled = false;
    let timer = null;
    const probe = async () => {
      try {
        const listRes = await api.research.list().catch(() => null);
        if (cancelled) return;
        const latest = ((listRes?.data ?? listRes)?.reports || [])[0] || null;
        const holding = Boolean(latest && RESEARCH_HOLD_STATUSES.includes(latest.status));
        setHold(holding);
        if (holding) {
          api.invalidateCache?.('/api/research');
          timer = setTimeout(probe, 20000);
        }
      } catch (_) {
        if (!cancelled) setHold(false);
      }
    };
    probe();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);
  return hold;
}

function ResearchHoldPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="cp-page">
      <div className="cp-root">
        <div className="card sj" style={{ maxWidth: 560, margin: '48px auto' }}>
          <div className="sj-glow" aria-hidden="true" />
          <header className="sj-h">
            <span className="sj-t">{t('createPost.researchHoldTitle')}</span>
            <span className="sj-pill"><i className="sj-pill-dot" /> {t('journey.stRunning')}</span>
            <span className="sj-s">{t('createPost.researchHoldSub')}</span>
          </header>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/app/research')}>
              {t('contentPlanScreen.researchRunningCta')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/app')}>
              {t('createPost.researchHoldBack')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Max reference images per post. The gpt-image composition path (>=2 refs ->
// /images/edits image[]) uses up to this many; more mostly adds generation
// latency toward the platform's ~120s ceiling for little visual gain.
const MAX_REFERENCE_IMAGES = 3;

// vpost pack — New Post Video composer, "My photos" scenes rail. A SEPARATE,
// higher cap than MAX_REFERENCE_IMAGES (mirrors BrandKitScreen's
// MAX_REEL_FRAMES) — the backend slices at 8 regardless.
const MAX_REEL_FRAMES = 8;

// Allowed attachment MIME types + size cap. Defense-in-depth: the backend
// re-validates, but rejecting client-side gives instant feedback and avoids
// shipping junk/oversized payloads. Images drive composition; pdf/doc/txt are
// text references only.
const ALLOWED_ATTACHMENT_MIME = /^(image\/(png|jpe?g|webp|gif)|application\/pdf|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document|text\/plain)$/i;
const MAX_ATTACHMENT_MB = 15;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;

// Compact a (possibly very long) attachment filename for its chip while keeping
// the extension visible — e.g. "rubbish-coll…7NN5G.jpg". Middle-truncation keeps
// both the start and the unique tail/extension; the full name stays available via
// the chip's title tooltip. Keeps chips narrow so they flow horizontally instead
// of stacking into a tall vertical list.
function shortenFileName(name, max = 24) {
  const value = String(name || '');
  if (value.length <= max) return value;
  const dot = value.lastIndexOf('.');
  const ext = dot > 0 && value.length - dot <= 6 ? value.slice(dot) : '';
  const base = value.slice(0, value.length - ext.length);
  const budget = Math.max(8, max - ext.length - 1); // 1 char for the ellipsis
  const head = Math.ceil(budget * 0.62);
  const tail = budget - head;
  return `${base.slice(0, head)}…${tail > 0 ? base.slice(-tail) : ''}${ext}`;
}

const RECENT_TOPIC_FALLBACKS = [
  '30-day plan for launch',
  'Behind-the-scenes process series',
  'Customer story carousel',
  'Educational series',
];

// Visual style palettes — canonical keys + swatch colors mirror Edit Strategy
// (ES_PALETTES) so the picker here shows exactly what the user configured.
const CP_VISUAL_PALETTES = [
  { v: 'premium_dark', i18n: 'editStrategy.palettePremiumDarkLabel', palette: ['#0F1419', '#3A2E58', '#766858', '#D9CBB3'] },
  { v: 'minimalist',   i18n: 'editStrategy.paletteMinimalistLabel',  palette: ['#FAFAFA', '#E5E5E5', '#9CA3AF', '#111111'] },
  { v: 'modern',       i18n: 'editStrategy.paletteModernLabel',      palette: ['#5B7EFF', '#A555F5', '#0E0E10', '#F4F4F4'] },
  { v: 'vibrant',      i18n: 'editStrategy.paletteVibrantLabel',     palette: ['#FF6F61', '#FFD66B', '#E455CF', '#FFFFFF'] },
  { v: 'playful',      i18n: 'editStrategy.palettePlayfulLabel',     palette: ['#FFB36B', '#FF7AA9', '#FFE89A', '#23222B'] },
  { v: 'corporate',    i18n: 'editStrategy.paletteCorporateLabel',   palette: ['#0E5BC1', '#1E40AF', '#FFFFFF', '#1F2128'] },
  { v: 'vintage',      i18n: 'editStrategy.paletteVintageLabel',     palette: ['#8C6E4A', '#C99966', '#F3EBDD', '#3B2A1B'] },
];
const CP_VISUAL_KEYS = CP_VISUAL_PALETTES.map((p) => p.v);

// Legacy → canonical visual-style remap (mirrors EditStrategy.parsePalette) so
// older brand_profiles still light up the right swatches here.
const LEGACY_VISUAL_REMAP = {
  natural: 'vintage', editorial: 'minimalist', studio: 'minimalist',
  electric: 'modern', matte: 'premium_dark', sun: 'playful',
};
function normalizeVisualKey(raw) {
  const k = String(raw || '').toLowerCase();
  if (CP_VISUAL_KEYS.includes(k)) return k;
  return LEGACY_VISUAL_REMAP[k] || null;
}

// Tone preset → i18n label (mirrors EditStrategy.ES_TONE_I18N) so the brand
// summary surfaces the tones the user actually picked in Edit Strategy.
const CP_TONE_I18N = {
  friendly: 'editStrategy.toneFriendlyLabel',
  professional: 'editStrategy.toneProfessionalLabel',
  playful: 'editStrategy.tonePlayfulLabel',
  authoritative: 'editStrategy.toneAuthoritativeLabel',
  inspirational: 'editStrategy.toneInspirationalLabel',
  educational: 'editStrategy.toneEducationalLabel',
  custom: 'editStrategy.toneCustomLabel',
};

// Read a File into a data URL (base64) via FileReader.
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

/* ============================================================
   Visual style picker (multi-select dropdown)
   Source: design pack pages/create-post.jsx VisualPicker. One style selected
   => consistent look (forced on every post); several => we mix across them.
   ============================================================ */
function VisualPicker({ selected, setSelected }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (v) => {
    const next = new Set(selected);
    // Deselecting the last style is allowed now — an empty set means "Default
    // (let the strategy's per-post rotation decide)", a real, selectable state.
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setSelected(next);
  };

  const chosen = CP_VISUAL_PALETTES.filter((p) => selected.has(p.v));
  const isDefault = chosen.length === 0;
  const summary = isDefault
    ? t('createPost.visualDefaultStyle')
    : chosen.length === 1
      ? t(chosen[0].i18n)
      : chosen.length === CP_VISUAL_PALETTES.length
        ? t('createPost.visualAllStylesAutoMix')
        : t('createPost.visualNStylesMix', { n: chosen.length });

  return (
    <div className={`cp-vis ${open ? 'is-open' : ''}`} ref={ref}>
      <button type="button" className="cp-vis-trigger" onClick={() => setOpen((o) => !o)}>
        <span className="cp-vis-stack">
          {isDefault ? (
            <span className="cp-vis-mini cp-vis-mini-auto" aria-hidden="true"><i /><i /><i /><i /></span>
          ) : (
            chosen.slice(0, 3).map((p, i) => (
              <span key={p.v} className="cp-vis-mini" style={{ zIndex: 3 - i }}>
                <i style={{ background: p.palette[0] }} /><i style={{ background: p.palette[1] }} />
                <i style={{ background: p.palette[2] }} /><i style={{ background: p.palette[3] }} />
              </span>
            ))
          )}
        </span>
        <span className="cp-vis-summary">{summary}</span>
        <I.chevDown size={13} className="cp-vis-caret" />
      </button>

      <div className="cp-vis-panel">
        <div className="cp-vis-panel-h">
          <span>{t('createPost.visualStylesPanelHeader')}</span>
          <span className="cp-vis-count mono">{t('createPost.visualSelectedCount', { n: chosen.length })}</span>
        </div>
        <button
          type="button"
          className={`cp-vis-default ${isDefault ? 'is-on' : ''}`}
          onClick={() => setSelected(new Set())}
        >
          <span className="cp-vis-default-txt">
            <span className="cp-vis-default-t">{t('createPost.visualDefaultStyle')}</span>
            <span className="cp-vis-default-h">{t('createPost.visualDefaultStyleHint')}</span>
          </span>
          <span className="cp-vis-opt-check">{isDefault && <I.check size={11} />}</span>
        </button>
        <div className="cp-vis-grid">
          {CP_VISUAL_PALETTES.map((p) => {
            const on = selected.has(p.v);
            return (
              <button key={p.v} type="button" className={`cp-vis-opt ${on ? 'is-on' : ''}`} onClick={() => toggle(p.v)}>
                <span className="cp-vis-opt-sw">
                  <i style={{ background: p.palette[0] }} /><i style={{ background: p.palette[1] }} />
                  <i style={{ background: p.palette[2] }} /><i style={{ background: p.palette[3] }} />
                </span>
                <span className="cp-vis-opt-l">{t(p.i18n)}</span>
                <span className="cp-vis-opt-check">{on && <I.check size={11} />}</span>
              </button>
            );
          })}
        </div>
        <div className="cp-vis-panel-f">
          <I.info size={11} />
          <span>{t('createPost.visualPickerHint')}</span>
        </div>
      </div>
    </div>
  );
}

/* New Post Visual block, clarified (ported from visual-block-pack). Two calm,
   distinct controls — Style + Colours — each with an outcome line that resolves
   what happens across the WHOLE batch (variety vs consistency; colours on/off).
   Behaviour unchanged: [] styles = Default/auto-mix rotation, [id] = consistent,
   [id,id,…] = rotate; colours = one batch-level on/off. */
const VB_ROTATE_SVG = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>;
const VB_CHECK_SVG = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 12 5 5 9-11" /></svg>;
const VB_COLORS_SVG = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="10.5" r="2.5" /><circle cx="8.5" cy="7.5" r="2.5" /><circle cx="6.5" cy="12.5" r="2.5" /></svg>;
const VB_COLORSOFF_SVG = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l18 18M10.5 6.1A8 8 0 0 1 20 14M6.1 10.5A8 8 0 0 0 12 22" /></svg>;

// Stable per-pillar accent for the suggestion-card tag. Hash the pillar name
// into a small fixed palette so the same pillar always reads the same colour.
const PILLAR_TAG_COLORS = ['#5BBFB5', '#D4A53F', '#8B7AFF', '#4ADE80', '#F472B6', '#60A5FA', '#FB923C', '#34D399'];
function pillarColor(name) {
  if (!name) return null;
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PILLAR_TAG_COLORS[h % PILLAR_TAG_COLORS.length];
}

// Style + Colours controls only. The brand on/off "gateway" and the group
// wrapper live in the parent now (npform redesign) — this renders the two
// controls that sit INSIDE the brand group when the brand is on.
function VisualControls({ postCount, selected, setSelected, colorsOn, setColorsOn, brandColors, typography, typographyOn, setTypographyOn }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Fullscreen viewer for the chosen font specimen (unconditional hook — this
  // component always renders, so topology is safe regardless of the flag).
  const [typoView, setTypoView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const n = Math.max(1, postCount || 1);
  const chosen = CP_VISUAL_PALETTES.filter((p) => selected.has(p.v));
  const isDefault = chosen.length === 0;
  const name = (p) => t(p.i18n);
  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    setSelected(next);
  };

  const trig = isDefault
    ? <><b>{t('createPost.styleDefault')}</b> · {t('createPost.styleAutoMix')}</>
    : chosen.length === 1
      ? <b>{name(chosen[0])}</b>
      : <><b>{t('createPost.styleNStyles', { n: chosen.length })}</b> · {t('createPost.styleRotate')}</>;

  let styleOutcome;
  if (isDefault) {
    styleOutcome = (
      <div className="nvbk-outcome is-rotate">{VB_ROTATE_SVG}<span>{t('createPost.outcomeRotate')}
        <span className="nvbk-seq">{CP_VISUAL_PALETTES.slice(0, 3).map((p, i) => <i key={i} style={{ background: p.palette[0] }} />)}<span className="nvbk-more">+{Math.max(0, CP_VISUAL_PALETTES.length - 3)}</span></span>
      </span></div>
    );
  } else if (chosen.length === 1) {
    styleOutcome = (
      <div className="nvbk-outcome">{VB_CHECK_SVG}<span>{n === 1
        ? t('createPost.outcomeOne', { style: name(chosen[0]) })
        : t('createPost.outcomeConsistent', { n, style: name(chosen[0]) })}</span></div>
    );
  } else {
    styleOutcome = (
      <div className="nvbk-outcome is-rotate">{VB_ROTATE_SVG}<span>{t('createPost.outcomeAlternate', { styles: chosen.map(name).join(t('createPost.outcomeAnd')) })}
        <span className="nvbk-seq">{[0, 1, 0, 1].map((idx, i) => <i key={i} style={{ background: (chosen[idx] || chosen[0]).palette[0] }} />)}</span>
      </span></div>
    );
  }

  const hasColors = Array.isArray(brandColors) && brandColors.filter(Boolean).length > 0;
  const colors = hasColors ? brandColors.filter(Boolean) : [];
  // Trigger swatches: default = 3 hatched; one style = that style's palette;
  // several = the lead colour of each chosen style.
  const miniColors = isDefault
    ? [null, null, null]
    : chosen.length === 1
      ? chosen[0].palette.slice(0, 3)
      : chosen.slice(0, 3).map((p) => p.palette[0]);

  return (
    <>
      <div className="nvbk-ctl" ref={ref}>
        <div className="nvbk-crow">
          <span className="nvbk-lbl"><span className="nvbk-lbl-n">{t('createPost.styleLabel')}</span><span className="nvbk-lbl-h">{t('createPost.styleSub')}</span></span>
          <button type="button" className="nvbk-strig" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            <span className={`nvbk-mini ${isDefault ? 'is-auto' : ''}`}>
              {miniColors.map((c, i) => <i key={i} style={c ? { background: c } : undefined} />)}
            </span>
            <span className="nvbk-strig-txt">{trig}</span>
            <I.chevDown size={12} className={`nvbk-cv ${open ? 'is-open' : ''}`} />
          </button>
        </div>
        {open && (
          <div className="nvbk-spop">
            <button type="button" className={`nvbk-auto ${isDefault ? 'is-sel' : ''}`} onClick={() => setSelected(new Set())}>
              <span className="nvbk-ck">{isDefault && <I.check size={11} />}</span>
              <span className="nvbk-auto-x">
                <span className="nvbk-auto-n">{t('createPost.styleDefault')} <span className="nvbk-auto-tag">{t('createPost.styleAutoMix')}</span></span>
                <span className="nvbk-auto-d">{t('createPost.styleDefaultDesc')}</span>
              </span>
            </button>
            <div className="nvbk-sec">{t('createPost.stylePickHint')}</div>
            <div className="nvbk-opts">
              {CP_VISUAL_PALETTES.map((p) => {
                const sel = selected.has(p.v);
                return (
                  <button key={p.v} type="button" className={`nvbk-sopt ${sel ? 'is-sel' : ''}`} onClick={() => toggle(p.v)}>
                    <span className="nvbk-ck">{sel && <I.check size={11} />}</span>
                    <span className="nvbk-sw4">{p.palette.map((c, i) => <i key={i} style={{ background: c }} />)}</span>
                    <span className="nvbk-nm">{name(p)}</span>
                  </button>
                );
              })}
            </div>
            <div className="nvbk-foot">
              <span className="nvbk-foot-c">{isDefault ? t('createPost.styleDefaultSelected') : t('createPost.styleNSelected', { n: chosen.length })}</span>
              <button type="button" className="nvbk-done" onClick={() => setOpen(false)}>{t('createPost.styleDone')}</button>
            </div>
          </div>
        )}
        {styleOutcome}
      </div>

      {hasColors && (
        <>
          <div className="nvbk-div" />
          <div className="nvbk-ctl">
            <div className="nvbk-crow">
              <span className="nvbk-lbl"><span className="nvbk-lbl-n">{t('createPost.colorsRowLabel')}</span><span className="nvbk-lbl-h">{t('createPost.colorsSub')}</span></span>
              <div className={`nvbk-ctog ${colorsOn ? '' : 'is-off'}`}>
                <span className="nvbk-sw-mini">{colors.slice(0, 5).map((c, i) => <i key={i} style={{ background: c }} />)}</span>
                <button type="button" role="switch" aria-checked={colorsOn} className={`cp-sw ${colorsOn ? '' : 'is-off'}`} onClick={() => setColorsOn(!colorsOn)}><i /></button>
              </div>
            </div>
            <div className="nvbk-outcome">{colorsOn ? VB_COLORS_SVG : VB_COLORSOFF_SVG}<span className={colorsOn ? '' : 'nvbk-muted'}>{colorsOn ? t('createPost.outcomeColorsOn') : t('createPost.outcomeColorsOff')}</span></div>
            <button type="button" className="nvbk-collink" onClick={() => navigate('/app/plan/edit-strategy#bc-colours')}>{t('createPost.colorsEditLink')}<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg></button>
          </div>
        </>
      )}

      {typography && (
        <>
          <div className="nvbk-div" />
          <div className="nvbk-ctl">
            <div className="nvbk-crow">
              {/* Mirror the Colours row anatomy 1:1 (label+hint, mini preview,
                  edit link) — the hint says this is the BRAND's custom style,
                  the specimen thumb shows WHICH font without naming games. */}
              <span className="nvbk-lbl"><span className="nvbk-lbl-n">{t('typography.postRowLabel')}</span><span className="nvbk-lbl-h">{t('typography.postRowHint')}</span></span>
              <div className={`nvbk-ctog ${typographyOn ? '' : 'is-off'}`}>
                {typography.referenceUrl
                  ? <button type="button" className="aa-thumb" style={{ backgroundImage: `url("${typography.referenceUrl}")` }} title={t('typography.viewOpen')} aria-label={t('typography.viewOpen')} onClick={() => setTypoView(true)} />
                  : <span className="aa-mini">Aa</span>}
                <button type="button" role="switch" aria-checked={typographyOn} className={`cp-sw ${typographyOn ? '' : 'is-off'}`} onClick={() => setTypographyOn(!typographyOn)}><i /></button>
              </div>
            </div>
            <div className="nvbk-outcome">{typographyOn ? VB_CHECK_SVG : VB_COLORSOFF_SVG}<span className={typographyOn ? '' : 'nvbk-muted'}>{typographyOn ? t('typography.postOutcomeOn', { font: typography.font?.family || t('typography.mediaReference') }) : t('typography.postOutcomeOff')}</span></div>
            <button type="button" className="nvbk-collink" onClick={() => navigate('/app/brandkit')}>{t('typography.postEditLink')}<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg></button>
          </div>
          {typoView && typography.referenceUrl && (
            <div className="cp-pick-bg" onClick={() => setTypoView(false)}>
              <div className="typo-view card" role="dialog" aria-modal="true" aria-label={typography.font?.family || t('typography.postRowLabel')} onClick={(e) => e.stopPropagation()}>
                <button type="button" className="x2 typo-view-x" onClick={() => setTypoView(false)} aria-label={t('createPost.close')}><I.x size={16} /></button>
                <img className="typo-view-img" src={typography.referenceUrl} alt={typography.font?.family || t('typography.postRowLabel')} />
                <div className="typo-view-meta">
                  <div><b>{typography.font?.family || t('typography.mediaReference')}</b><span>{t('typography.postRowHint')}</span></div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setTypoView(false); navigate('/app/brandkit'); }}>{t('typography.postEditLink')}</button>
                </div>
                <p className="typo-view-note">{t('typography.viewNote')}</p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ============================================================
   Compose form
   ============================================================ */
/* Quiet inline segmented control for the New Post source toggles.
   TASK-321: a native `disabled` attribute suppresses pointer events in
   Chrome, so a `title` tooltip on (or inside) a disabled button never
   appears, and the button drops out of the tab order — keyboard users got
   no explanation at all for a locked option. `aria-disabled` + an early
   return in onClick keeps the button focusable/hoverable (tooltip works,
   screen readers announce the disabled state) while still refusing the
   click. Serves both the AI-writes and AI-media toggles. */
function SourceSeg({ value, onChange, options, ariaLabel }) {
  return (
    <span className="cp-seg" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          aria-describedby={o.describedBy}
          aria-disabled={o.disabled ? 'true' : undefined}
          title={o.title}
          className={`cp-seg-btn ${value === o.v ? 'is-on' : ''}`}
          onClick={() => { if (o.disabled) return; onChange(o.v); }}
        >
          {o.l}
        </button>
      ))}
    </span>
  );
}

/* Library picker modal — reuses the Brand Kit asset library (api.brandAssets)
   so the user can choose their own images. Used both as post MEDIA ('My
   image') and could back a guide picker later. Multi-select up to `max`. */
const PICK_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const PICK_ACCEPT_SET = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const PICK_MAX_BYTES = 5 * 1024 * 1024;
const PICK_TAGS = ['logo', 'product', 'people', 'transport', 'style'];
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error('read failed'));
    r.readAsDataURL(file);
  });
}

// Unified add+upload picker (ported from brandkit2-04 / newpost-06). Tag
// filters + an upload-tile that SAVES a new image to the Brand Kit library
// (R2) and auto-selects it. mode 'guide' = AI references, 'media' = My-image.
// ===== Caption editor (New Post "I'll write it") — ported from caption-editor-pack.
// Quiet tools, the text IS the post (plain text + emoji + hashtags only). Limits
// come from the shared per-platform source (platformText.js) so they track the
// selected networks and extend with one map entry. "Improve" is metered by
// caption_assists_monthly (the human stays the author — accept/dismiss a diff).
const CE_EMOJI = {
  recent: ['✨', '💧', '🌿', '😊', '🙌', '🤍', '🔥', '⭐'],
  smileys: ['😀', '😄', '😍', '🥰', '😊', '😎', '🤩', '🥳', '😉', '🙂', '😇', '🤗', '😌', '😅', '🙃', '😂'],
  gestures: ['👋', '🙌', '👌', '👍', '🤝', '💪', '🙏', '✌️', '🤞', '👏', '🫶', '🤙'],
  objects: ['💧', '📷', '📦', '🛍️', '💎', '🎁', '📌', '📈', '🧴', '🧖', '💆', '⏰'],
  symbols: ['✨', '⭐', '💫', '❤️', '🤍', '✅', '➡️', '💯', '🔔', '♻️', '🔥', '🌟'],
  nature: ['🌿', '🌸', '🌷', '🌞', '🌙', '🍃', '🌊', '🪴', '🌼', '🍀', '🌺', '☀️'],
};
const CE_TABS = [['smileys', '😊'], ['gestures', '👋'], ['objects', '💧'], ['symbols', '✨'], ['nature', '🌿']];
const CE_MODES = ['tighten', 'punchier', 'grammar', 'hook', 'hashtags'];
const HASHTAG_FALLBACKS = ['#smallbusiness', '#marketing', '#contentcreation', '#brand', '#tips'];

function toHashtag(phrase) {
  if (typeof phrase !== 'string') return null;
  const words = phrase.trim().replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const tag = words.map((w) => w[0].toUpperCase() + w.slice(1)).join('');
  return tag.length >= 2 ? `#${tag}` : null;
}

// "From your plan" hashtags, derived client-side (no plan field stores hashtags):
// strategy/brand pillar names → Title-cased tags, then brand wordsToUse, deduped.
export function derivePlanHashtags(strategy, brand) {
  const out = [];
  const seen = new Set();
  const push = (raw) => {
    const tag = toHashtag(raw);
    if (tag && !seen.has(tag.toLowerCase())) { seen.add(tag.toLowerCase()); out.push(tag); }
  };
  const pillars = strategy?.strategy?.contentPillars || strategy?.contentPillars || strategy?.pillars || brand?.contentPillars || [];
  (Array.isArray(pillars) ? pillars : []).forEach((p) => push(p?.name || p));
  (Array.isArray(brand?.wordsToUse) ? brand.wordsToUse : []).forEach((w) => push(w));
  return out.length ? out.slice(0, 8) : HASHTAG_FALLBACKS;
}

// Word-level diff (LCS) for the AI-improve preview: left = original tokens with
// removed words flagged, right = suggestion tokens with added words flagged.
// Captions are short, so the O(m·n) table is fine; guarded for safety.
function wordDiff(a, b) {
  const aw = String(a).split(/(\s+)/);
  const bw = String(b).split(/(\s+)/);
  const m = aw.length, n = bw.length;
  if (m * n > 400000) return { left: [{ t: String(a) }], right: [{ t: String(b) }] };
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      dp[i][j] = aw[i] === bw[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const left = [], right = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (aw[i] === bw[j]) { left.push({ t: aw[i] }); right.push({ t: bw[j] }); i += 1; j += 1; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { left.push({ t: aw[i], del: true }); i += 1; }
    else { right.push({ t: bw[j], ins: true }); j += 1; }
  }
  while (i < m) { left.push({ t: aw[i], del: true }); i += 1; }
  while (j < n) { right.push({ t: bw[j], ins: true }); j += 1; }
  return { left, right };
}

function renderDiffTokens(tokens, kind) {
  return tokens.map((tk, i) => (tk[kind] && tk.t.trim())
    ? <span key={i} className={kind}>{tk.t}</span>
    : <React.Fragment key={i}>{tk.t}</React.Fragment>);
}

const CE_ICONS = {
  emoji: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><circle cx="12" cy="12" r="9" /><path d="M8.5 14a4 4 0 0 0 7 0" /><path d="M9 9h.01M15 9h.01" /></svg>,
  hash: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" /></svg>,
};

export function CaptionEditor({ value, onChange, platforms, planHashtags, captionAssists, onAssistUsed, aiDisabled = false, id, disabled = false, maxLength = 4000, showPlanHashtags = true, t }) {
  const taRef = useRef(null);
  const [pop, setPop] = useState(null); // 'emoji' | 'hashtag' | 'ai' | null
  const [emojiTab, setEmojiTab] = useState('smileys');
  const [hquery, setHquery] = useState('');
  const [ai, setAi] = useState(null); // { loading?, mode, suggestion?, error? }

  const plats = Array.isArray(platforms) ? platforms : [];
  const tightest = useMemo(() => tightestLimits(plats), [plats.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const len = value.length;
  const charLimit = tightest.charLimit || 2200;
  const pct = Math.min(100, Math.round((len / charLimit) * 100));
  const cState = len > charLimit ? 'over' : len > charLimit * 0.9 ? 'near' : '';

  const hashtagCount = (value.match(/#[\p{L}\p{N}_]+/gu) || []).length;
  const hashtagLimit = Number.isFinite(tightest.hashtagLimit) ? tightest.hashtagLimit : null;
  const hashtagOver = hashtagLimit != null && hashtagCount > hashtagLimit;
  const hashtagCapPlatform = hashtagLimit != null ? plats.find((p) => getHashtagLimit(p) === hashtagLimit) : null;

  const insertAtCaret = (text) => {
    if (disabled) return;
    const ta = taRef.current;
    if (!ta) { onChange(value + text); return; }
    const s = ta.selectionStart ?? value.length;
    const e = ta.selectionEnd ?? value.length;
    onChange(value.slice(0, s) + text + value.slice(e));
    requestAnimationFrame(() => { try { ta.focus(); ta.selectionStart = ta.selectionEnd = s + text.length; } catch (_) { /* noop */ } });
  };

  const addHashtag = (raw) => {
    if (disabled) return;
    const tag = raw.startsWith('#') ? raw : `#${String(raw).replace(/[^\p{L}\p{N}_]/gu, '')}`;
    if (tag.length < 2 || value.toLowerCase().includes(tag.toLowerCase())) return;
    const sep = value.length && !/\s$/.test(value) ? ' ' : '';
    onChange(value + sep + tag + ' ');
  };

  const planTags = showPlanHashtags ? (planHashtags && planHashtags.length ? planHashtags : HASHTAG_FALLBACKS) : [];
  const q = hquery.trim().replace(/^#/, '').toLowerCase();
  const matches = q
    ? [...planTags.filter((tg) => tg.toLowerCase().includes(q) && !planTags.slice(0, 0).length), ...(q.length >= 2 ? [`#${q[0].toUpperCase()}${q.slice(1)}`] : [])]
        .filter((tg, i, a) => a.findIndex((x) => x.toLowerCase() === tg.toLowerCase()) === i)
    : [];

  const assistLimit = captionAssists?.limit;
  const assistRemaining = captionAssists?.remaining;
  const noAssistsLeft = assistLimit != null && (assistRemaining ?? 0) <= 0;
  const canImprove = value.trim().length >= 3 && !ai?.loading && !noAssistsLeft && !aiDisabled && !disabled;

  const runImprove = (mode) => {
    if (!canImprove) return;
    setPop(null);
    const original = value; // what we diff the suggestion against
    setAi({ loading: true, mode });
    api.content.improveCaption({ text: value, mode, platforms: plats })
      .then((res) => {
        const suggestion = (res?.data ?? res)?.suggestion;
        if (suggestion) setAi({ mode, suggestion, original });
        else setAi({ mode, error: t('createPost.improveFailed') });
        onAssistUsed?.();
      })
      .catch((err) => { setAi({ mode, error: friendlyErrorMessage(err) }); onAssistUsed?.(); });
  };

  return (
    <div className="cap-ed">
      <div className="ed-body">
        <textarea
          ref={taRef}
          id={id}
          disabled={disabled}
          className="ta"
          rows={4}
          value={value}
          onChange={(e) => { if (!disabled) onChange(e.target.value); }}
          placeholder={t('createPost.captionPlaceholder')}
          maxLength={maxLength}
        />
      </div>

      {ai && (
        <div className="aiprev">
          <div className="aiprev-h">
            {ai.loading ? <span className="spin" /> : <I.sparkles size={13} />}
            {t(`createPost.improve_${ai.mode}`)} · {t('createPost.suggestion')}
          </div>
          {ai.loading ? (
            <div className="aiprev-body"><div className="diff was">{t('createPost.improving')}</div></div>
          ) : ai.error ? (
            <>
              <div className="aiprev-body"><div className="diff" style={{ color: 'var(--danger, #F87171)' }}>{ai.error}</div></div>
              <div className="aiprev-foot"><span className="sp" /><button type="button" className="bp rej" onClick={() => setAi(null)}>{t('createPost.dismiss')}</button></div>
            </>
          ) : (
            <>
              {(() => {
                const d = wordDiff(ai.original ?? value, ai.suggestion);
                return (
                  <div className="aiprev-body">
                    <div className="diff was"><span className="lbl">{t('createPost.yours')}</span>{renderDiffTokens(d.left, 'del')}</div>
                    <div className="diff"><span className="lbl">{t('createPost.suggested')}</span>{renderDiffTokens(d.right, 'ins')}</div>
                  </div>
                );
              })()}
              <div className="aiprev-foot">
                <span className="sp" />
                <button type="button" className="bp rej" onClick={() => setAi(null)}>{t('createPost.dismiss')}</button>
                <button type="button" className="bp acc" disabled={disabled} onClick={() => { if (!disabled) { onChange(ai.suggestion); setAi(null); } }}>{t('createPost.useThis')}</button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="tb">
        {pop && <div className="cap-scrim" onClick={() => setPop(null)} />}
        <div className="tb-l">
          <button type="button" disabled={disabled} className={`tbtn ${pop === 'emoji' ? 'on' : ''}`} onClick={() => setPop(pop === 'emoji' ? null : 'emoji')} title={t('createPost.emoji')}>{CE_ICONS.emoji}</button>
          <button type="button" disabled={disabled} className={`tbtn ${pop === 'hashtag' ? 'on' : ''}`} onClick={() => setPop(pop === 'hashtag' ? null : 'hashtag')} title={t('createPost.hashtags')}>{CE_ICONS.hash}</button>
          {<><span className="tb-sep" />
          <button type="button" className={`tbtn ai ${pop === 'ai' ? 'on' : ''}`} disabled={!canImprove && pop !== 'ai'} onClick={() => setPop(pop === 'ai' ? null : 'ai')} title={t('createPost.improveWithAi')}>
            <I.sparkles size={14} /> {t('createPost.improve')}
          </button>
          {assistLimit != null && (
            <span className="cap-assists" title={t('createPost.improveWithAi')}>{t('createPost.nAssistsLeft', { count: Math.max(0, assistRemaining ?? 0) })}</span>
          )}</>}

          {!disabled && pop === 'emoji' && (
            <div className="pop" style={{ bottom: 'calc(100% + 10px)', left: 0 }}>
              <div className="pop-arrow" />
              <div className="em-tabs">{CE_TABS.map(([k, e]) => <button type="button" key={k} className={emojiTab === k ? 'on' : ''} onMouseDown={(ev) => ev.preventDefault()} onClick={() => setEmojiTab(k)}>{e}</button>)}</div>
              <div className="em-recent"><div className="l">{t('createPost.recentlyUsed')}</div>
                <div className="em-grid">{CE_EMOJI.recent.map((e, i) => <button type="button" key={i} onMouseDown={(ev) => ev.preventDefault()} onClick={() => insertAtCaret(e)}>{e}</button>)}</div>
              </div>
              <div className="em-grid">{CE_EMOJI[emojiTab].map((e, i) => <button type="button" key={i} onMouseDown={(ev) => ev.preventDefault()} onClick={() => insertAtCaret(e)}>{e}</button>)}</div>
            </div>
          )}

          {!disabled && pop === 'hashtag' && (
            <div className="hh" style={{ bottom: 'calc(100% + 10px)', left: 0 }}>
              <div className="hh-arrow" />
              <div className="hh-in">{CE_ICONS.hash}<input value={hquery} onChange={(e) => setHquery(e.target.value)} placeholder={t('createPost.typeHashtag')} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (hquery.trim()) { addHashtag(hquery.trim()); setHquery(''); } } }} /></div>
              {showPlanHashtags && <div className="hh-sec">
                <div className="hh-l">{t('createPost.fromYourPlan')}</div>
                <div className="hh-chips">{planTags.map((tg) => <button type="button" key={tg} className={`sg ${value.toLowerCase().includes(tg.toLowerCase()) ? 'added' : ''}`} onMouseDown={(ev) => ev.preventDefault()} onClick={() => addHashtag(tg)}>{tg}</button>)}</div>
              </div>}
              {matches.length > 0 && (
                <div className="hh-sec">
                  <div className="hh-l">{t('createPost.matches')}</div>
                  <div className="hh-chips">{matches.map((tg, i) => <button type="button" key={i} className="sg" onMouseDown={(ev) => ev.preventDefault()} onClick={() => { addHashtag(tg); setHquery(''); }}>{tg}</button>)}</div>
                </div>
              )}
              <div className="hh-foot">
                <span className="hh-count"><b>{hashtagCount}</b> {t('createPost.hashtags')}</span>
                {hashtagOver && <span className="hh-warn">{t('createPost.platformAllowsHashtags', { platform: platformLabel(hashtagCapPlatform), count: hashtagLimit })}</span>}
              </div>
            </div>
          )}

          {!disabled && pop === 'ai' && (
            <div className="aimenu" style={{ bottom: 'calc(100% + 10px)', left: 0 }}>
              {CE_MODES.map((m) => <button type="button" key={m} onClick={() => runImprove(m)}><I.sparkles size={13} />{t(`createPost.improve_${m}`)}</button>)}
            </div>
          )}
        </div>

        <div className="tb-r">
          {plats.length > 0 && (
            <span className="plats">
              {plats.map((p) => {
                const lim = getCharacterLimit(p);
                const st = len > lim ? 'over' : len > lim * 0.9 ? 'near' : 'ok';
                return (
                  <span key={p} className={`pg ${st}`} title={`${platformLabel(p)} · ${lim} — ${len > lim ? `${len - lim} ${t('createPost.over')}` : t('createPost.fits')}`}>
                    <span className="ring" />
                    {BrandIcons[p] ? BrandIcons[p]({ size: 12 }) : <span className="pgk">{(p[0] || '?').toUpperCase()}</span>}
                  </span>
                );
              })}
            </span>
          )}
          <span className={`counter ${cState}`}>
            <span className="meter"><i style={{ width: `${pct}%` }} /></span>
            {len}<span className="lim">/ {charLimit}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export function LibraryPicker({ open, onClose, onConfirm, max = 3, initial = [], mode = 'media', capHint = null, confirmLabel = null, includeVideos = false, singleClipMode = false, allowVideoUpload = false, onUpgrade, assetIds = null, title = null, originalsForBrand = null, onUploadRequest = null, disabled = false }) {
  const { t } = useTranslation();
  const isGuide = mode === 'guide';
  const [assets, setAssets] = useState(null);
  // Ordered selection (NOT a Set) — the order IS the carousel/post order.
  const [order, setOrder] = useState(() => initial.map((m) => m.id));
  const [filter, setFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(null); // { fileName }
  // TASK-053: the panel shows byte progress and keeps failures visible with a
  // retry, instead of a boolean that only this dialog can see.
  const { track: trackUpload } = useUploadProgress();
  const [err, setErr] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const dragIdx = useRef(null);

  useEffect(() => {
    if (!open) return;
    setOrder(initial.map((m) => m.id));
    setFilter('all'); setSaved(null); setErr(null);
    if (originalsForBrand) setAssets(null);
    if (originalsForBrand === '') { setAssets([]); setErr(t('common.unavailable')); return; }
    let cancelled = false;
    // includeVideos only when the caller opted in (New Post reel-pick, admin+
    // flag). Every other picker calls list() with no args → byte-identical.
    // TASK-325 fix (blocker #1): mode='media' (My-image picker) requests
    // origin: 'all' — reusing a past post image as post media is legitimate,
    // so post-provenance rows belong in this gallery. mode='guide' (AI
    // reference picker) omits it and stays native-only: a post-provenance
    // row is display-only history, never a valid generation reference (D3).
    api.brandAssets.list(undefined, { includeVideos, ...(isGuide ? {} : { origin: 'all' }), ...(originalsForBrand ? { brandProfileId: originalsForBrand } : {}) })
      .then((res) => {
        if (cancelled) return;
        const listed = (res?.data ?? res)?.assets || [];
        setAssets(listed);
        if (originalsForBrand) {
          const allowed = new Set(listed.filter(originalAllowed).map(asset => asset.id));
          setOrder(previous => previous.filter(id => allowed.has(id)));
        }
      })
      .catch(() => { if (!cancelled) { setAssets([]); if (originalsForBrand) setErr(t('common.unavailable')); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, originalsForBrand]);

  // Display filtering complements Content's authoritative ownership/provider
  // validation. Unknown provenance is never treated as an original upload.
  function originalAllowed(asset) {
    return originalsForBrand === null || (Boolean(originalsForBrand) && asset?.brandProfileId === originalsForBrand
      && typeof asset.url === 'string' && asset.url.trim()
      && (isVideoAsset(asset) ? asset.provider === 'upload' : asset.provider === 'brand_kit' && asset.mimeType?.startsWith('image/')));
  }

  if (!open) return null;

  const selSet = new Set(order);
  // "Upload your own video" — is there a video currently selected in THIS
  // picker session (before confirm)? Drives the exclusivity rule below and the
  // in-modal hint. Only meaningful when includeVideos is on; cheap to compute
  // regardless (assets is null while loading, .some on null-safe array).
  const orderHasVideo = (assets || []).some((x) => order.includes(x.id) && isVideoAsset(x));
  const toggle = (a) => setOrder((prev) => {
    if (disabled || !originalAllowed(a)) return prev;
    if (prev.includes(a.id)) return prev.filter((id) => id !== a.id);
    // V-R9 §7 C5 — Video format enforces single-clip AT SELECTION TIME: picking
    // a video clip swaps any prior selection (max effectively 1); a video post
    // uses exactly one clip. No more silent post-confirm trims.
    if (singleClipMode && isVideoAsset(a)) return [a.id];
    // General My-media video exclusivity (non-reel; "upload your own video"):
    // a video and images can't share a post. Picking a video clears any picked
    // images; picking an image while a video is picked drops the video first.
    // Gated on includeVideos (the picker actually surfaces videos) and NOT
    // singleClipMode (that's the reel-only path above, already handled).
    if (includeVideos && !singleClipMode) {
      if (isVideoAsset(a)) return [a.id];
      const byId = new Map((assets || []).map((x) => [x.id, x]));
      const imagesOnly = prev.filter((id) => !isVideoAsset(byId.get(id)));
      if (imagesOnly.length >= max) return imagesOnly;
      return [...imagesOnly, a.id];
    }
    if (prev.length >= max) return prev;
    return [...prev, a.id];
  });
  const removeId = (id) => setOrder((prev) => prev.filter((x) => x !== id));
  // Live reorder while dragging a selected-strip chip over its neighbours.
  const moveItem = (from, to) => setOrder((prev) => {
    if (from == null || to == null || from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
    const next = [...prev];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    return next;
  });

  // File-handling core, reusable for BOTH the hidden <input> (onUpload) and a
  // drag-drop onto the upload zone (onDrop). Validates type+size before upload
  // since a dropped file bypasses the input's accept= filter.
  const handleFile = async (file) => {
    if (!file) return;
    const isVideoFile = VIDEO_ACCEPT_SET.has(file.type) || /\.(mp4|mov)$/i.test(file.name || '');
    if (isVideoFile && allowVideoUpload) {
      if (file.size > MAX_VIDEO_BYTES) { setErr(t('media.videoErrSize')); return; }
      setUploading(true); setErr(null);
      try {
        let meta;
        try { meta = await readVideoMetaAndPoster(file); }
        catch (_) { setErr(t('media.videoErrRead')); return; }
        if (Number.isFinite(meta.duration) && meta.duration > MAX_VIDEO_SECONDS) {
          setErr(t('media.videoErrLength'));
          return;
        }
        let created;
        try {
          created = await trackUpload(file.name, (onProgress) => api.videos.upload(file, {
            onProgress,
            fileName: file.name,
            width: meta.width || undefined,
            height: meta.height || undefined,
            durationSeconds: Number.isFinite(meta.duration) ? Math.round(meta.duration) : undefined,
          }), { size: file.size });
        } catch (uploadError) {
          if (uploadError?.status === 402) { onUpgrade?.(uploadError); return; }
          setErr(uploadError?.message || t('media.videoUploadError'));
          return;
        }
        const asset = (created?.data ?? created)?.asset ?? (created?.data ?? created);
        if (asset?.id && meta.posterDataUrl) {
          try { await api.videos.setPoster(asset.id, meta.posterDataUrl); }
          catch (posterError) { console.warn('[CreatePost] video poster upload failed', posterError); }
        }
        if (asset?.id) {
          const uploaded = {
            ...asset,
            durationSeconds: asset.durationSeconds ?? (Math.round(meta.duration || 0) || null),
            posterUrl: asset.posterUrl ?? meta.posterDataUrl ?? null,
          };
          setAssets((prev) => [uploaded, ...(prev || [])]);
          setOrder([asset.id]);
          setSaved({ fileName: file.name });
        }
      } finally { setUploading(false); }
      return;
    }
    if (!PICK_ACCEPT_SET.has(file.type)) { setErr(t('brandkit.errType')); return; }
    if (file.size > PICK_MAX_BYTES) { setErr(t('brandkit.errSize')); return; }
    setUploading(true); setErr(null);
    try {
      const dataUrl = await fileToDataUrl(file);
      const tag = filter !== 'all' ? filter : 'style';
      const res = await trackUpload(
        file.name,
        (onProgress) => api.brandAssets.upload({ dataUrl, fileName: file.name, tag, onProgress }),
        { size: file.size }
      );
      const asset = (res?.data ?? res)?.asset;
      if (asset?.id) {
        setAssets((prev) => [asset, ...(prev || [])]);
        setOrder((prev) => (prev.includes(asset.id) || prev.length >= max) ? prev : [...prev, asset.id]);
        setSaved({ fileName: file.name });
      }
    } catch (e2) {
      setErr(e2?.message || t('brandkit.uploadError'));
    } finally { setUploading(false); }
  };
  const onUpload = (e) => { const f = e.target.files?.[0]; if (e.target) e.target.value = ''; handleFile(f); };
  // preventDefault on dragOver is REQUIRED or the browser refuses the drop;
  // on drop it stops the browser navigating to the file.
  const onDropFile = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); handleFile(e.dataTransfer?.files?.[0]); };
  const onDragOverFile = (e) => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); };
  const onDragLeaveFile = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const confirm = () => {
    if (disabled || (originalsForBrand && assets === null)) return;
    // Return in the USER'S chosen order (the carousel/post order), not library order.
    const byId = new Map((assets || []).filter(originalAllowed).map((a) => [a.id, a]));
    // Superset of {id,url,tag} — existing consumers read only those; the video
    // fields let the rail chip (§2a) + single-clip guard know it's a reel.
    const chosen = order.map((id) => byId.get(id)).filter(Boolean).map((a) => ({
      id: a.id, url: a.url, tag: a.tag,
      type: originalsForBrand ? (isVideoAsset(a) ? 'video' : 'image') : a.type, mimeType: a.mimeType,
      ...(originalsForBrand ? { provider: a.provider, brandProfileId: a.brandProfileId } : {}),
      durationSeconds: a.durationSeconds ?? a.duration_seconds ?? null,
      posterUrl: a.posterUrl ?? a.poster_url ?? null,
      width: a.width ?? null, height: a.height ?? null,
    }));
    if (!originalsForBrand || chosen.length) onConfirm(chosen);
  };

  const assetIdSet = assetIds ? new Set(assetIds) : null;
  const shown = (assets || []).filter((a) => originalAllowed(a) && (!assetIdSet || assetIdSet.has(a.id)) && (filter === 'all' || a.tag === filter));

  return (
    <div className="cp-pick-bg" onClick={onClose}>
      <div className="picker" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="pk-head">
          <span className="t">
            {title || (singleClipMode
              ? t('media.pickOneClip', { defaultValue: 'Pick one video clip' })
              : (isGuide ? t('createPost.addBrandReferences') : t('createPost.chooseMediaToPost')))}
          </span>
          <button type="button" className="x2" onClick={onClose} aria-label={t('createPost.close')}><I.x size={16} /></button>
        </div>
        {saved && (
          <div className="saved"><I.check size={13} /><span><b>{saved.fileName}</b> {isGuide ? t('createPost.uploadedSavedRef') : t('createPost.uploadedSavedMedia')}</span></div>
        )}
        {err && <div className="saved pk-err"><I.alert size={13} /><span>{err}</span></div>}
        <div className="pk-filters">
          <button type="button" className={`fc ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>{t('brandkit.tagAll')}</button>
          {PICK_TAGS.map((tg) => (
            <button type="button" key={tg} className={`fc ${filter === tg ? 'on' : ''}`} onClick={() => setFilter(tg)}>
              <span className={`d d-${tg}`} />{t(`brandkit.tag_${tg}`)}
            </button>
          ))}
        </div>
        <div className="pk-grid" onDragOver={assetIdSet || onUploadRequest ? undefined : onDragOverFile} onDragLeave={assetIdSet || onUploadRequest ? undefined : onDragLeaveFile} onDrop={assetIdSet || onUploadRequest ? undefined : onDropFile}>
          {!assetIdSet && <>
            <button
              type="button"
              className={`pk-up ${dragOver ? 'is-drop' : ''}`}
              onClick={() => onUploadRequest ? onUploadRequest() : fileRef.current?.click()}
              disabled={disabled || uploading}
            >
              <I.upload size={18} />
              <span className="t">{uploading ? t('createPost.uploading') : (dragOver ? t('createPost.dropToUpload') : allowVideoUpload ? t('createPost.tiktokCompliance.uploadVideoOrImage') : t('createPost.uploadNew'))}</span>
              <span className="s">{t('createPost.savesToLibrary')}</span>
            </button>
            {!onUploadRequest && <input ref={fileRef} type="file" accept={allowVideoUpload ? `${PICK_ACCEPT},${VIDEO_ACCEPT}` : PICK_ACCEPT} hidden onChange={onUpload} />}
          </>}
          {assets === null ? (
            <div className="cp-pick-empty" style={{ gridColumn: assetIdSet ? '1 / -1' : '2 / -1' }}>{t('createPost.loading')}</div>
          ) : shown.length === 0 ? (
            <div className="cp-pick-empty" style={{ gridColumn: assetIdSet ? '1 / -1' : '2 / -1' }}>{t(originalsForBrand !== null && (err || assets?.length) ? 'common.unavailable' : 'createPost.libraryEmpty')}</div>
          ) : shown.map((a) => {
            const vid = isVideoAsset(a);
            const bg = vid ? resolvePosterUrl(a) : a.url;
            return (
            <button
              type="button"
              key={a.id}
              className={`pa ${selSet.has(a.id) ? 'sel' : ''} ${vid ? 'is-video' : ''}`}
              disabled={disabled}
              onClick={() => toggle(a)}
              style={bg ? { backgroundImage: `url("${bg}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
            >
              {!vid && !a.url && <span className="lbl">{a.tag}</span>}
              {vid && (
                <>
                  <span className="dv-play" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></span>
                  <span className="dv-badge dv-badge-kind">{(a.height || 0) > (a.width || 0) && (a.durationSeconds || 0) <= 90 ? 'REEL' : 'VIDEO'}</span>
                </>
              )}
              {selSet.has(a.id) && <span className="num">{order.indexOf(a.id) + 1}</span>}
            </button>
            );
          })}
        </div>
        {!isGuide && order.length > 0 && (
          <div className="pk-strip" role="list" aria-label={t('createPost.postOrder')}>
            {order.map((id, i) => {
              const a = (assets || []).find((x) => x.id === id);
              const vid = isVideoAsset(a);
              const chipBg = vid ? resolvePosterUrl(a) : a?.url;
              return (
                <div
                  key={id}
                  role="listitem"
                  className={`pk-chip ${vid ? 'is-video' : ''}`}
                  draggable
                  onDragStart={() => { dragIdx.current = i; }}
                  onDragOver={(e) => { e.preventDefault(); if (dragIdx.current != null && dragIdx.current !== i) { moveItem(dragIdx.current, i); dragIdx.current = i; } }}
                  onDragEnd={() => { dragIdx.current = null; }}
                  title={t('createPost.dragToReorder')}
                  style={chipBg ? { backgroundImage: `url("${chipBg}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                >
                  {!vid && !a?.url && <span className="pk-chip-tag">{a?.tag}</span>}
                  <span className="pk-chip-n">{i + 1}</span>
                  <button type="button" className="pk-chip-x" aria-label={t('createPost.remove')} onMouseDown={(e) => e.stopPropagation()} onClick={() => removeId(id)}><I.x size={11} /></button>
                </div>
              );
            })}
          </div>
        )}
        <div className="pk-foot">
          <span className="c">
            <b>{order.length}</b>{max ? `/${max}` : ''} {t('createPost.selectedSuffix')}
            {capHint && <span className="pk-caphint">{capHint}</span>}
            {/* Own-uploaded-video exclusivity hint — only once a video is
                actually picked in THIS session (see toggle()'s includeVideos
                branch above); doesn't nag while browsing images only. */}
            {includeVideos && !singleClipMode && orderHasVideo && (
              <span className="pk-caphint">{t('media.videoExclusiveHint', { defaultValue: 'A video post can contain only the video.' })}</span>
            )}
          </span>
          <button type="button" className="done" onClick={confirm} disabled={disabled || order.length === 0 || (Boolean(originalsForBrand) && assets === null)}>{confirmLabel || t('createPost.addToPost')}</button>
        </div>
      </div>
    </div>
  );
}

// Brand references rail (ported from newpost-06-visual-block.html). Presentational:
// all state lives in ComposeForm. mode 'guide' = AI references (accent, "will
// guide"); mode 'media' = My-image media (info, order badges, "will be posted").
// isVideo: owner-reported bug fix (2026-07-06) — in Video format the generic
// "N image(s) will be posted" count is nonsense (0/1 clip, not an image count)
// and the parent already renders a dedicated video empty-state note below this
// rail, so the summary line is suppressed entirely rather than shown wrong.
function BrandRefRail({ mode, logo, logoExcluded, onToggleLogo, items, onRemove, onAdd, onReorder, brandInitial, justPulsed, isVideo, titleKey, hasBrandContext = true, disabled = false, t }) {
  const isGuide = mode === 'guide';
  // Media mode: thumbnails are a reorderable strip (drag = post/carousel order)
  // and clicking one re-opens the picker. Guide refs have no meaningful order.
  const dragIdx = useRef(null);
  const reorder = (from, to) => {
    if (disabled || !onReorder || from == null || to == null || from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onReorder(next);
  };
  const logoOn = isGuide && logo && !logoExcluded;
  // Pick a singular/plural i18n key by count (avoids the "1 references" bug
  // without relying on per-language plural rules).
  const pl = (base, count) => t(`createPost.${base}_${count === 1 ? 'one' : 'other'}`, { count });
  let summCls = 'quiet';
  let summText = t(hasBrandContext ? 'createPost.noRefsQuiet' : 'createPost.noRefsWithoutBrand');
  if (isGuide) {
    if (logoOn && items.length === 0) { summCls = 'acc'; summText = t('createPost.logoWillGuide'); }
    else if (logoOn && items.length > 0) { summCls = 'acc'; summText = pl('logoPlusRefsGuide', items.length); }
    else if (!logoOn && items.length > 0) { summCls = 'acc'; summText = pl('refsGuideNoLogo', items.length); }
  } else if (!isVideo) {
    summCls = 'info';
    // A picked video is always the sole media (exclusivity in the picker), so
    // "1 item will be posted" reads vague — say what actually happens.
    summText = items.length === 1 && isVideoAsset(items[0])
      ? t('media.videoWillBePost', { defaultValue: 'The video will be posted' })
      : pl('willBePostedSumm', items.length);
  }
  // Video format: no generic count line — the parent's dedicated video
  // empty-state/clip-picked note is the only status shown for this rail.
  const showSumm = isGuide || !isVideo;
  return (
    <div className="vb-body">
      <div className="rail-l">
        <span className="lbl">{isGuide ? t(titleKey || 'createPost.brandReferences') : t('createPost.mediaToPost')}</span>
        <span className="opt">{isGuide ? t('createPost.optionalGuides') : t('createPost.orderIsPostOrder')}</span>
      </div>
      <div className="rail">
        {isGuide && logo && (
          <span className={`logo-wrap ${logoOn ? 'on' : ''}`}>
            <button
              type="button"
              className={`logo-tog ${logoOn ? 'on' : ''} ${justPulsed ? 'just' : ''}`}
              onClick={onToggleLogo}
              disabled={disabled}
              title={logoOn ? t('createPost.logoOnTitle') : t('createPost.logoOffTitle')}
            >
              {logo.url ? <img src={logo.url} alt="" className="logo-img" /> : <b>{brandInitial}</b>}
              <span className="lt-check"><I.check size={11} /></span>
            </button>
            <span className="logo-cap">{logoOn ? t('createPost.logoOn') : t('createPost.logoOff')}</span>
          </span>
        )}
        {items.map((it, i) => (
          <span
            className={`thumb-wrap ${!isGuide ? 'thumb-wrap--media' : ''}`}
            key={it.id}
            draggable={!disabled && !isGuide && !!onReorder}
            onDragStart={!isGuide && onReorder ? () => { dragIdx.current = i; } : undefined}
            onDragOver={!isGuide && onReorder ? (e) => { e.preventDefault(); if (dragIdx.current != null && dragIdx.current !== i) { reorder(dragIdx.current, i); dragIdx.current = i; } } : undefined}
            onDragEnd={!isGuide && onReorder ? () => { dragIdx.current = null; } : undefined}
          >
            <span
              className={`thumb ${isVideoAsset(it) ? 'is-video' : ''}`}
              style={(() => { const bg = isVideoAsset(it) ? resolvePosterUrl(it) : it.url; return bg ? { backgroundImage: `url("${bg}")` } : undefined; })()}
              onClick={!disabled && !isGuide && onAdd ? onAdd : undefined}
              title={!isGuide ? t('createPost.dragToReorder') : undefined}
            >
              {!isGuide && <span className="ord">{i + 1}</span>}
              {isVideoAsset(it) && (
                <>
                  <span className="dv-chip-play" aria-hidden="true" />
                  {formatDuration(it.durationSeconds) && <span className="dv-chip-dur">{formatDuration(it.durationSeconds)}</span>}
                </>
              )}
              <button type="button" className="x" disabled={disabled} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (!disabled) onRemove(it.id); }} aria-label={t('createPost.removeAttachment')}><I.x size={9} /></button>
            </span>
            <span className="thumb-cap">{it.tag ? it.tag.charAt(0).toUpperCase() + it.tag.slice(1) : ''}</span>
          </span>
        ))}
        <span className="add-wrap">
          <button type="button" disabled={disabled} className={`add ${isGuide ? '' : 'add--media'}`} onClick={onAdd} title={t(isGuide ? 'createPost.addRefTitle' : 'createPost.chooseMediaToPost')} aria-label={t(isGuide ? 'createPost.addRefTitle' : 'createPost.chooseMediaToPost')}>
            <I.plus size={16} />
          </button>
          <span className="add-cap">{t('createPost.add')}</span>
        </span>
      </div>
      {showSumm && (
        <div className={`summ ${summCls}`}>
          {summCls === 'info' ? <I.check size={13} /> : <I.sparkles size={13} />}
          <span>{summText}</span>
        </div>
      )}
    </div>
  );
}

// mediaKind: 'video' (own-upload reel lane, unchanged) | 'photo' (Slides —
// owner decision 2026-09-08: TikTok photo posts get the SAME compliance UX
// as video — privacy/comments/commercial-disclosure/policy links + the
// direct-vs-draft choice, gated by the SAME tiktokDirectPostEnabled() kill
// switch — just without the video-only bits (preview, duet/stitch, duration
// cap) that don't apply to a photo deck.
function TikTokComplianceController({ accountId, mediaKind = 'video', video, schedule, submitting, generationLocked, videoFormatBlocked, noBrandBlocked, onSubmit }) {
  const { t } = useTranslation();
  const isPhoto = mediaKind === 'photo';
  const [creatorState, setCreatorState] = useState({ status: 'loading', info: null });
  const [privacy, setPrivacy] = useState('');
  const [allowComments, setAllowComments] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  const [commercialContent, setCommercialContent] = useState(false);
  const [yourBrand, setYourBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);
  // Audit passed 2026-08-07 (app Dathent.AI), so direct posting is the GA
  // default: the selector shows both modes and preselects "Post directly".
  // The flag is now an emergency kill switch — off hides the selector and
  // forces inbox drafts, exactly the pre-audit behavior.
  const directEnabled = tiktokDirectPostEnabled();
  const [postMode, setPostMode] = useState(directEnabled ? 'direct' : 'draft');

  useEffect(() => {
    let cancelled = false;
    setPrivacy('');
    setPostMode(directEnabled ? 'direct' : 'draft');
    setAllowComments(false);
    setAllowDuet(false);
    setAllowStitch(false);
    setCommercialContent(false);
    setYourBrand(false);
    setBrandedContent(false);
    setCreatorState({ status: 'loading', info: null });
    api.socialAccounts.getTikTokCreatorInfo(accountId)
      .then((response) => {
        if (!cancelled) setCreatorState({ status: 'ready', info: response?.data ?? response });
      })
      .catch((error) => {
        if (!cancelled) setCreatorState({ status: isTikTokReconnectError(error) ? 'reconnect' : 'error', info: null });
      });
    return () => { cancelled = true; };
  }, [accountId]);

  const info = creatorState.info;
  const privateSelected = privacy === 'SELF_ONLY';
  const commercialChoiceMissing = commercialContent && !yourBrand && !brandedContent;
  // Duration cap only applies to the video lane — a photo deck has no clip length.
  const durationTooLong = !isPhoto
    && Number.isFinite(Number(video?.durationSeconds))
    && Number.isFinite(Number(info?.maxVideoPostDurationSec))
    && Number(video.durationSeconds) > Number(info.maxVideoPostDurationSec);
  // TikTok requires the full compliance UX (privacy from creator_info, toggles,
  // commercial disclosure, music consent) only for DIRECT posting. Inbox drafts
  // are finished inside the TikTok app, so the card stays lean: preview +
  // account + send. Drafts also don't depend on creator_info — only a token
  // known to be dead (reconnect) blocks them.
  const needsCompliance = directEnabled && postMode === 'direct';
  const blocked = needsCompliance
    ? (creatorState.status !== 'ready' || !privacy || commercialChoiceMissing || durationTooLong)
    : (creatorState.status === 'reconnect' || durationTooLong);
  const submit = () => onSubmit({
    privacy: TIKTOK_PRIVACY_TO_API[privacy],
    allowComments,
    allowDuet,
    allowStitch,
    commercialContent,
    yourBrand,
    brandedContent,
    postMode: directEnabled ? postMode : 'draft',
  });

  return (
    <section className="card cp-card cp-tt-compliance" aria-labelledby="cp-tt-title">
      <header className="cp-card-h">
        <div className="eyebrow"><span>TikTok</span></div>
        <h3 id="cp-tt-title">{t('createPost.tiktokCompliance.title')}</h3>
      </header>

      {!isPhoto && video?.url && (
        <div className="cp-tt-preview">
          <span className="cp-label">{t('createPost.tiktokCompliance.videoPreview')}</span>
          <video controls preload="metadata" src={video.url} poster={resolvePosterUrl(video) || undefined} />
        </div>
      )}

      {needsCompliance && creatorState.status === 'loading' && (
        <div className="bk-notice is-info" role="status">{t('createPost.tiktokCompliance.loadingCreator')}</div>
      )}
      {creatorState.status === 'ready' && info && (
        <div className="bk-notice is-success" role="status">
          <I.check size={14} />
          <span>{t('createPost.tiktokCompliance.postingAs', { nickname: info.nickname, username: info.username })}</span>
        </div>
      )}
      {creatorState.status === 'reconnect' && (
        <div className="bk-notice is-error" role="alert">
          <I.alert size={14} />
          <Link to="/app/settings?tab=accounts">{t('createPost.tiktokCompliance.reconnect')}</Link>
        </div>
      )}
      {needsCompliance && creatorState.status === 'error' && (
        <div className="bk-notice is-error" role="alert">
          <I.alert size={14} />
          <span>{t('createPost.tiktokCompliance.creatorUnavailable')}</span>
        </div>
      )}

      {directEnabled && (
      <fieldset className="cp-tt-fieldset" disabled={creatorState.status !== 'ready'}>
        <legend className="cp-label">{t('createPost.tiktokCompliance.postModeLabel')}</legend>
        <label className="cp-tt-switch">
          <input type="radio" name="cp-tt-postmode" checked={postMode === 'draft'} onChange={() => setPostMode('draft')} />
          <span>
            <b>{t('createPost.tiktokCompliance.postModeDraft')}</b>
            <small>{t('createPost.tiktokCompliance.postModeDraftHint')}</small>
          </span>
        </label>
        <label className="cp-tt-switch">
          <input type="radio" name="cp-tt-postmode" checked={postMode === 'direct'} onChange={() => setPostMode('direct')} />
          <span>
            <b>{t('createPost.tiktokCompliance.postModeDirect')}</b>
            <small>{t('createPost.tiktokCompliance.postModeDirectHint')}</small>
          </span>
        </label>
      </fieldset>
      )}

      {needsCompliance && (
      <div className="cp-tt-grid">
        <div className="cp-field">
          <label className="cp-label" htmlFor="cp-tt-privacy">{t('createPost.tiktokCompliance.privacy')}</label>
          <select
            id="cp-tt-privacy"
            className="cp-tt-select"
            value={privacy}
            disabled={creatorState.status !== 'ready'}
            onChange={(event) => {
              const next = event.target.value;
              setPrivacy(next);
              // TikTok: branded content is public/friends only. If the user
              // ticked "Branded content" first and then picks "Only me", the
              // checkbox is merely disabled, so brandedContent:true would still
              // be submitted and the worker would silently drop the disclosure.
              // Reset it here so the UI and the submitted payload agree.
              if (next === 'SELF_ONLY') setBrandedContent(false);
            }}
          >
            <option value="" disabled>{t('createPost.tiktokCompliance.choosePrivacy')}</option>
            {(info?.privacyLevelOptions || []).map((option) => (
              <option key={option} value={option} disabled={option === 'SELF_ONLY' && brandedContent}>
                {t(`createPost.tiktokCompliance.privacy_${option}`, { defaultValue: option })}
              </option>
            ))}
          </select>
          {brandedContent && <span className="cp-tt-hint">{t('createPost.tiktokCompliance.brandedPrivateHint')}</span>}
        </div>

        <fieldset className="cp-tt-fieldset" disabled={creatorState.status !== 'ready'}>
          <legend className="cp-label">{t('createPost.tiktokCompliance.interactions')}</legend>
          <label className={info?.commentDisabled ? 'is-disabled' : ''}>
            <input type="checkbox" checked={allowComments} disabled={!!info?.commentDisabled} onChange={(event) => setAllowComments(event.target.checked)} />
            {t('createPost.tiktokCompliance.allowComments')}
          </label>
          {/* Duet/Stitch are video-only TikTok interactions — a photo deck has
              no clip to duet or stitch, so Slides hides both (state stays
              false and still travels in the payload — same shape as video). */}
          {!isPhoto && (
          <label className={info?.duetDisabled ? 'is-disabled' : ''}>
            <input type="checkbox" checked={allowDuet} disabled={!!info?.duetDisabled} onChange={(event) => setAllowDuet(event.target.checked)} />
            {t('createPost.tiktokCompliance.allowDuet')}
          </label>
          )}
          {!isPhoto && (
          <label className={info?.stitchDisabled ? 'is-disabled' : ''}>
            <input type="checkbox" checked={allowStitch} disabled={!!info?.stitchDisabled} onChange={(event) => setAllowStitch(event.target.checked)} />
            {t('createPost.tiktokCompliance.allowStitch')}
          </label>
          )}
        </fieldset>
      </div>
      )}

      {needsCompliance && (
      <div className="cp-tt-commercial">
        <label className="cp-tt-switch">
          <input
            type="checkbox"
            checked={commercialContent}
            disabled={creatorState.status !== 'ready'}
            onChange={(event) => {
              const next = event.target.checked;
              setCommercialContent(next);
              if (!next) { setYourBrand(false); setBrandedContent(false); }
            }}
          />
          <span>
            <b>{t('createPost.tiktokCompliance.commercialContent')}</b>
            <small>{t('createPost.tiktokCompliance.commercialContentHint')}</small>
          </span>
        </label>
        {commercialContent && (
          <div className="cp-tt-commercial-options">
            <label>
              <input type="checkbox" checked={yourBrand} onChange={(event) => setYourBrand(event.target.checked)} />
              {t('createPost.tiktokCompliance.yourBrand')}
            </label>
            <label className={privateSelected ? 'is-disabled' : ''} title={privateSelected ? t('createPost.tiktokCompliance.brandedPrivateHint') : undefined}>
              <input type="checkbox" checked={brandedContent} disabled={privateSelected} onChange={(event) => setBrandedContent(event.target.checked)} />
              {t('createPost.tiktokCompliance.brandedContent')}
            </label>
          </div>
        )}
        {commercialChoiceMissing && (
          <div className="bk-notice is-error" role="alert">{t('createPost.tiktokCompliance.commercialRequired')}</div>
        )}
        {(yourBrand || brandedContent) && (
          <div className="bk-notice is-info" role="status">
            {brandedContent
              ? t('createPost.tiktokCompliance.paidPartnershipLabel')
              : t('createPost.tiktokCompliance.promotionalLabel')}
          </div>
        )}
      </div>
      )}

      {durationTooLong && (
        <div className="bk-notice is-error" role="alert">
          {t('createPost.tiktokCompliance.videoTooLong', { seconds: info.maxVideoPostDurationSec })}
        </div>
      )}

      {needsCompliance ? (
        <p className="cp-tt-declaration">
          {brandedContent ? (
            <>
              {t('createPost.tiktokCompliance.agreePrefix')}{' '}
              <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer">{t('createPost.tiktokCompliance.brandedContentPolicy')}</a>
              {' '}{t('createPost.tiktokCompliance.and')}{' '}
              <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">{t('createPost.tiktokCompliance.musicUsageConfirmation')}</a>
            </>
          ) : (
            <>
              {t('createPost.tiktokCompliance.agreePrefix')}{' '}
              <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">{t('createPost.tiktokCompliance.musicUsageConfirmation')}</a>
            </>
          )}
        </p>
      ) : (
        <p className="cp-tt-declaration">{t('createPost.tiktokCompliance.draftInboxNote')}</p>
      )}
      <span className="cp-tt-cta-wrap" title={commercialChoiceMissing ? t('createPost.tiktokCompliance.commercialRequired') : undefined}>
        <button
          className="btn btn-primary cp-generate"
          onClick={submit}
          disabled={submitting || generationLocked || videoFormatBlocked || noBrandBlocked || blocked}
          aria-describedby={noBrandBlocked ? 'no-brand-guard-hint-create' : undefined}
          title={noBrandBlocked ? t('noBrandGuard.disabledHint') : undefined}
          type="button"
        >
          <I.sparkles size={14} />
          {submitting
            ? t('createPost.starting')
            : generationLocked
              ? t('createPost.generationInProgressShort')
              : schedule === 'draft'
                ? t('createPost.saveAsDrafts')
                : postMode === 'direct'
                  ? t(isPhoto ? 'createPost.tiktokCompliance.publishCtaDirectPhoto' : 'createPost.tiktokCompliance.publishCtaDirect')
                  : t(isPhoto ? 'createPost.tiktokCompliance.publishCtaPhoto' : 'createPost.tiktokCompliance.publishCta')}
          {!submitting && !generationLocked && <span className="kbd" style={{ marginLeft: 'auto' }}>⏎</span>}
        </button>
      </span>
    </section>
  );
}

function ComposeForm({ onGenerated, manual = null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dash = useDashboardData() || {};
  const { entitlements, ensureDashboardData, invalidateResource } = dash;
  const socialAccounts = manual ? manual.accounts : dash.socialAccounts;
  const composeSourcesEnabled = Boolean(manual) || brandKitEnabled();
  const originalUploadRef = useRef(null);
  const uploadSelection = useRef({ replace: false });
  const openOriginalUpload = (replace = false) => {
    if (manual?.disabled) return;
    uploadSelection.current = { replace };
    originalUploadRef.current?.click();
  };
  const noBrand = manual ? !manual.form.brandProfileId : dash.setupStatus?.brand === 'missing';
  const needsBrandContext = !manual && needsCreateBrandContext(dash.setupStatus);
  const imageAllowance = deriveImageAllowance(entitlements);
  const imageAllowanceZero = Boolean(manual) || imageAllowance.zero;
  const imageAvailabilityError = !imageAllowance.known && Boolean(dash.fetchedAt?.entitlements) && !dash.refreshing;
  const aiImageDisabled = !imageAllowance.known || imageAllowanceZero;
  // BLOCKER fix (fix list item 1): the "Not in your plan" badge + locked
  // tooltip must reflect a CONFIRMED zero allowance, not just aiImageDisabled
  // (which is also true mid-fetch and permanently true on a fetch error) —
  // otherwise every paying account flashes "AI media NOT IN YOUR PLAN"
  // during the entitlements round-trip. `disabled` stays aiImageDisabled so
  // the segment is still inert while unknown; only the badge/copy differ.
  const imageLockBadge = imageAllowanceZero;
  const imageLockTitle = imageAllowanceZero
    ? t('imageLocked.lockedLabel')
    : (!imageAllowance.known ? (imageAvailabilityError ? t('imageLocked.error') : t('imageLocked.loading')) : undefined);
  const textAllowance = deriveTextAllowance(entitlements);
  const aiTextDisabled = Boolean(manual) || (textAllowance.known && textAllowance.zero);
  // TASK-320: Generation Level hide rule. Deliberately NOT derived from
  // aiImageDisabled/aiTextDisabled — those are asymmetric (image treats
  // "unknown" as disabled, text does not), so reusing them would flash the
  // panel in in on load or never hide it. Both allowances must be CONFIRMED
  // zero: on that account the picked level is inert (still sent as
  // level/metadata.level and stored on the batch row, but content-worker's
  // zero-allowance compose path — providedCaption/providedMediaAssetIds/
  // allowTextOnly — never reaches a paid call) so the picker is dead,
  // sellable-looking UI. Pure entitlements, no flag: an upgrade brings it
  // back on the next refresh, and a future text-only/image-only credit plan
  // keeps it visible because only the both-zero case hides it.
  const aiFullyUnavailable = Boolean(manual) || imageAllowance.known && imageAllowance.zero && textAllowance.known && textAllowance.zero;
  const { startGenerationBatch, activeBatches } = useContentFeedContext();
  const { setActivity } = useActivity();
  const { startOperation, operationsArr: genOperationsArr } = useGenerationStatus();
  const { openUpgrade } = useUpgrade();
  // Reel-pick in New Post: admin-only + flag-dark (server double-gates) UNLESS
  // GA is on. When showReel is false the media picker calls list() with no
  // args → byte-identical, no reel ever surfaces. Gates ONLY the "My image"
  // picker's includeVideos. GA rollout = server VIDEO_REELS_GA=1 + this
  // default flipped (videoReelsGA becomes readFlagOn); until then videoReelsGA()
  // is the staged per-browser tester switch that opens this for EVERY user
  // (server still enforces quota/402 — see visual-worker videoGaEnabled).
  // Video OFF 2026-07-10: showReel is now JUST the master flag (default OFF) — the
  // legacy `isAdmin && videoReelsEnabled()` (localStorage 'dathent.video.reels')
  // escape was still showing video to admins who set it during the old dark pilot.
  // To test/re-enable: localStorage.setItem('dathent.video.ga','1') or flip
  // videoReelsGA() back to readFlagOn.
  const showReel = videoReelsGA();
  // v2 opens only the own-video publishing lane. The legacy GA switch remains
  // the sole gate for AI video generation.
  const videoPublishV2 = Boolean(manual) || videoPublishV2Enabled();
  const showVideoFormat = showReel || videoPublishV2;
  // V-R8 output format for the "My image" post: 'image' (today's behavior,
  // default) | 'reel' (pick ONE rendered video) | 'slides' (stills → TikTok
  // photo draft). Pure UX guidance + picker scoping — payloads are unchanged
  // (routing happens server-side by asset shape), EXCEPT Phase 4b AI slides
  // which additively sends slideCount (see the payload build).
  const [postFormat, setPostFormat] = useState('image');
  // Phase 4b — how many images the AI generates for a slides post (2–5,
  // default 3). Only consumed in imageSource==='ai' + postFormat==='slides'.
  const [slideCount, setSlideCount] = useState(3);
  // TASK-236 (#354, EPIC-023 pricing v2) — Economy/Standard/Premium for this
  // batch's text + image generation. Persisted per-user (localStorage), seeded
  // from the last choice or 'economy' (see lib/generationLevel.js).
  const [generationLevel, setGenerationLevel] = useGenerationLevel();
  // Owner decision 2026-09-08: the picker shipped with credit spending still
  // fully dark, and its own 'economy' UI default was silently routing every
  // post through the nano model instead of the pre-picker gpt-4.1 default —
  // see lib/flags.js's generationLevelPickerEnabled for the full story. While
  // this reads false (default), the picker stays unmounted below AND `level`/
  // `metadata.level` are omitted from the createBatch payload entirely.
  const generationLevelPickerVisible = generationLevelPickerEnabled();

  const [prompt, setPrompt] = useState('');
  // Selected publish target accounts (social_account.id). Multiple accounts of the
  // same platform are independent — e.g. user can pick both Telegram channels.
  const [localAccounts, setSelectedAccounts] = useState(() => new Set());
  const selectedAccounts = useMemo(() => manual ? new Set(manual.accountId ? [manual.accountId] : []) : localAccounts, [manual?.accountId, Boolean(manual), localAccounts]);
  // Platforms the user wants to generate "as for" even though NO account is
  // connected for them. These produce drafts (no publish target) — we tell the
  // user they'll need to connect that network to actually publish. Kept apart
  // from selectedAccounts because they carry no account id.
  const [localDraftPlatforms, setSelectedDraftPlatforms] = useState(() => new Set());
  const selectedDraftPlatforms = useMemo(() => manual ? new Set(manual.accountId ? [] : [manual.form.platform]) : localDraftPlatforms, [manual?.accountId, manual?.form.platform, Boolean(manual), localDraftPlatforms]);
  const [autoPickedDefault, setAutoPickedDefault] = useState(false);
  const [count, setCount] = useState(1);
  const [schedule, setSchedule] = useState(manual ? 'draft' : 'now'); // 'now' | 'spread' | 'draft'
  // Multi-select visual styles for this batch. Seeded from the brand's saved
  // set (Edit Strategy). One selected => consistent look (forced on every
  // post); several => the backend mixes across them per post.
  const [visualSet, setVisualSet] = useState(() => new Set());
  const [visualInitialized, setVisualInitialized] = useState(false);
  // Per-generation brand-colors override (New Post). Seeded from the brand's
  // apply_brand_colors default; the user can flip it for this post only.
  const [brandLook, setBrandLook] = useState({ colors: true, typography: null });
  const useBrandColors = brandLook.colors;
  const useTypography = brandLook.typography;
  const setUseBrandColors = (colors) => setBrandLook((current) => ({ ...current, colors }));
  const setUseTypography = (typography) => setBrandLook((current) => ({ ...current, typography }));
  // Brand styling is optional; explicit reference photos remain available in
  // either mode. Resolve the default once context is known, preserving choices.
  const [brandChoice, setBrandChoice] = useState(() => ({ userId: user?.id, brandId: null, known: false, value: true, explicit: false }));
  const setUseBrand = (value) => setBrandChoice((current) => ({ ...current, value, explicit: true }));
  const [submitting, setSubmitting] = useState(false);
  const [brand, setBrand] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [recentTopics, setRecentTopics] = useState([]);
  const [attachments, setAttachments] = useState([]); // [{ id, name, url, mimeType, uploading, error }]
  const fileInputRef = useRef(null);
  // New Post composition source toggles (both default to AI — when both are
  // 'ai' the request payload is byte-identical to before these existed).
  // text: 'ai' (AI writes) | 'write' (user-provided caption)
  // image: 'ai' (AI generates) | 'my' (use chosen library media) | 'none' (text-only)
  const [localTextSource, setLocalTextSource] = useState('ai');
  const textSource = manual ? 'write' : localTextSource;
  const setTextSource = value => { if (!manual) setLocalTextSource(value); };
  const [imageSource, setLocalImageSource] = useState(manual ? 'none' : 'ai');
  const setImageSource = value => {
    if (!manual) { setLocalImageSource(value); return; }
    const next = typeof value === 'function' ? value(imageSource) : value;
    if (manual && (next === 'ai' || manual.disabled)) return;
    if (manual && next === 'none' && manual.form.media.length) manual.change({ media: [] });
    setLocalImageSource(next);
  };
  const isOwnVideoV2Lane = videoPublishV2 && postFormat === 'reel' && imageSource === 'my';
  const [localCaption, setLocalCaption] = useState('');
  const providedCaption = manual ? manual.form.caption : localCaption;
  const setProvidedCaption = value => manual ? manual.change({ caption: value }) : setLocalCaption(value);
  const [localMedia, setLocalMedia] = useState([]);
  const pickedMedia = manual ? manual.form.media : localMedia;
  const setPickedMedia = value => {
    if (!manual) { setLocalMedia(value); return; }
    const next = typeof value === 'function' ? value(pickedMedia) : value;
    manual.change({ media: next });
  };
  const [pickerOpen, setLocalPickerOpen] = useState(false);
  const setPickerOpen = value => {
    if (manual?.disabled && value) return;
    setLocalPickerOpen(value);
  };
  useEffect(() => {
    if (!manual || !pickedMedia.length) return;
    setLocalImageSource('my');
    setPostFormat(pickedMedia.some(isVideoAsset) ? 'reel' : 'image');
  }, [manual?.form.media]);
  // Brand references rail (AI image mode) — kept SEPARATE from pickedMedia.
  const [pickedRefs, setPickedRefs] = useState([]); // [{ id, url, tag }] references that GUIDE the image
  const [primaryLogo, setPrimaryLogo] = useState(null); // { id, url } | null
  const visualBrandContextAvailable = hasCreateVisualBrandContext(dash.setupStatus, brand, primaryLogo);
  const meaningfulBrandContext = hasCreateMeaningfulBrandContext(brand || dash.setupStatus?.profile);
  // Fail OFF while setup/profile knowledge is absent as well as when it is
  // confirmed empty. brandChoice still remembers an explicit same-brand OFF;
  // the effective value simply cannot turn on styling that does not exist.
  const useBrand = effectiveCreateBrandChoice(brandChoice, visualBrandContextAvailable);
  useEffect(() => {
    setBrandChoice((current) => resolveCreateBrandChoice(current, {
      userId: user?.id, brandId: dash.setupStatus?.profile?.id || brand?.id || null,
      known: ['missing', 'present'].includes(dash.setupStatus?.brand),
      hasContext: visualBrandContextAvailable,
    }));
  }, [user?.id, dash.setupStatus, brand, primaryLogo, visualBrandContextAvailable]);
  const [logoExcluded, setLogoExcluded] = useState(false); // logo pin ON by default; true = off for THIS post
  const [logoPulse, setLogoPulse] = useState(false); // one-shot pulse animation when re-enabling
  const [pickerMode, setPickerMode] = useState('media'); // 'guide' (AI refs) | 'media' (My image)

  useEffect(() => {
    if (imageAllowanceZero && imageSource === 'ai') setImageSource('none');
  }, [imageAllowanceZero, imageSource]);

  useEffect(() => {
    if (aiTextDisabled && textSource === 'ai') setTextSource('write');
  }, [aiTextDisabled, textSource]);

  useEffect(() => {
    if (aiTextDisabled) trackOnce('ai_text_locked_shown', { surface: 'create_post' });
  }, [aiTextDisabled]);

  // Video sub-mode within the composer (driven by the unified source picker):
  // 'describe' (AI writes + narrates) | 'photos' (scenes rail → frameAssetIds) —
  // both under source='ai'; 'clip' (pick a rendered clip) under source='my'.
  // Default 'describe'.
  const [videoSrc, setVideoSrc] = useState('describe');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoBrandVoice, setVideoBrandVoice] = useState(true);
  // Mode-2 optional reference photos (describe mode only) — SEPARATE from
  // videoScenes (photos mode's required source) so neither fights the other.
  const [videoDescribeRefs, setVideoDescribeRefs] = useState([]); // [{ id, url, tag }]
  // Photos-mode scenes rail — the required video source in that mode.
  const [videoScenes, setVideoScenes] = useState([]); // [{ id, url, tag }]
  const videoFrameDragIdx = useRef(null);
  // Options disclosure (hook / narration+script / aspect) — collapsed by default.
  const [videoOptsOpen, setVideoOptsOpen] = useState(false);
  const [videoHook, setVideoHook] = useState('');
  const [videoNarration, setVideoNarration] = useState(true);
  const [videoScript, setVideoScript] = useState('');
  // Per-video narration language override. '' = follow the brand/strategy
  // default (videoLanguage below); an explicit code overrides for THIS render.
  // Passed through the existing submit `language` field (no backend change).
  const [videoLang, setVideoLang] = useState('');
  // Tier-2 cinematic (admin pilot) — describe mode only; server double-gates
  // (VIDEO_CINEMATIC_ENABLED + admin). Omitted otherwise = standard slideshow,
  // byte-identical to today. Mirrors BrandKitScreen's reelQuality.
  const [videoQuality, setVideoQuality] = useState('standard');
  // Set once the user picks a quality by hand, so the cinematic-default effect
  // never overrides an explicit choice.
  const videoQualityTouchedRef = useRef(false);
  // Render lifecycle — mirrors BrandKitScreen's video generation state machine,
  // but inline in the post composer (Variant A). 'idle' | 'submitting' |
  // 'rendering' | 'ready' | 'failed'.
  const [videoPhase, setVideoPhase] = useState('idle');
  const [videoOpId, setVideoOpId] = useState(null);
  const [videoElapsed, setVideoElapsed] = useState(0);
  const [videoError, setVideoError] = useState(null);
  const videoElapsedTimerRef = useRef(null);
  // Snapshot of the last submit, for the failed-state "Try again" restore.
  const lastVideoSubmitRef = useRef(null);
  // De-dupe terminal op handling — mirrors BrandKitScreen's seenDoneRef so a
  // reload/re-render doesn't re-fire the ready/failed notice for the same op.
  const seenVideoDoneRef = useRef(new Set());

  // Resolve the brand's primary logo once (flag-on) so the references rail can
  // pin it ON by default for AI image generation. No-op when the flag is off,
  // so the classic form never fetches it.
  useEffect(() => {
    if (manual || !composeSourcesEnabled) return;
    let cancelled = false;
    api.brandAssets.list()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        const assets = Array.isArray(data?.assets) ? data.assets : [];
        const logoId = data?.primaryLogoId ?? null;
        const logo = assets.find((a) => a.id === logoId) || assets.find((a) => a.isPrimaryLogo) || null;
        if (logo) setPrimaryLogo({ id: logo.id, url: logo.url });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Compose modes produce exactly ONE post — the backend clamps count=1 for any
  // user-provided caption/media (generation-batch-service isCompose). Keep the
  // local count in lockstep so the stepper, cost card and CTA never claim "14
  // posts" for a single compose. Flag-off keeps both sources 'ai' → no-op.
  useEffect(() => {
    // A Video reel is inherently ONE post (a single clip) regardless of source —
    // the old code got this for free by force-setting imageSource='my'; now it's
    // explicit so an AI-generated reel can't ship count=N.
    const single = (composeSourcesEnabled && (textSource === 'write' || imageSource === 'my')) || (showVideoFormat && postFormat === 'reel');
    if (single && count !== 1) setCount(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSource, imageSource, postFormat]);

  // Refresh dashboard data + load brand profile, strategy, recent batches
  useEffect(() => {
    if (manual) return;
    ensureDashboardData?.();
    api.profile.getBrandProfile()
      .then((res) => setBrand(res?.data?.brandProfile || res?.brandProfile || res?.data || res))
      .catch(() => {});
    // Strategy (pillars, audience etc) — may 404 when no content plan yet.
    // Pillars.exampleTopics drives the "Suggested topics" chips below the prompt.
    api.strategy.get()
      .then((res) => setStrategy(res?.data?.strategy || res?.strategy || res?.data || res))
      .catch(() => setStrategy(null));
    // Recent batch topics from user's history (fallback when no pillars yet)
    api.content.listBatches({ limit: 12 })
      .then((res) => {
        const batches = res?.batches || res?.data?.batches || res?.data || [];
        const seen = new Set();
        const uniqueTopics = [];
        for (const b of batches) {
          const topic = (b?.topic || b?.title || '').trim();
          if (!topic || topic.length < 4) continue;
          const key = topic.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          uniqueTopics.push(topic);
          if (uniqueTopics.length >= 6) break;
        }
        setRecentTopics(uniqueTopics);
      })
      .catch(() => setRecentTopics([]));
  }, [ensureDashboardData]);

  // Topic chip suggestions — pull from strategy pillars first (the curated content
  // plan topics) and fall back to the user's batch history when no pillars yet.
  const suggestedTopics = useMemo(() => {
    const pillars = strategy?.contentPillars || strategy?.pillars || [];
    const fromPillars = [];
    for (const p of pillars) {
      const examples = p?.exampleTopics || p?.exampleIdeas || [];
      for (const ex of examples) {
        const t = String(ex).trim();
        if (!t) continue;
        if (!fromPillars.includes(t)) fromPillars.push(t);
        if (fromPillars.length >= 8) break;
      }
      if (fromPillars.length >= 8) break;
    }
    if (fromPillars.length > 0) return fromPillars;
    return recentTopics;
  }, [strategy, recentTopics]);

  // Richer suggestion items for the interactive cards: { title, pillar }, up to
  // ~20 pulled from the content plan's pillars. Shuffle cycles a small window
  // through this list in order — no AI, just the plan's own ideas. (When the
  // plan later emits localized title+angle ideas, this maps them 1:1.)
  const suggestionItems = useMemo(() => {
    // Prefer the strategy's smart, localized, brand-derived suggestions (a SHORT
    // card gist + a CONCRETE `prompt` that fills the box on click). Generated by
    // a separate free strategy sub-call; carried in the strategy snapshot.
    const snap = strategy?.strategy || strategy;
    const smart = Array.isArray(snap?.postSuggestions) ? snap.postSuggestions : [];
    if (smart.length) {
      const out = [];
      const seen = new Set();
      for (const s of smart) {
        const short = s && typeof s.short === 'string' ? s.short.trim() : '';
        if (!short || seen.has(short.toLowerCase())) continue;
        seen.add(short.toLowerCase());
        const prompt = s && typeof s.prompt === 'string' && s.prompt.trim() ? s.prompt.trim() : short;
        const pillar = s && typeof s.pillar === 'string' && s.pillar.trim() ? s.pillar.trim() : null;
        out.push({ title: short, prompt, pillar });
        if (out.length >= 20) break;
      }
      if (out.length) return out;
    }
    // Fallback: pillar exampleTopics (title-only; click fills the title).
    const pillars = strategy?.contentPillars || strategy?.pillars || [];
    const out = [];
    const seen = new Set();
    for (const p of pillars) {
      const pillarName = String(p?.name || p?.title || p?.pillar || '').trim();
      const examples = p?.exampleTopics || p?.exampleIdeas || [];
      for (const ex of examples) {
        const ti = String(ex).trim();
        if (!ti || seen.has(ti.toLowerCase())) continue;
        seen.add(ti.toLowerCase());
        out.push({ title: ti, prompt: ti, pillar: pillarName || null });
        if (out.length >= 20) break;
      }
      if (out.length >= 20) break;
    }
    if (out.length === 0) return (recentTopics || []).map((ti) => ({ title: ti, prompt: ti, pillar: null }));
    return out;
  }, [strategy, recentTopics]);
  const SUGG_WINDOW = 4;
  const [suggOffset, setSuggOffset] = useState(0);
  const visibleSuggestions = useMemo(() => {
    const nItems = suggestionItems.length;
    if (nItems === 0) return [];
    const w = Math.min(SUGG_WINDOW, nItems);
    return Array.from({ length: w }, (_, i) => suggestionItems[(suggOffset + i) % nItems]);
  }, [suggestionItems, suggOffset]);
  const canShuffle = suggestionItems.length > SUGG_WINDOW;

  // "From your plan" hashtags for the caption editor — derived from plan/brand
  // pillar names + brand words (no plan field stores hashtags), generic fallback.
  const planHashtags = useMemo(() => derivePlanHashtags(strategy, brand), [strategy, brand]);

  // Only CONNECTED accounts drive the picker. A just-disconnected account can
  // linger in the shared dashboard list (2-min cache) or come back from the API
  // with status='disconnected'; filtering here mirrors Edit Post and guarantees
  // we never offer — or generate "for" — a channel that's no longer connected.
  const connectedSocialAccounts = useMemo(
    () => (socialAccounts || []).filter((a) => a.status === 'connected'),
    [socialAccounts]
  );

  // Connected accounts grouped by platform (preserves multiple per platform).
  const accountsByPlatform = useMemo(() => {
    const groups = new Map();
    for (const acc of connectedSocialAccounts) {
      const p = String(acc.platform || '').toLowerCase();
      if (!p) continue;
      if (!groups.has(p)) groups.set(p, []);
      groups.get(p).push(acc);
    }
    return groups;
  }, [connectedSocialAccounts]);

  // Auto-pick all CONNECTED accounts on the first account's platform (one-shot).
  useEffect(() => {
    if (manual || autoPickedDefault) return;
    if (!connectedSocialAccounts.length) return;
    if (selectedAccounts.size === 0) {
      const defaultPlatform = String(connectedSocialAccounts[0].platform || '').toLowerCase();
      setSelectedAccounts(new Set(
        connectedSocialAccounts
          .filter((account) => String(account.platform || '').toLowerCase() === defaultPlatform)
          .map((account) => account.id)
      ));
    }
    setAutoPickedDefault(true);
  }, [connectedSocialAccounts, autoPickedDefault, selectedAccounts]);

  const toggleAccount = (accountId) => {
    if (manual) { manual.selectAccount(manual.accountId === accountId ? '' : accountId); return; }
    setSelectedAccounts((curr) => {
      const next = new Set(curr);
      next.has(accountId) ? next.delete(accountId) : next.add(accountId);
      return next;
    });
  };

  // Only connected account ids become real publish targets — a stale selection
  // for a now-disconnected account must never reach the batch payload.
  const selectedAccountIds = useMemo(
    () => Array.from(selectedAccounts).filter((accId) => connectedSocialAccounts.some((a) => a.id === accId)),
    [selectedAccounts, connectedSocialAccounts]
  );

  // Toggle a "generate as draft" platform (one with no connected account).
  const toggleDraftPlatform = (platformId) => {
    if (manual) { manual.change({ platform: platformId }); manual.selectAccount(''); return; }
    setSelectedDraftPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platformId)) next.delete(platformId);
      else next.add(platformId);
      return next;
    });
  };

  // Derived: unique platforms across BOTH the selected connected accounts AND
  // the "generate as draft" platforms (no account). Passed to the batch payload
  // so generation runs for every chosen network; draft platforms carry no
  // targetAccountId, so the result is just a draft until the user connects them.
  const selectedPlatforms = useMemo(() => {
    const set = new Set();
    for (const acc of connectedSocialAccounts) {
      if (selectedAccounts.has(acc.id)) {
        const p = String(acc.platform || '').toLowerCase();
        if (p) set.add(p);
      }
    }
    for (const p of selectedDraftPlatforms) {
      if (p) set.add(String(p).toLowerCase());
    }
    return Array.from(set);
  }, [connectedSocialAccounts, selectedAccounts, selectedDraftPlatforms]);

  // My-image media cap = tightest carousel limit across the selected platforms
  // (e.g. X allows only 4). { max, platform } — platform names the constraint.
  const mediaCap = useMemo(() => tightestMaxMedia(selectedPlatforms), [selectedPlatforms]);

  // "Publishes to" chips — derived from the user's actual selectedPlatforms
  // (see destChipsFor above), NOT a hardcoded list. Recomputed per postFormat
  // since the same selection carries different destinations per format.
  const destChips = useMemo(() => destChipsFor(postFormat, selectedPlatforms), [postFormat, selectedPlatforms]);
  // If the platform mix changed and the current selection now exceeds the cap,
  // trim it (keep the first N) and tell the user, so we never send a platform
  // more images than it accepts.
  useEffect(() => {
    if (!manual && imageSource === 'my' && pickedMedia.length > mediaCap.max) {
      setPickedMedia((prev) => prev.slice(0, mediaCap.max));
      notify({
        level: 'warning',
        title: mediaCap.platform
          ? t('createPost.mediaTrimmed', { n: mediaCap.max, platform: platformLabel(mediaCap.platform) })
          : t('createPost.mediaTrimmedGeneric', { n: mediaCap.max }),
      });
    }
  }, [mediaCap.max, mediaCap.platform, imageSource, pickedMedia.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Upload your own video" — is a video the picked media right now (My-media,
  // any postFormat — reel included, though that's currently unreachable while
  // showReel is off)? Drives the channel gating below.
  const hasPickedVideo = useMemo(() => pickedMedia.some((m) => isVideoAsset(m)), [pickedMedia]);
  const selectedTikTokAccount = connectedSocialAccounts.find(
    (account) => selectedAccounts.has(account.id) && String(account.platform || '').toLowerCase() === 'tiktok'
  ) || null;
  // Owner decision 2026-09-08: Slides now publish DIRECTLY to the profile by
  // default (backend default flips from inbox draft to direct in the same
  // release) — so the photo lane gets the SAME TikTok compliance card as
  // own-video, gated the same way (a connected TikTok account selected as a
  // target). destChipsFor already guarantees Slides only ever targets TikTok.
  //
  // Blocker fix (Opus review, 2026-09-09): the compliance card used to be
  // gated on postFormat/imageSource (own-video reel + Slides only), so the
  // plain Image/Carousel lane and the AI-generated reel lane could target a
  // connected TikTok account and hit "Generate" with NO compliance card —
  // platformOptions.tiktok stayed undefined and the dispatcher's direct-post
  // default published straight to the public profile while the UI still
  // promised an inbox draft. The card must show for EVERY lane the instant a
  // connected TikTok account is a selected target — mediaKind (below) is what
  // adapts the card's video-only bits, not this visibility gate.
  const tikTokComplianceVisible = !manual && !!selectedTikTokAccount;
  // Remove only unsupported targets when a clip is attached. Keep both newly
  // supported native-video channels selected; the rows use this same predicate.
  useEffect(() => {
    if (manual || !hasPickedVideo) return;
    setSelectedAccounts((prev) => {
      const toDrop = connectedSocialAccounts.filter(
        (a) => prev.has(a.id) && ownVideoChannelUnsupported(String(a.platform || '').toLowerCase(), isOwnVideoV2Lane)
      );
      if (!toDrop.length) return prev;
      const next = new Set(prev);
      toDrop.forEach((a) => next.delete(a.id));
      return next;
    });
    setSelectedDraftPlatforms((prev) => {
      const next = new Set([...prev].filter((p) => !ownVideoChannelUnsupported(p, isOwnVideoV2Lane)));
      return next.size === prev.size ? prev : next;
    });
  }, [hasPickedVideo, connectedSocialAccounts, isOwnVideoV2Lane]);

  // V-R9 §7 C6 — format-switch reconciliation. Switching AWAY from Video with a
  // clip picked drops it (a video post uses exactly one clip; other formats
  // don't render it) and says so via the existing trim-toast plumbing. Switching
  // INTO Video with images already picked needs NO code here — the images stay
  // in pickedMedia (they're simply not the video source) and the rail renders
  // the §C5 empty-state until a clip is chosen (checked by isVideoAsset below).
  const prevPostFormatRef = useRef(postFormat);
  useEffect(() => {
    if (manual) return;
    const prevFormat = prevPostFormatRef.current;
    prevPostFormatRef.current = postFormat;
    // Leaving reel (legacy) OR entering Slides with a video picked — both are
    // formats a clip can't ride (slides = image deck), so drop it visibly
    // rather than let it silently corrupt the compose payload.
    if ((prevFormat === 'reel' && postFormat !== 'reel') || (postFormat === 'slides' && prevFormat !== 'slides')) {
      setPickedMedia((prev) => {
        if (!prev.some((m) => isVideoAsset(m))) return prev;
        notify({ level: 'warning', title: t('media.videoDropped', { defaultValue: 'Removed the video — this format uses images.' }) });
        return prev.filter((m) => !isVideoAsset(m));
      });
    }
  }, [postFormat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unified-source reconciliation: 'none' (text-only) only makes sense for
  // Image; AI and My-media both work for every format. So the only bounce is
  // none + (video|slides) → Image. If a clip WAS picked, the postFormat change
  // re-triggers the C6 effect above, which drops it visibly.
  useEffect(() => {
    if (imageSource === 'none' && postFormat !== 'image') setPostFormat('image');
  }, [imageSource]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unified-source → Video sub-mode sync. In Video, the top source picker drives
  // videoSrc: My media = pick a ready clip; AI = generate (keep describe/photos,
  // default describe). useLayoutEffect (not useEffect) so videoSrc is reconciled
  // BEFORE paint — otherwise the wrong sub-body flashes for one frame on switch.
  useLayoutEffect(() => {
    if (postFormat !== 'reel') return;
    if (imageSource === 'my' && videoSrc !== 'clip') setVideoSrc('clip');
    else if (imageSource === 'ai' && videoSrc === 'clip') {
      setVideoSrc('describe');
      // Starting a fresh AI generate — drop any previously-picked ready clip so
      // submitting without generating can't publish the stale My-media clip.
      setPickedMedia((prev) => prev.filter((m) => !isVideoAsset(m)));
    }
  }, [imageSource, postFormat]); // eslint-disable-line react-hooks/exhaustive-deps

  // GA cinematic default: once entitlements load, default the video quality to
  // cinematic when the plan has it available (free/trial 0 quota stay Slideshow).
  // Never overrides a manual pick (videoQualityTouchedRef).
  useEffect(() => {
    if (!videoCinematicEnabled()) return;
    const cq = videoQuotaInfo(entitlements?.usage?.video_cinematic_monthly);
    if (cq.known && cq.available && !videoQualityTouchedRef.current) setVideoQuality('cinematic');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitlements]);

  // vpost pack (F) — leaving Video clears ONLY the settled inline render UI
  // (ready/failed notices + timer). An IN-FLIGHT render (submitting/rendering)
  // MUST stay tracked: clearing videoOpId/videoPhase here would orphan the
  // completion watch AND make the single-flight check misread the user's own
  // op as a foreign Media render (sticky false lockout). Only the transient
  // error is dropped in that case. An attached clip in pickedMedia is left
  // alone — the C6 effect above already drops it when the format actually
  // changes AWAY from 'reel'.
  useEffect(() => {
    if (postFormat === 'reel') return;
    if (videoPhase === 'submitting' || videoPhase === 'rendering') {
      setVideoError(null);
      return;
    }
    if (videoPhase !== 'idle') {
      if (videoElapsedTimerRef.current) { clearInterval(videoElapsedTimerRef.current); videoElapsedTimerRef.current = null; }
      setVideoPhase('idle');
      setVideoOpId(null);
      setVideoElapsed(0);
      setVideoError(null);
    }
  }, [postFormat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop the elapsed-time ticker on unmount.
  useEffect(() => () => { if (videoElapsedTimerRef.current) clearInterval(videoElapsedTimerRef.current); }, []);

  // Unified-source format handlers. The source picker is the SAME control across
  // every format and STAYS PUT on switch — no imageSource juggling, no pre-video
  // ref. Only rule: Video and Slides can't be text-only, so 'none' → 'ai' when
  // entering them. handleGenerate is now reel-aware (attaches the video asset for
  // postFormat==='reel' regardless of imageSource), so no source force is needed.
  const setTopFormatImage = () => { if (manual && hasPickedVideo) return; setPostFormat('image'); };
  const setTopFormatVideo = () => {
    if (manual) { if (manual.disabled || (pickedMedia.length && !hasPickedVideo)) return; setImageSource('my'); setVideoSrc('clip'); setPostFormat('reel'); if (!hasPickedVideo) setPickerOpen(true); return; }
    setImageSource((prev) => (!showReel && videoPublishV2 ? 'my' : (prev === 'none' ? 'ai' : prev)));
    if (!showReel && videoPublishV2) {
      setVideoSrc('clip');
      if (!pickedMedia.some((media) => isVideoAsset(media))) { setPickerMode('media'); setPickerOpen(true); }
    }
    setPostFormat('reel');
  };
  const setTopFormatSlides = () => {
    if (manual) return;
    setImageSource((prev) => (prev === 'none' ? 'ai' : prev));
    setPostFormat('slides');
  };

  // vpost (A) a11y — ArrowLeft/Right on the TOP format radiogroup. Must route
  // through the SAME setters as clicks (the imageSource juggling above is the
  // invariant); a bare setPostFormat('reel') here would build a broken post
  // (imageSource left on 'ai' → the video silently drops at submit). Captures
  // the group node BEFORE the rAF: React nulls e.currentTarget after the
  // handler returns. Replaces the old shared formatKeyNav — the per-branch
  // format radiogroups that used it were removed by this port.
  const topFormatKeyNav = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const group = e.currentTarget;
    const idx = TOP_FORMATS.indexOf(postFormat);
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = TOP_FORMATS[((idx < 0 ? 0 : idx) + dir + TOP_FORMATS.length) % TOP_FORMATS.length];
    if (next === 'reel') setTopFormatVideo();
    else if (next === 'slides') setTopFormatSlides();
    else setTopFormatImage();
    requestAnimationFrame(() => {
      group.querySelector(`[data-format="${next}"]`)?.focus();
    });
  };

  // vpost pack (D) — inline video generation. Mirrors BrandKitScreen.doGenerate's
  // isReel branch EXACTLY: two mutually exclusive submit shapes, never sending
  // the inactive mode's fields. `language` uses the SAME brand-profile fields
  // CreatePost already resolves brand defaults from (see brandDefaults above).
  const videoLanguage = brand?.defaultPublishLanguage || brand?.language || undefined;
  // Per-video language picker (mirrors BrandKitScreen). Limited to languages we
  // ship native TTS voices for (run-video.ts AZURE_VOICES); labels use each
  // language's own autonym. Default option follows videoLanguage (strategy).
  const VIDEO_NARRATION_LANGS = ['uk', 'ru', 'en', 'de', 'es', 'pl', 'cs', 'lt', 'lv', 'sv'];
  const videoLangAutonym = (code) => {
    try {
      const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) || code;
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch (_) { return code; }
  };
  const videoDefaultLangName = videoLanguage
    ? (() => { try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(String(videoLanguage).slice(0, 2)) || videoLanguage; } catch (_) { return videoLanguage; } })()
    : null;
  const videoAllowance = entitlements?.usage?.video_reels_monthly || null;
  // Cinematic + standard reels are metered separately; the CTA-limit follows the
  // ACTIVE quality (over quota, or a 0-quota plan on cinematic → upsell).
  const cinemaQuota = videoQuotaInfo(entitlements?.usage?.video_cinematic_monthly);
  const stdQuota = videoQuotaInfo(entitlements?.usage?.video_reels_monthly);
  // Cinematic quota only applies in DESCRIBE mode (photos mode always renders a
  // standard slideshow + never sends quality) — else a user out of cinematic but
  // with standard left dead-ends in photos mode with the toggle hidden.
  const activeVideoQuota = (videoSrc === 'describe' && videoQuality === 'cinematic') ? cinemaQuota : stdQuota;
  const videoAtLimit = activeVideoQuota.known && !activeVideoQuota.available;
  // Proactive single-flight: another VIDEO_RENDER op (e.g. started in Media)
  // already in flight — block Generate and name where it came from. 'pending'
  // included: a just-submitted op sits there before the server flips it to
  // 'running' (GenerationStatusContext status vocabulary).
  const otherVideoRenderRunning = (genOperationsArr || []).some(
    (op) => op.kind === 'video_reel' && (op.status === 'running' || op.status === 'pending') && op.id !== videoOpId
  );
  const videoCtaDisabled = noBrand || videoPhase === 'submitting' || videoPhase === 'rendering' || videoAtLimit || otherVideoRenderRunning
    || (videoSrc === 'photos' && videoScenes.length === 0)
    || (videoSrc === 'describe' && videoPrompt.trim().length < 3);

  const generateVideoInline = async () => {
    if (manual) return;
    if (creationAccessState(dash) !== 'allowed') return;
    if (noBrand) {
      setVideoError(t('noBrandGuard.disabledHint'));
      return;
    }
    if (videoPhase === 'submitting' || videoPhase === 'rendering') return;
    if (otherVideoRenderRunning) {
      setVideoError(t('media.oneRenderingFromMedia', { defaultValue: "A video is already rendering — one at a time. It's the one you started in Media." }));
      return;
    }
    if (videoAtLimit) {
      setVideoError(t('media.videoLimit', { defaultValue: 'Monthly video limit reached.' }));
      return;
    }
    let submitBody;
    if (videoSrc === 'describe') {
      const promptTrimmed = videoPrompt.trim();
      if (promptTrimmed.length < 3) {
        setVideoError(t('media.describeMin', { defaultValue: 'Describe the video in a few words first.' }));
        return;
      }
      submitBody = {
        prompt: promptTrimmed,
        brandVoice: videoBrandVoice,
        narration: videoNarration,
        script: videoScript.trim() || undefined,
        hookLine: videoHook.trim() || undefined,
        language: videoLang || videoLanguage,
        referenceAssetIds: videoDescribeRefs.length ? videoDescribeRefs.map((r) => r.id) : undefined,
        // Tier-2 cinematic (admin pilot) — describe mode only; server double-
        // gates (VIDEO_CINEMATIC_ENABLED + admin). Omitted otherwise = standard
        // slideshow, byte-identical to today.
        quality: (videoCinematicEnabled() && videoQuality === 'cinematic') ? 'cinematic' : undefined,
      };
    } else if (videoSrc === 'photos') {
      if (videoScenes.length === 0) {
        setVideoError(t('media.videoNeedsFrames', { defaultValue: 'Pick at least one image to build the video from.' }));
        return;
      }
      submitBody = {
        frameAssetIds: videoScenes.map((s) => s.id),
        narration: videoNarration,
        script: videoScript.trim() || undefined,
        hookLine: videoHook.trim() || undefined,
        language: videoLang || videoLanguage,
      };
    } else {
      // 'clip' source has no generation step (Options/footer hidden).
      return;
    }
    setVideoError(null);
    setVideoPhase('submitting');
    lastVideoSubmitRef.current = {
      videoSrc, submitBody,
      videoScenes: [...videoScenes],
      videoDescribeRefs: [...videoDescribeRefs],
    };
    try {
      const res = await api.brandAssets.generateVideo(submitBody);
      const opId = (res?.data ?? res)?.operationId;
      if (opId) {
        setVideoOpId(opId);
        setVideoPhase('rendering');
        setVideoElapsed(0);
        if (videoElapsedTimerRef.current) clearInterval(videoElapsedTimerRef.current);
        videoElapsedTimerRef.current = setInterval(() => setVideoElapsed((s) => s + 1), 1000);
      } else {
        setVideoPhase('idle');
      }
    } catch (err) {
      if (err?.status === 409) {
        setVideoPhase('idle');
        setVideoError(t('media.oneRenderingFromMedia', { defaultValue: "A video is already rendering — one at a time. It's the one you started in Media." }));
      } else if (err?.status === 402 || err?.code === 'QUOTA_EXCEEDED') {
        // Out of quota for the picked quality → convert, don't dead-end.
        // TASK-331: a lapsed org gets PLAN_LAPSED (not QUOTA_EXCEEDED) on this
        // same 402 — check it first so the modal opens in its 'renew' face
        // instead of the upgrade/trial one.
        setVideoPhase('idle');
        openUpgrade({ feature: 'default', variant: err?.code === 'PLAN_LAPSED' ? 'renew' : 'upgrade' });
      } else {
        setVideoPhase('failed');
        setVideoError(friendlyErrorMessage(err));
      }
    }
  };

  const retryFailedVideoInline = () => {
    const last = lastVideoSubmitRef.current;
    setVideoError(null);
    setVideoPhase('idle');
    if (!last) return;
    setVideoSrc(last.videoSrc);
    if (last.videoSrc === 'describe') {
      setVideoPrompt(last.submitBody.prompt || '');
      setVideoBrandVoice(last.submitBody.brandVoice !== false);
      setVideoDescribeRefs(Array.isArray(last.videoDescribeRefs) ? last.videoDescribeRefs : []);
    } else if (last.videoSrc === 'photos') {
      setVideoScenes(Array.isArray(last.videoScenes) ? last.videoScenes : []);
    }
    setVideoNarration(last.submitBody.narration !== false);
    setVideoScript(last.submitBody.script || '');
    setVideoHook(last.submitBody.hookLine || '');
  };

  // vpost pack (D7) — completion watch. The op mechanism is shared with Media
  // (same VIDEO_RENDER kind, same server poll) — a render started in New Post
  // finishes and is picked up here exactly like BrandKitScreen's justDone effect.
  // Runs on every operationsArr poll change, so the closure's postFormat is
  // fresh at the moment the op turns terminal.
  useEffect(() => {
    if (!videoOpId) return;
    const op = (genOperationsArr || []).find((o) => o.id === videoOpId);
    if (!op) return;
    if (op.status !== 'succeeded' && op.status !== 'failed' && op.status !== 'cancelled') return;
    if (seenVideoDoneRef.current.has(op.id)) return;
    seenVideoDoneRef.current.add(op.id);
    if (videoElapsedTimerRef.current) { clearInterval(videoElapsedTimerRef.current); videoElapsedTimerRef.current = null; }
    if (op.status === 'failed') {
      setVideoPhase('failed');
      // Refund note composed INTO the message (BrandKitScreen ~317 pattern) —
      // the cancelled branch below deliberately omits it (no refund claim we
      // can't stand behind), and the submit-catch path shows just the API error.
      setVideoError(`${op.error || op.errorMessage || t('media.videoRenderFailed', { defaultValue: 'Video render failed.' })} ${t('media.videoRefunded', { defaultValue: 'Your video credit was not used.' })}`);
      return;
    }
    if (op.status === 'cancelled') {
      setVideoPhase('failed');
      setVideoError(t('media.videoRenderFailed', { defaultValue: 'Video render failed.' }));
      return;
    }
    // Succeeded while the user has switched AWAY from Video: do NOT touch
    // pickedMedia — they're mid-image-composing and clobbering their picked
    // images is the worse bug. The clip is in Media like any render; reset so
    // a later return to Video starts clean.
    if (postFormat !== 'reel') {
      resetVideoComposer();
      return;
    }
    // Succeeded — fetch the library and auto-attach the newest video asset.
    api.brandAssets.list(undefined, { includeVideos: true })
      .then((res) => {
        const assets = (res?.data ?? res)?.assets || [];
        const newestVideo = assets.find((a) => isVideoAsset(a)) || null;
        if (newestVideo) {
          setPickedMedia([{
            id: newestVideo.id, url: newestVideo.url, tag: newestVideo.tag,
            type: newestVideo.type, mimeType: newestVideo.mimeType,
            durationSeconds: newestVideo.durationSeconds ?? newestVideo.duration_seconds ?? null,
            posterUrl: newestVideo.posterUrl ?? newestVideo.poster_url ?? null,
            width: newestVideo.width ?? null, height: newestVideo.height ?? null,
          }]);
        }
        setVideoPhase('ready');
      })
      .catch(() => setVideoPhase('ready'));
  }, [genOperationsArr, videoOpId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetVideoComposer = () => {
    setVideoPhase('idle');
    setVideoOpId(null);
    setVideoElapsed(0);
    setVideoError(null);
    setVideoPrompt('');
    setVideoScenes([]);
    setVideoDescribeRefs([]);
    setVideoHook('');
    setVideoScript('');
  };

  const moveVideoScene = (from, to) => {
    if (to < 0 || to >= videoScenes.length || from === to) return;
    setVideoScenes((prev) => {
      const arr = [...prev];
      const [it] = arr.splice(from, 1);
      arr.splice(to, 0, it);
      return arr;
    });
  };

  // When the ONLY selected channels are "generate as draft" platforms (no
  // connected account to publish to), force the schedule UI to draft: the
  // Publish/Spread options can't apply (handleGenerate forces draft anyway), so
  // showing them selected would be misleading. Scoped to "draft platforms but
  // zero connected" so a normal user with connected accounts keeps their pick.
  useEffect(() => {
    if (selectedDraftPlatforms.size > 0 && selectedAccountIds.length === 0 && schedule !== 'draft') {
      setSchedule('draft');
    }
  }, [selectedDraftPlatforms, selectedAccountIds, schedule]);

  const remainingPosts = (() => {
    const u = entitlements?.usage || {};
    const q = textSource === 'write' ? u.manual_posts_monthly : u.publish_ready_posts_monthly;
    if (!q) return null;
    if (q.remaining === null) return t('createPost.unlimited');
    return q.remaining;
  })();
  // Numeric remaining allowance (null = unlimited/unknown). Used to warn BEFORE
  // the user hits the hard backend rejection when the requested count would
  // exceed this month's post budget.
  const remainingPostsNum = (() => {
    const usage = entitlements?.usage;
    const q = textSource === 'write' ? usage?.manual_posts_monthly : usage?.publish_ready_posts_monthly;
    if (!q || q.remaining === null || q.remaining === undefined) return null;
    return Number(q.remaining);
  })();
  const exceedsPostLimit = remainingPostsNum != null && count > remainingPostsNum;
  // Global single-flight lock: while any batch is generating, block starting a
  // new one here (mirrors the content-worker cap=1). The user jumps into the
  // active batch instead. The assistant + backend enforce the same rule.
  const activeBatch = useMemo(
    () => (activeBatches || []).find((b) => ['queued', 'generating', 'reserved'].includes(b.status)),
    [activeBatches],
  );
  const generationLocked = !manual && !!activeBatch;
  // V-R9 §7 C5 — Video format has no image-post fallback: Generate stays
  // disabled until exactly one video clip is picked (inline reason near the CTA).
  // Blocks the bottom "Generate posts" CTA for BOTH reel sources until a clip
  // exists: AI media → until the inline "Generate video" attaches one; My media →
  // until one is picked. (No imageSource clause — reel always needs a clip.)
  const videoFormatBlocked = showVideoFormat && postFormat === 'reel'
    && !pickedMedia.some((m) => isVideoAsset(m));
  // Phase 4b — AI slides quota surface: carousel_generations_monthly is reserved
  // server-side and exhaustion silently degrades the post to a single image, so
  // say it up front. Shape mirrors publish_ready_posts_monthly above
  // ({ used, limit, remaining }; limit == null → unlimited).
  const carouselQuota = entitlements?.usage?.carousel_generations_monthly;
  const slidesQuotaOut = !!(carouselQuota && carouselQuota.limit != null
    && (carouselQuota.remaining != null
      ? Number(carouselQuota.remaining) <= 0
      : Number(carouselQuota.used ?? 0) >= Number(carouselQuota.limit)));
  // Per-generation ceiling: never more than 30 at once, and never more than the
  // remaining monthly allowance (null = unlimited tier → just the 30 cap).
  const maxCount = Math.min(30, remainingPostsNum ?? 30);

  // Brand defaults aggregated from brand_profile + content_strategy.
  // Strategy wins when both have a value (it's derived from the brand pass).
  const brandDefaults = useMemo(() => {
    const s = strategy || {};
    const b = brand || {};
    // Canonical language source (in priority order):
    //   1. brand.sourceMetadata.languages — full array from Edit Strategy
    //      (preserves both primary + any extras the user picked).
    //   2. brand.language — the primary single-language column. The Edit
    //      Strategy form always writes this on save, so it's the most
    //      reliable single source.
    //   3. strategy.language — derived from brand at strategy-generation
    //      time. Lagging indicator if brand was edited after.
    //
    // Build a unique ordered array so the UI can show "UK · EN" when there
    // are multiple, or just "UK" when single.
    const metaLanguages = Array.isArray(b?.sourceMetadata?.languages)
      ? b.sourceMetadata.languages.filter(Boolean)
      : [];
    const primaryFromBrand = b?.language ? [b.language] : [];
    const primaryFromStrategy = s?.language ? [s.language] : [];
    const combined = [...metaLanguages, ...primaryFromBrand, ...primaryFromStrategy];
    const languages = Array.from(new Set(combined.map((l) => String(l).toLowerCase()).filter(Boolean)));
    // Visual style(s): friendly label(s) for the full multi-select set
    // (Edit Strategy saves brand.visualStyles), not the raw key like
    // "premium_dark". Falls back to the single style for legacy profiles.
    const VISUAL_STYLE_I18N = {
      premium_dark: 'editStrategy.palettePremiumDarkLabel',
      minimalist: 'editStrategy.paletteMinimalistLabel',
      modern: 'editStrategy.paletteModernLabel',
      vibrant: 'editStrategy.paletteVibrantLabel',
      playful: 'editStrategy.palettePlayfulLabel',
      corporate: 'editStrategy.paletteCorporateLabel',
      vintage: 'editStrategy.paletteVintageLabel',
    };
    const labelForStyle = (k) => (k && VISUAL_STYLE_I18N[k] ? t(VISUAL_STYLE_I18N[k]) : k);
    const styleSet = Array.isArray(b.visualStyles) && b.visualStyles.length
      ? b.visualStyles
      : [s.visualStyle || s.visual || b.visualStyle || b.visual].filter(Boolean);
    // Tone presets the user actually picked in Edit Strategy. The brand stores
    // the primary in `toneOfVoice` and any extras as a CSV in
    // `customToneDescription` (mirrors EditStrategy.parseTones). Surface the
    // friendly labels; fall back to a strategy-level tone when no presets.
    const toneSet = new Set();
    if (b.toneOfVoice) toneSet.add(String(b.toneOfVoice).toLowerCase());
    if (typeof b.customToneDescription === 'string' && b.customToneDescription.includes(',')) {
      b.customToneDescription.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean).forEach((x) => toneSet.add(x));
    }
    const toneKeys = Array.from(toneSet).filter((k) => CP_TONE_I18N[k]).slice(0, 3);
    const toneLabels = toneKeys.length
      ? toneKeys.map((k) => t(CP_TONE_I18N[k]))
      : (s.tone ? [Array.isArray(s.tone) ? s.tone.join(' · ') : String(s.tone)] : []);
    return {
      audience: [s.audience, s.targetAudience, b.audience, b.targetAudience]
        .find((value) => value && !/^(target audience|business description) not provided$/i.test(String(value).trim())),
      tone: toneLabels,
      visual: styleSet.length ? styleSet.map(labelForStyle).join(' · ') : null,
      languages,
      goal: s.goal || b.goal,
    };
  }, [strategy, brand, t]);
  const hasBrandDefaults = meaningfulBrandContext || visualBrandContextAvailable;

  // Seed the visual picker from the brand's saved styles once they load
  // (one-shot — after that the per-batch selection belongs to the user).
  useEffect(() => {
    if (visualInitialized || !brand) return;
    const fromBrand = Array.isArray(brand.visualStyles) ? brand.visualStyles : [];
    const keys = fromBrand.map(normalizeVisualKey).filter(Boolean);
    const single = normalizeVisualKey(brand.visualStyle);
    const seed = keys.length ? keys : (single ? [single] : ['premium_dark']);
    setVisualSet(new Set(seed));
    setBrandLook({
      colors: brand.applyBrandColors !== false,
      typography: brandTypographyEnabled() && brand.typography?.defaultAssetId
        ? brand.typography.enabled === true
        : null,
    });
    setVisualInitialized(true);
  }, [brand, visualInitialized]);

  // Final chip list — pillar topics preferred, then batch history, then canned fallback.
  const topics = suggestedTopics.length > 0 ? suggestedTopics : RECENT_TOPIC_FALLBACKS;
  const topicsLabel = (() => {
    if (strategy?.contentPillars?.some((p) => (p.exampleTopics || []).length > 0)) return t('createPost.fromYourContentPlan');
    if (recentTopics.length > 0) return t('createPost.recentTopics');
    return t('createPost.suggestedTopics');
  })();

  // Shared upload pipeline used by both the file picker and drag-and-drop.
  const uploadFiles = async (files) => {
    if (creationAccessState(dash) !== 'allowed') return;
    const incoming = Array.from(files || []);
    // Cap reference images at 3 — that's the max the gpt-image composition path
    // consumes; more mostly adds generation latency toward the platform timeout.
    const room = Math.max(0, MAX_REFERENCE_IMAGES - attachments.length);
    if (incoming.length > room) {
      notify({ level: 'warning', title: t('createPost.maxReferenceImages', { max: MAX_REFERENCE_IMAGES }) });
    }
    for (const file of incoming.slice(0, room)) {
      // Reject wrong types / oversized files before uploading (backend
      // re-checks). Drag-and-drop bypasses the picker's accept filter, so this
      // guard matters there too.
      if (!ALLOWED_ATTACHMENT_MIME.test(file.type || '')) {
        notify({ level: 'warning', title: t('createPost.attachmentTypeRejected', { name: file.name }) });
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        notify({ level: 'warning', title: t('createPost.attachmentTooLarge', { name: file.name, max: MAX_ATTACHMENT_MB }) });
        continue;
      }
      const tempId = `tmp-${Date.now()}-${file.name}`;
      setAttachments((curr) => [...curr, { id: tempId, name: file.name, mimeType: file.type, uploading: true }]);
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const res = await api.assistant.uploadAttachment({
          dataUrl,
          originalFilename: file.name,
          mimeType: file.type,
          type: file.type.startsWith('image/') ? 'image' : 'file',
        });
        const asset = res?.asset || res?.data?.asset || res;
        setAttachments((curr) => curr.map((a) => a.id === tempId
          ? { id: asset?.id || tempId, name: file.name, mimeType: file.type, url: asset?.publicUrl || asset?.url, uploading: false }
          : a));
      } catch (err) {
        setAttachments((curr) => curr.map((a) => a.id === tempId
          ? { ...a, uploading: false, error: errMessage(err) }
          : a));
      }
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // reset so user can re-pick same file
    await uploadFiles(files);
  };

  // Drag-and-drop state + handlers
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.items?.length > 0) setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    await uploadFiles(files);
  };

  const removeAttachment = (id) => {
    setAttachments((curr) => curr.filter((a) => a.id !== id));
  };

  // Publish-to section ref + a brief accent ring, used when the user tries to
  // generate without choosing a channel: we scroll the section into view and
  // pulse it so the fix is obvious (paired with the warning toast) instead of a
  // blocking browser alert.
  const publishSectionRef = useRef(null);
  const [flashPublish, setFlashPublish] = useState(false);
  const flashPublishSection = () => {
    publishSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashPublish(true);
    window.setTimeout(() => setFlashPublish(false), 1600);
  };

  const handleGenerate = async (tikTokOptions = null, tikTokSubmit = false) => {
    // Free owns a separate mutation contract, but uses this same composer view.
    if (manual) {
      if (!manual.recovering && imageSource === 'my' && pickedMedia.length === 0) return;
      await manual.save(); return;
    }
    if (creationAccessState(dash) !== 'allowed') return;
    if (noBrandBlocksGeneration) {
      notify({ level: 'warning', title: t('noBrandGuard.createBody') });
      return;
    }
    if (generationLocked) {
      notify({ level: 'warning', title: t('createPost.generationLockedAlert') });
      return;
    }
    if (textSource === 'ai' && aiTextDisabled) {
      notify({ level: 'warning', title: t('textLocked.lockedLabel') });
      return;
    }
    if (imageSource === 'ai' && aiImageDisabled) {
      notify({
        level: 'warning',
        title: imageAllowanceZero
          ? t('imageLocked.lockedLabel')
          : imageAvailabilityError
            ? t('imageLocked.error')
            : t('imageLocked.loading'),
      });
      return;
    }
    // In 'I'll write it' mode the caption can stand in for the topic, so a
    // 3+ char caption is enough even with an empty prompt.
    const hasComposeText = textSource === 'write' && providedCaption.trim().length >= 3;
    if (prompt.trim().length < 3 && !hasComposeText) {
      notify({ level: 'warning', title: t('createPost.alertDescribeBatch') });
      return;
    }
    // 'My image' with nothing chosen would silently fall back to AI image —
    // ask the user to pick instead.
    if (imageSource === 'my' && pickedMedia.length === 0) {
      notify({ level: 'warning', title: t('createPost.alertChooseImage') });
      setPickerOpen(true);
      return;
    }
    // V-R9 §7 C5 — Video format requires exactly one video clip picked. No
    // silent image-post fallback: block and reopen the picker on that format.
    if (showVideoFormat && postFormat === 'reel' && !pickedMedia.some((m) => isVideoAsset(m))) {
      notify({ level: 'warning', title: t('media.videoNeedsClip', { defaultValue: 'Pick a video from your Media library — or create one in Media first.' }) });
      setPickerMode('media');
      setPickerOpen(true);
      return;
    }
    // selectedPlatforms already unions connected-account platforms + "generate
    // as draft" platforms and excludes any stale/disconnected account, so an
    // empty list means there's genuinely nothing to generate for.
    if (selectedPlatforms.length === 0) {
      notify({ level: 'warning', title: t('createPost.alertPickAccount') });
      flashPublishSection();
      return;
    }
    // Selecting ONLY "generate as draft" platforms (networks with no connected
    // account) is allowed — there's just nothing to publish to, so we force
    // draft mode below and the picker shows a connect-to-publish notice.

    // Schedule mode mapping (normalizeScheduleMode accepts now/schedule/draft/
    // publish_now; publish_now normalizes to the backend "now" mode):
    //   'now'    → publish_now: the single/first post publishes immediately.
    //   'spread' → publish_now WITH scheduleStartAt=now (below). The backend
    //              "now" mode publishes the FIRST post now and schedules the
    //              rest — combined with scheduleStartAt=today that spreads them
    //              across the coming days. (Was 'schedule', i.e. EVERYTHING
    //              queued with the first slot landing tomorrow — which is why
    //              "Spread starting today" never published anything today.)
    // If no connected account is selected (only "generate as draft" platforms),
    // we physically can't publish — force draft regardless of the toggle. The
    // picker notice tells the user to connect the network to publish later.
    const scheduleMode = submissionDecision.scheduleMode;

    setSubmitting(true);
    // Previously fired setActivity({ kind: 'planning', step: 'Starting
    // batch…' }) here — that lit up the legacy ActivityPill in the topbar
    // ("Updating content plan · Starting batch…") and the activity was
    // NEVER cleared on success (only on error), so it persisted alongside
    // the new GenerationStatusPlate after the batch started. With
    // startContentBatchOperation below already wiring the new plate to
    // the batch as soon as the API returns, there's no need for any old-
    // pill state during submit. The submit button's disabled+spinner
    // state already covers the brief in-flight window.
    try {
      const baseRefAssetIds = attachments
        .filter((a) => a.id && !String(a.id).startsWith('tmp-') && !a.error)
        .map((a) => a.id);
      const baseRefUrls = attachments
        .filter((a) => a.url && !a.error)
        .map((a) => a.url);
      // Brand-Kit references rail (AI image mode only): picked references + the
      // default primary logo (unless toggled off) GUIDE the generated image.
      // Flag-gated + AI-only, deduped — so flag-off / non-AI payloads are
      // byte-identical and no reference is ever double-counted.
      const railRefs = collectCreateRailReferences({
        enabled: composeSourcesEnabled && imageSource === 'ai' && postFormat !== 'reel',
        pickedRefs, primaryLogo, logoExcluded, useBrand,
      });
      const referenceAssetIds = [...new Set([...baseRefAssetIds, ...railRefs.map((r) => r.id).filter(Boolean)])];
      const referenceUrls = [...new Set([...baseRefUrls, ...railRefs.map((r) => r.url).filter(Boolean)])];
      // Default EVERY generated post to image_post (text + a generated visual),
      // on every platform — including the "text-first" networks (LinkedIn, X).
      // Product decision: when a user asks for a post they expect a picture, not
      // raw copy. The content-worker still falls back to text-only if the visual
      // generation fails, so defaulting to image_post is safe. (Previously
      // LinkedIn/X-only batches fell back to text_post — which is exactly why
      // those posts kept publishing without any image.)
      // 'No image' composes a text-only post (backend gates all image
      // generation on image_post); every other mode keeps the image_post
      // default. When imageSource is left on 'ai' this is byte-identical.
      const formatForBatch = imageSource === 'none' ? 'text_post' : 'image_post';
      // Compose-mode fields — only present when the user opted out of AI for
      // that axis, so the default (AI text + AI image) payload is unchanged.
      const composeCaption = textSource === 'write' ? providedCaption.trim() : '';
      // Reel = a single video-asset post: always attach the rendered/picked clip
      // (imageSource is 'ai' when the video was AI-generated, 'my' when a ready
      // clip was chosen — either way the post carries that ONE video, never AI
      // image fields). Non-reel keeps the my-media compose behavior.
      const composeMediaIds = postFormat === 'reel'
        ? pickedMedia.filter((m) => isVideoAsset(m)).map((m) => m.id)
        : (imageSource === 'my' ? pickedMedia.map((m) => m.id) : []);
      // Visual style selection from the multi-select picker.
      const visualKeys = Array.from(visualSet);
      const payload = {
        // Topic is still required by the backend (min 3); in compose-text mode
        // an empty prompt borrows the first 80 chars of the caption.
        topic: prompt.trim() || composeCaption.slice(0, 80),
        platforms: selectedPlatforms,
        targetAccountIds: selectedAccountIds,
        format: formatForBatch,
        formats: [formatForBatch],
        // A video reel is always exactly one post (one clip) — never fan out.
        count: (showVideoFormat && postFormat === 'reel') ? 1 : count,
        scheduleMode,
        scheduleTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        scheduleStartAt: schedule === 'spread' ? new Date().toISOString() : null,
        confirmCreditSpend: true,
        // TASK-236 (#354, EPIC-023 pricing v2): Economy/Standard/Premium for
        // this batch's text + image generation (ai-router's
        // resolveGenerationLevel reads either `level` or `metadata.level` —
        // sent both places so it survives however content-worker eventually
        // threads it through to the ai-router call). Owner decision
        // 2026-09-08: while the picker is hidden (generationLevelPickerVisible
        // false), generationLevelPayloadFields returns `{}` — neither key is
        // sent, `level` is dropped by JSON.stringify, and the backend falls
        // back to its own pre-picker default (see lib/flags.js).
        ...generationLevelPayloadFields(generationLevel, generationLevelPickerVisible),
        // useContentPlan removed — backend (generation-batch-service.ts:135)
        // already defaults to true when the field is omitted, and per
        // product direction the content plan is ALWAYS used as context
        // for every generation. Multi-brand workspaces will get their
        // own opt-out when that feature lands; until then there's no UX
        // surface for turning the plan off.
        referenceAssetIds: referenceAssetIds.length > 0 ? referenceAssetIds : undefined,
        referenceUrls: referenceUrls.length > 0 ? referenceUrls : undefined,
        // Visual style selection (New Post multi-select picker). One style =>
        // force it on every post (consistent look). Several => omit the hard
        // force so the backend mixes across the brand's style set per post.
        // `visualStyles` is sent too for forward-compatible per-batch subset
        // mixing (ignored by the current backend, which rotates the brand set).
        visualStyle: visualKeys.length === 1 ? visualKeys[0] : undefined,
        visualStyles: visualKeys.length ? visualKeys : undefined,
        // Per-generation brand-colors override. Only sent when the brand has
        // colors AND the toggle differs from the brand default — otherwise omit
        // so the brand's apply_brand_colors default governs (no needless override).
        applyBrandColors: (Array.isArray(brand?.brandColors) && brand.brandColors.length > 0 && useBrandColors !== (brand.applyBrandColors !== false))
          ? useBrandColors
          : undefined,
        // Per-post typography override. Matching the brand default stays absent
        // so later Brand Kit changes continue to govern this batch.
        ...(brandTypographyEnabled()
          && postFormat === 'image'
          && imageSource === 'ai'
          && useBrand
          && brand?.typography?.defaultAssetId
          && useTypography !== (brand.typography.enabled === true)
          ? { applyTypography: useTypography }
          : {}),
        // "Use my brand" master override. Only relevant for AI image; sent only
        // when the user turned brand OFF (default ON => omitted => byte-
        // identical). The image is then built from `topic` (the main prompt
        // field) alone via the neutral image directive — there is no separate
        // art-direction field, so the user fills one box, not two.
        applyBrand: (imageSource === 'ai' && !useBrand && postFormat !== 'reel') ? false : undefined,
        // Compose mode (backend migration 0060): user-provided caption and/or
        // chosen media. Conditionally-undefined so the AI default is untouched.
        providedCaption: composeCaption ? composeCaption : undefined,
        providedMediaAssetIds: composeMediaIds.length ? composeMediaIds : undefined,
        // Phase 4b — AI slides: the FIRST (and only) postFormat-driven payload
        // field. Server parses body.slideCount (generation-batch-service.ts:2851;
        // 2–5 forces a carousel of N images, overriding the Instagram heuristic)
        // and TikTok photo-deck routing follows from the media shape. Additive:
        // undefined in every other source/format combination → payloads there
        // stay byte-identical.
        slideCount: (showReel && imageSource === 'ai' && postFormat === 'slides') ? slideCount : undefined,
        // TikTok v2 only: the content batch's scheduling contract carries these
        // options through to the scheduler. DRAFT is hardcoded until audit approval.
        platformOptions: tikTokOptions ? { tiktok: tikTokOptions } : undefined,
        // An explicit No media choice must never enter the backend's waiting-for-visual gate.
        allowTextOnly: imageSource === 'none' ? true : undefined,
      };
      const res = await api.content.createBatch(payload);
      // API client unwraps { ok, data } → res === { batch, jobs }
      const batch = res?.batch || res?.data?.batch || res;
      if (batch?.id) {
        try { trackOnce('first_post_created', { source: getFunnelSource(), surface: 'create_batch' }); } catch { /* fail-silent */ }
      }
      // Hand off to ContentFeed so the polling loop picks it up
      if (startGenerationBatch && batch?.id) {
        startGenerationBatch(batch);
      }
      // Register with global status plate so user sees the batch
      // progress no matter where they navigate (Dashboard, Library,
      // PostPreview). The ContentFeed bridge in App.jsx will mirror
      // the batch's status into this op.
      if (batch?.id) {
        try {
          startContentBatchOperation(startOperation, {
            batchId: batch.id,
            total: count,
            platforms: selectedPlatforms || [],
          });
        } catch (_) { /* best-effort */ }
      }
      onGenerated?.(tikTokSubmit && schedule !== 'draft' ? { ...batch, videoPublishV2TikTok: true } : batch);
    } catch (err) {
      setActivity(null);
      if (err?.status === 402 || err?.code === 'QUOTA_EXCEEDED') {
        // TASK-331: PLAN_LAPSED first — a lapsed org's generate call 402s with
        // that code, never QUOTA_EXCEEDED, and needs the renew face of the modal.
        openUpgrade({ feature: 'default', variant: err?.code === 'PLAN_LAPSED' ? 'renew' : 'upgrade' });
      } else {
        notify({ level: 'error', title: t('createPost.generateFailed', { error: friendlyErrorMessage(err) }) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Compose-mode derivations for the mode-aware CTA + count UI. Flag-gated: when
  // composeSourcesEnabled is false both sources stay 'ai', composeCtaKey is null and
  // isComposeMode is false → the classic "Generate N posts" path is byte-identical.
  const isComposeMode = composeSourcesEnabled && (textSource === 'write' || imageSource === 'my');
  const submissionDecision = resolveCreatePostSubmission({
    noBrand, prompt, providedCaption, textSource, imageSource,
    canPublish: selectedAccountIds.length > 0, schedule, brandKit: composeSourcesEnabled,
  });
  const { fullyManual, noBrandBlocksGeneration } = submissionDecision;
  const composeCtaKey = submissionDecision.ctaKey;
  const generateButton = (
    <button
      className="btn btn-primary cp-generate"
      onClick={() => handleGenerate()}
      disabled={manual ? (manual.disabled || manual.saved || !manual.canSave || (!manual.recovering && (videoFormatBlocked || (imageSource === 'my' && pickedMedia.length === 0)))) : (submitting || generationLocked || videoFormatBlocked || noBrandBlocksGeneration)}
      aria-describedby={noBrandBlocksGeneration ? 'no-brand-guard-hint-create' : undefined}
      title={noBrandBlocksGeneration ? t('noBrandGuard.createBody') : undefined}
      type="button"
    >
      {fullyManual && !submitting && !generationLocked ? <I.calendar size={14} /> : <I.sparkles size={14} />}
      {manual ? t(manual.busy ? 'freePublishing.savingDraft' : manual.recovering ? 'freePublishing.recoverDraft' : 'freePublishing.saveDraft') : submitting
        ? t('createPost.starting')
        : generationLocked
          ? t('createPost.generationInProgressShort')
          : composeCtaKey
            ? t(composeCtaKey)
            : t('createPost.generatePosts', { posts: t('createPost.postCount', { count }) })}
      {!submitting && !generationLocked && <span className="kbd" style={{ marginLeft: 'auto' }}>⏎</span>}
    </button>
  );

  return (
    <div className="cp-compose" data-publishing-mode={manual ? 'free' : undefined}>
      <div className="cp-main">
        {/* Prompt / Caption block */}
        <section className="card cp-card">
          <header className={`cp-card-h ${(composeSourcesEnabled || aiTextDisabled) ? 'cp-card-h-row' : ''}`}>
            <div>
              <div className="eyebrow"><span>{t(manual ? 'freePublishing.newPost' : 'createPost.whatShouldWeGenerate')}</span></div>
              <h3>{t(manual ? 'freePublishing.caption' : 'createPost.describeBatchHeading')}</h3>
            </div>
            {/* Keep the upgrade action beside the source control, including when
                they wrap. Flag-off accounts still retain their upgrade action. */}
            {(composeSourcesEnabled || aiTextDisabled) && (
              <div className="cp-caption-source-controls">
                {composeSourcesEnabled && (
                  <SourceSeg
                    value={textSource}
                    onChange={setTextSource}
                    ariaLabel={t('createPost.textSource')}
                    options={[
                      { v: 'ai', l: <>{t('createPost.srcAiWrites')} {aiTextDisabled && <span className="dv-hook-badge">{t('planLock.tag')}</span>}</>, disabled: aiTextDisabled, title: aiTextDisabled ? t('textLocked.lockedLabel') : undefined },
                      { v: 'write', l: t('createPost.srcIWrite') },
                    ]}
                  />
                )}
                {aiTextDisabled && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm cp-upgrade"
                    onClick={() => { track('ai_text_upgrade_click', { surface: 'create_post' }); openUpgrade({ feature: 'default' }); }}
                  >
                    {t('assistant.result.upgrade')}
                  </button>
                )}
              </div>
            )}
          </header>
          {/* The AI topic box is HIDDEN in write mode (Brand Kit on) — only the
              caption editor below shows, so there's no duplicate box. With Brand
              Kit ON the legacy paperclip/drag-attach is also removed (references
              now flow through the Visual block rail); flag-OFF keeps it. */}
          {!(composeSourcesEnabled && textSource === 'write') && (
            <div
              className={`cp-prompt ${dragging ? 'is-dragging' : ''}`}
              onDragEnter={composeSourcesEnabled ? undefined : handleDragEnter}
              onDragLeave={composeSourcesEnabled ? undefined : handleDragLeave}
              onDragOver={composeSourcesEnabled ? undefined : handleDragOver}
              onDrop={composeSourcesEnabled ? undefined : handleDrop}
            >
              <textarea
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('createPost.promptPlaceholder')}
              />
              {dragging && !composeSourcesEnabled && (
                <div
                  style={{
                    position: 'absolute', inset: 0, border: '2px dashed var(--accent)',
                    borderRadius: 'var(--r-md)', background: 'var(--accent-soft)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', color: 'var(--accent-text)',
                    fontSize: 14, fontWeight: 500, pointerEvents: 'none', zIndex: 5,
                  }}
                >
                  {t('createPost.dropToAttach')}
                </div>
              )}
              {!composeSourcesEnabled && (
                <div className="cp-prompt-foot">
                  <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx,.txt" style={{ display: 'none' }} onChange={handleFileSelect} />
                  <button className="cp-tool" title={t('createPost.attachTooltip')} type="button" onClick={() => fileInputRef.current?.click()}>
                    <I.paperclip size={14} />
                  </button>
                </div>
              )}
            </div>
          )}

          {imageSource === 'ai' && !useBrand && (
            <div className="cp-brandoff-note">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 3l18 18M10.5 6.1A8 8 0 0 1 20 14M6.1 10.5A8 8 0 0 0 12 22" /></svg>
              <span>{t('createPost.brandOffBasisNote')}</span>
            </div>
          )}

          {textSource === 'write' && (
            <CaptionEditor
              value={providedCaption}
              onChange={setProvidedCaption}
              platforms={selectedPlatforms}
              planHashtags={planHashtags}
              captionAssists={entitlements?.usage?.caption_assists_monthly || null}
              onAssistUsed={() => invalidateResource?.('entitlements')}
              aiDisabled={aiTextDisabled}
              id={manual ? 'own-caption' : undefined}
              disabled={Boolean(manual?.disabled)}
              maxLength={manual ? 10000 : 4000}
              showPlanHashtags={!manual}
              t={t}
            />
          )}

          {attachments.length > 0 && imageSource === 'ai' && (
            <div className="cp-attachments" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
              {attachments.map((a) => (
                <span
                  key={a.id}
                  className="cp-sugg-pill"
                  title={`${a.name}${a.error ? ` — ${a.error}` : a.uploading ? ` — ${t('createPost.uploading')}` : ''}`}
                  style={{
                    maxWidth: '100%',
                    background: a.error ? 'var(--danger-soft)'
                      : a.uploading ? 'var(--bg-subtle)'
                      : 'var(--success-soft)',
                    color: a.error ? 'var(--danger-text)'
                      : a.uploading ? 'var(--text-muted)'
                      : 'var(--success-text)',
                  }}
                >
                  <I.paperclip size={11} style={{ flex: '0 0 auto' }} />
                  <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {shortenFileName(a.name)}
                  </span>
                  {a.uploading && <span style={{ marginLeft: 4 }}>…</span>}
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    style={{ marginLeft: 4, background: 'transparent', border: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                    aria-label={t('createPost.removeAttachment')}
                  >
                    <I.x size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {attachments.filter((a) => !a.error && !a.uploading).length >= 2 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                marginTop: 4,
                padding: '9px 11px',
                background: 'var(--accent-soft)',
                border: '1px solid var(--border-2)',
                borderRadius: 8,
                fontSize: 12.5,
                color: 'var(--accent)',
                lineHeight: 1.45,
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1 }}>{I.sparkles?.({ size: 14 })}</span>
              <span>{t('createPost.compositionNotice', { max: MAX_REFERENCE_IMAGES })}</span>
            </div>
          )}

          {textSource !== 'write' && visibleSuggestions.length > 0 && (
            <div className="cp-sugg">
              <div className="cp-sugg-l">
                <span className="cp-sugg-lt">{topicsLabel}</span>
                <span className="cp-sugg-sp" />
                {canShuffle && (
                  <button type="button" className="cp-sugg-shuffle" onClick={() => setSuggOffset((o) => (o + SUGG_WINDOW) % Math.max(1, suggestionItems.length))}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>
                    {t('createPost.shuffle')}
                  </button>
                )}
              </div>
              <div className="cp-sugg-cards">
                {visibleSuggestions.map((s, i) => {
                  const col = pillarColor(s.pillar);
                  return (
                    <button key={`${suggOffset}-${i}`} type="button" className="cp-sugg-card" onClick={() => setPrompt(s.prompt || s.title)} title={s.prompt || s.title}>
                      {s.pillar && <span className="cp-sugg-ptag" title={s.pillar} style={{ color: col, background: `color-mix(in srgb, ${col} 18%, transparent)` }}>{s.pillar}</span>}
                      <span className="cp-sugg-body"><span className="cp-sugg-ti">{s.title}</span>{s.angle && <span className="cp-sugg-an">{s.angle}</span>}</span>
                      <span className="cp-sugg-use"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7" /></svg></span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Visual block — Brand-Kit composition (flag-gated). With the flag OFF
            this whole section is absent and the existing visual-style picker
            stays in the sidebar, so generation behaves exactly as before. */}
        {composeSourcesEnabled && (
          <section className="card cp-card">
            <header className={`cp-card-h ${postFormat !== 'reel' ? 'cp-card-h-row' : ''}`}>
              <div>
                <div className="eyebrow"><span>{t('createPost.visualEyebrow')}</span></div>
                <h3>{t('createPost.visualHeading')}</h3>
              </div>
              {/* Unified source picker — the SAME control for every format (owner
                  UX 2026-07-09): AI media (generate) / My media (use existing) /
                  No media (text-only, Image only). For Video: My media = pick a
                  ready clip; AI media = generate (describe/photos sub-toggle in
                  the body). No more "picker disappears when you choose Video". */}
              <SourceSeg
                value={imageSource}
                onChange={(v) => {
                  setImageSource(v);
                  if (v === 'my') {
                    // Open the media/clip picker straight away when nothing's chosen.
                    if (postFormat === 'reel') {
                      if (!pickedMedia.some((m) => isVideoAsset(m))) { setPickerMode('media'); setPickerOpen(true); }
                    } else if (pickedMedia.length === 0) { setPickerMode('media'); setPickerOpen(true); }
                  }
                }}
                ariaLabel={t('createPost.imageSource')}
                options={!manual && postFormat === 'reel' && videoPublishV2 && !showReel
                  ? [{ v: 'my', l: t('media.srcMyMedia', { defaultValue: 'My media' }) }]
                  : postFormat === 'image'
                  ? [{ v: 'ai', l: <>{t('imageLocked.aiMedia')} {imageLockBadge && <span className="dv-hook-badge">{t('planLock.tag')}</span>}</>, disabled: aiImageDisabled, title: imageLockTitle }, { v: 'my', l: t('media.srcMyMedia', { defaultValue: 'My media' }) }, { v: 'none', l: t('media.srcNoMedia', { defaultValue: 'No media' }) }]
                  : [{ v: 'ai', l: <>{t('imageLocked.aiMedia')} {imageLockBadge && <span className="dv-hook-badge">{t('planLock.tag')}</span>}</>, disabled: aiImageDisabled, title: imageLockTitle }, { v: 'my', l: t('media.srcMyMedia', { defaultValue: 'My media' }) }]}
              />
            </header>

            {/* vpost (A) — top-level format-first selector: Image/Carousel |
                Video | Slides (muted), showReel-gated. DELIBERATE DEVIATIONS
                from the pack: (1) non-admin users (showReel false) see NO
                format row at all — the card renders exactly as it did before
                this port, byte-identical; (2) Slides is a muted third segment,
                not the pack's question-link (owner UX call 2026-07-07 — see
                TOP_FORMATS). */}
            {/* Video OFF 2026-07-10: the format row now shows for everyone (it
                carries the kept Slides feature) — the Video button itself is
                showReel-gated below, so with video dark the row = Image | Slides. */}
            {composeSourcesEnabled && (
              /* .dv-formatwrap for the card-scoped 10px separation below the
                 selector row (mg-ctl itself carries no external margins). */
              <div className="dv-formatwrap">
              <div className="mg-ctl">
                <div className="mg-ml"><span className="mg-t">{t('media.postFormat', { defaultValue: 'Post format' })}</span></div>
                <div
                  className="dv-format"
                  role="radiogroup"
                  aria-label={t('media.postFormat', { defaultValue: 'Post format' })}
                  onKeyDown={topFormatKeyNav}
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={postFormat === 'image'}
                    data-format="image"
                    tabIndex={postFormat === 'image' ? 0 : -1}
                    className={postFormat === 'image' ? 'on' : ''}
                    disabled={Boolean(manual && (manual.disabled || hasPickedVideo))}
                    onClick={setTopFormatImage}
                  >
                    <I.image size={13} /> {t('media.formatImageCarousel', { defaultValue: 'Image / Carousel' })}
                  </button>
                  {showVideoFormat && (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={postFormat === 'reel'}
                    data-format="reel"
                    tabIndex={postFormat === 'reel' ? 0 : -1}
                    className={postFormat === 'reel' ? 'on' : ''}
                    disabled={Boolean(manual && (manual.disabled || (pickedMedia.length && !hasPickedVideo)))}
                    onClick={setTopFormatVideo}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                    {t('media.formatVideo', { defaultValue: 'Video' })} <span className="dv-hook-badge">9:16</span>
                  </button>
                  )}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={postFormat === 'slides'}
                    data-format="slides"
                    tabIndex={postFormat === 'slides' ? 0 : -1}
                    className={`dv-fmt-quiet ${postFormat === 'slides' ? 'on' : ''}`}
                    disabled={Boolean(manual)}
                    title={manual ? t('planLock.tag') : undefined}
                    onClick={setTopFormatSlides}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4h11v13H8zM5 7v13h11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
                    {t('media.formatSlidesShort', { defaultValue: 'Slides' })} <span className="dv-hook-badge">{t('media.formatSlidesBadge')}</span>
                  </button>
                </div>
                <div className="mg-refhelp">
                  {postFormat === 'slides'
                    ? t('media.formatSlidesSub')
                    : (showVideoFormat && postFormat === 'reel')
                      ? (videoPublishV2 && !showReel
                        ? t('createPost.tiktokCompliance.ownVideoHint')
                        : t('media.formatVideoSub', { defaultValue: 'Video is an MP4 with voice narration.' }))
                      : null}
                </div>
              </div>
              </div>
            )}

            {/* postFormat==='image' (B) — byte-preserving Image/Carousel body. */}
            {postFormat === 'image' && (
              <>
                {imageSource === 'ai' && (
                  <>
                    {showReel && (
                      /* .dv-formatwrap = the card-scoped 10px separation the dest
                         chips had before the format selector moved to card level. */
                      <div className="dv-formatwrap">
                        <div className="dv-dest">
                          <span className="dv-dest-lbl">{t('media.publishesTo', { defaultValue: 'Publishes to' })}</span>
                          {destChips.empty
                            ? <span className="dv-dest-hint">{t('media.destNoAccount', { defaultValue: 'Connect an account to publish' })}</span>
                            : destChips.chips.map((c, i) => (
                              <span key={c.id} className={`dv-dest-chip ${i === 0 ? 'is-primary' : ''}`}>{c.label}</span>
                            ))}
                        </div>
                      </div>
                    )}
                    {/* GATEWAY — the brand decision, first under the image-source
                        choice (npform redesign): "match your brand, or generic?".
                        It governs whether the brand controls below matter at all. */}
                    <div className={`nvbk-ms ${useBrand ? '' : 'is-off'}${visualBrandContextAvailable ? '' : ' is-unavailable'}`}>
                      <span className="nvbk-ms-ic"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" />{useBrand && <path d="m9 12 2 2 4-4" />}{!useBrand && <path d="M4 4l16 16" />}</svg></span>
                      <span className="nvbk-ms-txt"><span className="nvbk-ms-n">{t('createPost.gatewayLabel')}</span><span id="create-post-brand-unavailable" className="nvbk-ms-h">{t(visualBrandContextAvailable ? 'createPost.gatewaySub' : 'createPost.brandStylingUnavailable')}</span>{!visualBrandContextAvailable && <Link className="nvbk-ms-add" to="/onboarding">{t('createPost.addBrand')}</Link>}</span>
                      <button type="button" role="switch" aria-checked={useBrand} aria-label={t('createPost.gatewayLabel')} aria-describedby={!visualBrandContextAvailable ? 'create-post-brand-unavailable' : undefined} disabled={!visualBrandContextAvailable} className={`nvbk-ms-toggle ${useBrand ? 'on' : 'off'}`} onClick={() => setUseBrand(!useBrand)}>
                        <span className="nvbk-ms-rail on-rail">{t('createPost.toggleOn')}</span>
                        <span className="nvbk-ms-rail off-rail">{t('createPost.toggleOff')}</span>
                        <span className="nvbk-ms-knob">
                          <span className="nvbk-shield-on"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="m9 12 2 2 4-4" /></svg></span>
                          <span className="nvbk-shield-off"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="M4 4l16 16" /></svg></span>
                        </span>
                      </button>
                    </div>
                      <div className="nvbk-brandgroup">
                        <div className="nvbk-bg-cap">
                          <span className="nvbk-bg-t">{t(useBrand ? 'createPost.brandGroupCap' : 'createPost.referenceImages')}</span>
                          <span className="nvbk-bg-sp" />
                          <span className="nvbk-bg-batch"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg><b>{count}</b> {count === 1 ? t('createPost.postWordOne') : t('createPost.postsWord')}</span>
                        </div>
                        <div className="nvbk-bg-inner">
                          <BrandRefRail
                            mode="guide"
                            titleKey={useBrand ? 'createPost.brandReferences' : 'createPost.referenceImages'}
                            logo={useBrand ? primaryLogo : null}
                            logoExcluded={logoExcluded}
                            justPulsed={logoPulse}
                            onToggleLogo={() => setLogoExcluded((prev) => {
                              const next = !prev;
                              if (!next) { setLogoPulse(true); setTimeout(() => setLogoPulse(false), 650); }
                              return next;
                            })}
                            items={pickedRefs}
                            onRemove={(id) => setPickedRefs((prev) => prev.filter((x) => x.id !== id))}
                            onAdd={() => { setPickerMode('guide'); setPickerOpen(true); }}
                            brandInitial={(brand?.businessName || brand?.name || 'B').charAt(0).toUpperCase()}
                            hasBrandContext={useBrand && meaningfulBrandContext}
                            t={t}
                          />
                          {useBrand && <><div className="nvbk-div" />
                          <VisualControls
                            postCount={count}
                            selected={visualSet}
                            setSelected={setVisualSet}
                            colorsOn={useBrandColors}
                            setColorsOn={setUseBrandColors}
                            brandColors={brand?.brandColors}
                            typography={brandTypographyEnabled() && brand?.typography?.defaultAssetId ? brand.typography : null}
                            typographyOn={useTypography === true}
                            setTypographyOn={setUseTypography}
                          /></>}
                        </div>
                      </div>
                    {!useBrand && visualBrandContextAvailable && (
                      <div className="nvbk-gateoff">
                        <span className="nvbk-gateoff-ic"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l18 18M10.5 6.1A8 8 0 0 1 20 14M6.1 10.5A8 8 0 0 0 12 22" /></svg></span>
                        <span className="nvbk-gateoff-tx">{t('createPost.brandOffOutcome')} {dash.setupStatus?.brand === 'present' && !needsBrandContext && <span className="nvbk-keep"><span className="nvbk-keep-ic">{VB_CHECK_SVG}</span>{t('createPost.brandOffKeep')}</span>}</span>
                      </div>
                    )}
                  </>
                )}
                {imageSource === 'my' && (
                  <>
                    {showReel && (
                      /* Same card-scoped separation as the AI branch above. */
                      <div className="dv-formatwrap">
                        <div className="dv-dest">
                          <span className="dv-dest-lbl">{t('media.publishesTo', { defaultValue: 'Publishes to' })}</span>
                          {destChips.empty
                            ? <span className="dv-dest-hint">{t('media.destNoAccount', { defaultValue: 'Connect an account to publish' })}</span>
                            : destChips.chips.map((c, i) => (
                              <span key={c.id} className={`dv-dest-chip ${i === 0 ? 'is-primary' : ''}`}>{c.label}</span>
                            ))}
                        </div>
                      </div>
                    )}
                    {/* TASK-321: the old "AI images not included…" explainer
                        (with its own copy of this button) is gone — this is
                        now the primary action of the branch the user is
                        already in, not an explanation of why another branch
                        is locked. Same key/handler as before. */}
                    {!manual && imageAllowanceZero && pickedMedia.length === 0 && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => { setPickerMode('media'); setPickerOpen(true); }}
                      >
                        {t('imageLocked.upload')}
                      </button>
                    )}
                    <BrandRefRail
                      mode="media"
                      items={pickedMedia}
                      disabled={Boolean(manual?.disabled)}
                      onRemove={(id) => setPickedMedia((prev) => prev.filter((x) => x.id !== id))}
                      onReorder={(next) => setPickedMedia(next)}
                      onAdd={() => { setPickerMode('media'); setPickerOpen(true); }}
                      isVideo={false}
                      t={t}
                    />
                  </>
                )}
                {imageSource === 'none' && (
                  <div className="cp-noimg">
                    <I.image size={14} /> {t('createPost.noImageNote')}
                    {/* Fix list item 7: zero-allowance accounts land here by
                        default (the 'ai'->'none' auto-switch above) and the
                        upload entry point otherwise only exists inside the
                        'my' branch — without this, a Scheduler user sees no
                        way at all to add their own photo. Judge decision:
                        don't auto-switch to 'my' (that auto-opens the
                        picker); offer one quiet button instead. */}
                    {imageAllowanceZero && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => { setImageSource('my'); setPickerMode('media'); setPickerOpen(true); }}
                      >
                        {t('imageLocked.upload')}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* postFormat==='slides' (C) — today's slides flow, demoted entry.
                SourceSeg above already hides 'none' for this format. */}
            {postFormat === 'slides' && (
              <>
                {imageSource === 'ai' && (
                  <>
                    <div className="cp-field">
                      <label className="cp-label">{t('media.slidesCount', { defaultValue: 'Slides' })}</label>
                      <div className="cp-stepper">
                        <button onClick={() => setSlideCount((c) => Math.max(SLIDES_MIN, c - 1))} type="button">−</button>
                        <div className="cp-stepper-v">
                          <span className="cp-stepper-num">{slideCount}</span>
                          <span className="cp-stepper-unit">{t('media.slidesUnit', { defaultValue: 'slides' })}</span>
                        </div>
                        <button onClick={() => setSlideCount((c) => Math.min(SLIDES_MAX, c + 1))} type="button">+</button>
                      </div>
                    </div>
                    <div className="mg-refhelp">
                      <I.info size={13} />
                      <span>{t('media.slidesAiHint', { n: slideCount })}</span>
                    </div>
                    {/* Quota degradation is silent server-side (the post gets
                        a single image instead) — surface it up front. */}
                    {slidesQuotaOut && (
                      <div className="mg-refhelp">
                        <I.info size={13} />
                        <span>{t('media.slidesQuotaOut', { defaultValue: 'Monthly slide limit reached — this post will get a single image instead.' })}</span>
                      </div>
                    )}
                  </>
                )}
                {imageSource === 'my' && (
                  <>
                    <BrandRefRail
                      mode="media"
                      items={pickedMedia}
                      disabled={Boolean(manual?.disabled)}
                      onRemove={(id) => setPickedMedia((prev) => prev.filter((x) => x.id !== id))}
                      onReorder={(next) => setPickedMedia(next)}
                      onAdd={() => { setPickerMode('media'); setPickerOpen(true); }}
                      isVideo={false}
                      t={t}
                    />
                    {/* §7 C4 — slides mode: cover hint (always) + 1-image nudge (only at
                        exactly 1; 0-images stays covered by the existing generic
                        "My image needs media" guard, so no second empty-state here). */}
                    {pickedMedia.length > 0 && (
                      <div className="mg-refhelp">
                        <I.info size={13} />
                        <span>{t('media.slidesCoverHint', { defaultValue: 'The first image becomes the cover — drag to reorder.' })}</span>
                      </div>
                    )}
                    {pickedMedia.length === 1 && (
                      <div className="mg-refhelp">
                        <I.info size={13} />
                        <span>{t('media.slidesBetterWithMore', { defaultValue: 'Slides work best with 2 or more images.' })}</span>
                      </div>
                    )}
                  </>
                )}
                {/* Shared once — dest (plain "TikTok" chip, no draft promise —
                    the compliance card below is the single source of truth for
                    direct-vs-draft) + ttmusic explainer, regardless of AI/My
                    source (switching back happens in the format segments
                    above). .dv-formatwrap = the pack's own slides-body wrapper
                    (vpost-01); it owns the 12px stack gap + the card's 10px
                    bottom margin. */}
                <div className="dv-formatwrap">
                <div className="dv-dest">
                  <span className="dv-dest-lbl">{t('media.publishesTo', { defaultValue: 'Publishes to' })}</span>
                  <span className="dv-dest-chip is-primary">TikTok</span>
                </div>
                <div className="dv-ttmusic">
                  <span className="dv-ttmusic-ic"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg></span>
                  <span className="dv-ttmusic-tx">
                    <span className="dv-ttmusic-t">{t('media.ttmusicTitle', { defaultValue: 'Music is added in the TikTok app' })}</span>
                    <span className="dv-ttmusic-s">{t('media.ttmusicSub')}</span>
                  </span>
                </div>
                </div>
              </>
            )}

            {/* postFormat==='reel' (D) — the new unified Video composer, ported
                from vpost-01/02/03/04. All new JSX; showReel-gated (non-admin
                never reaches this branch — postFormat can't become 'reel'). */}
            {showVideoFormat && postFormat === 'reel' && (
              /* .dv-formatwrap owns the column rhythm here — the Media form gets
                 it from .bk-gen's own flex gap, but .cp-card has no such gap and
                 bare children would glue together (owner-reported). */
              <div className="dv-formatwrap">
                {/* Unified source drives Video: My media → the ready-clip picker
                    below (videoSrc='clip', synced from imageSource); AI media →
                    this "Build from" sub-toggle chooses how to generate. */}
                {imageSource === 'ai' && (
                  <div className="mg-ctl">
                    <div className="mg-ml"><span className="mg-t">{t('media.videoBuildFrom', { defaultValue: 'Build from' })}</span></div>
                    <div className="dv-src" role="radiogroup" aria-label={t('media.videoBuildFrom', { defaultValue: 'Build from' })}>
                      <button type="button" role="radio" aria-checked={videoSrc === 'describe'} className={videoSrc === 'describe' ? 'on' : ''} onClick={() => setVideoSrc('describe')}>
                        {t('media.srcDescribe', { defaultValue: 'A description' })}
                      </button>
                      <button type="button" role="radio" aria-checked={videoSrc === 'photos'} className={videoSrc === 'photos' ? 'on' : ''} onClick={() => setVideoSrc('photos')}>
                        {t('media.srcMyPhotos', { defaultValue: 'My photos' })}
                      </button>
                    </div>
                  </div>
                )}

                {videoSrc === 'describe' && (
                  <>
                    {/* Quality — Tier-2 cinematic (fal.ai/Kling) vs Standard slideshow.
                        Admin pilot + dark client flag; VISIBLE (not buried in Options)
                        so it's a clear, primary choice. Server double-gates. */}
                    {videoCinematicEnabled() && (
                      <div className="mg-ctl">
                        <div className="mg-ml"><span className="mg-t">{t('media.quality', { defaultValue: 'Quality' })}</span></div>
                        <div className="bk-chips">
                          <button type="button" className={`bk-chip ${videoQuality === 'cinematic' ? 'on' : ''}`} aria-pressed={videoQuality === 'cinematic'} onClick={() => { videoQualityTouchedRef.current = true; setVideoQuality('cinematic'); }}>{t('media.qualityCinematic', { defaultValue: 'Cinematic' })}</button>
                          <button type="button" className={`bk-chip ${videoQuality === 'standard' ? 'on' : ''}`} aria-pressed={videoQuality === 'standard'} onClick={() => { videoQualityTouchedRef.current = true; setVideoQuality('standard'); }}>{t('media.qualityStandard', { defaultValue: 'Slideshow' })}</button>
                        </div>
                        <span className="dv-voicehint">{videoQuality === 'cinematic' ? t('media.qualityCinematicHint', { defaultValue: 'Cinematic motion — richer movement, takes a little longer to render.' }) : t('media.qualityStandardHint2', { defaultValue: 'Quick narrated video built from your images.' })}</span>
                        {/* Separate monthly quotas — cinematic + standard reels. */}
                        <span className="dv-quota">
                          <span className="dv-quota-lbl">{t('media.quotaLeftLabel', { defaultValue: 'Left this month:' })}</span>
                          <b className={videoQuality === 'cinematic' ? 'is-on' : ''}>{t('media.qualityCinematic', { defaultValue: 'Cinematic' })} {cinemaQuota.known ? (cinemaQuota.unlimited ? '∞' : `${Math.max(0, cinemaQuota.remaining)} of ${cinemaQuota.limit ?? 0}`) : '…'}</b>
                          <span className="dv-quota-sep">·</span>
                          <b className={videoQuality === 'standard' ? 'is-on' : ''}>{t('media.qualityStandard', { defaultValue: 'Slideshow' })} {stdQuota.known ? (stdQuota.unlimited ? '∞' : `${Math.max(0, stdQuota.remaining)} of ${stdQuota.limit ?? 0}`) : '…'}</b>
                          {videoQuality === 'cinematic' && cinemaQuota.known && !cinemaQuota.available && (
                            <Link to="/app/plan" className="dv-quota-up">{t('media.upgradeShort', { defaultValue: 'Upgrade' })} <I.arrowRight size={10} /></Link>
                          )}
                        </span>
                      </div>
                    )}
                    <div className="bk-prompt">
                      <textarea
                        value={videoPrompt}
                        onChange={(e) => setVideoPrompt(e.target.value)}
                        placeholder={t('media.describePlaceholder', { defaultValue: "What is the video about? e.g. 'Three tips for a stress-free office move'" })}
                        maxLength={500}
                      />
                    </div>
                    <div className="mg-refhelp">{t('media.describeHint', { defaultValue: 'AI writes the story, creates four scenes and narrates it — usually 20–30 seconds.' })}</div>
                    <div className={`nvbk-ms dv-bv ${videoBrandVoice ? '' : 'is-off'}`}>
                      <span className="nvbk-ms-ic"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" />{videoBrandVoice ? <path d="m9 12 2 2 4-4" /> : <path d="M4 4l16 16" />}</svg></span>
                      <span className="nvbk-ms-txt"><span className="nvbk-ms-n">{t('media.brandVoice', { defaultValue: 'Brand voice' })}</span><span className="nvbk-ms-h">{videoBrandVoice ? t('media.bvOn', { defaultValue: 'Uses your brand voice & content plan' }) : t('media.bvOff', { defaultValue: 'Clean creation — no brand context' })}</span></span>
                      <button type="button" role="switch" aria-checked={videoBrandVoice} aria-label={t('media.brandVoice', { defaultValue: 'Brand voice' })} className={`nvbk-ms-toggle ${videoBrandVoice ? 'on' : 'off'}`} onClick={() => setVideoBrandVoice((v) => !v)}>
                        <span className="nvbk-ms-rail on-rail">{t('createPost.toggleOn')}</span>
                        <span className="nvbk-ms-rail off-rail">{t('createPost.toggleOff')}</span>
                        <span className="nvbk-ms-knob">
                          <span className="nvbk-shield-on"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="m9 12 2 2 4-4" /></svg></span>
                          <span className="nvbk-shield-off"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="M4 4l16 16" /></svg></span>
                        </span>
                      </button>
                    </div>
                    <div className="nvbk-div" />
                    <div className="mg-ctl mg-refrow">
                      <div className="mg-ml">
                        <span className="mg-t">{t('media.describeRefs', { defaultValue: 'Reference photos' })}</span>
                        <span className="mg-opt">{t('brandkit.refOptional')}</span>
                      </div>
                      <div className="mg-ref-rail">
                        {videoDescribeRefs.map((r) => (
                          <span key={r.id} className="mg-ref" style={{ backgroundImage: `url(${r.url})` }}>
                            <button type="button" className="mg-ref-x" onClick={() => setVideoDescribeRefs((p) => p.filter((x) => x.id !== r.id))} aria-label={t('brandkit.removeRef')}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M6 6 18 18M18 6 6 18" /></svg>
                            </button>
                          </span>
                        ))}
                        {videoDescribeRefs.length < MAX_REFERENCE_IMAGES && (
                          <button type="button" className="mg-refadd" onClick={() => { setPickerMode('videoRefs'); setPickerOpen(true); }} aria-label={t('brandkit.addRef')}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
                            <span className="mg-refadd-cap">{t('brandkit.add')}</span>
                          </button>
                        )}
                      </div>
                      <div className="mg-refhelp">{t('media.describeRefHelp', { defaultValue: 'Throw in a product or face photo — the AI-generated scenes will feature it.' })}</div>
                    </div>
                  </>
                )}

                {videoSrc === 'photos' && (
                  <>
                    <div className="mg-ctl mg-refrow">
                      <div className="mg-ml">
                        <span className="mg-t">{t('media.videoFrames', { defaultValue: 'Scenes' })}</span>
                        <span className="mg-hint-r">{t('media.videoFramesHint', { defaultValue: `Pick 1–${MAX_REEL_FRAMES} images` })}</span>
                      </div>
                      <div className="mg-ref-rail">
                        {videoScenes.map((r, i) => (
                          <div
                            key={r.id}
                            className="mg-ref dv-frame"
                            style={{ backgroundImage: `url(${r.url})` }}
                            tabIndex={0}
                            role="option"
                            aria-label={t('media.frameAria', { defaultValue: `Frame ${i + 1} of ${videoScenes.length}. Arrow keys to reorder.`, n: i + 1, total: videoScenes.length })}
                            draggable
                            onDragStart={() => { videoFrameDragIdx.current = i; }}
                            onDragOver={(e) => { e.preventDefault(); if (videoFrameDragIdx.current != null && videoFrameDragIdx.current !== i) { moveVideoScene(videoFrameDragIdx.current, i); videoFrameDragIdx.current = i; } }}
                            onDragEnd={() => { videoFrameDragIdx.current = null; }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); moveVideoScene(i, i - 1); }
                              else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); moveVideoScene(i, i + 1); }
                              else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); setVideoScenes((p) => p.filter((x) => x.id !== r.id)); }
                            }}
                          >
                            <span className="ord">{i + 1}</span>
                            {i === 0 && <span className="opens">{t('media.videoOpens', { defaultValue: 'opens' })}</span>}
                            <button type="button" className="mg-ref-x" onClick={() => setVideoScenes((p) => p.filter((x) => x.id !== r.id))} aria-label={t('brandkit.removeRef')}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M6 6 18 18M18 6 6 18" /></svg>
                            </button>
                          </div>
                        ))}
                        {videoScenes.length < MAX_REEL_FRAMES && (
                          <button type="button" className="mg-refadd dv-frame" onClick={() => { setPickerMode('videoScenes'); setPickerOpen(true); }} aria-label={t('brandkit.addRef')}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
                            <span className="mg-refadd-cap">{t('brandkit.add')}</span>
                          </button>
                        )}
                      </div>
                      <div className="mg-refhelp">{t('media.videoFramesHelp', { defaultValue: 'Each image becomes a scene — plays in order, about 3–6 seconds each. Drag to reorder.' })}</div>
                    </div>
                    {videoScenes.length === 0 && (
                      <div className="bk-notice is-error">
                        <span>{t('media.videoNeedsFrames', { defaultValue: 'Pick at least one image to build the video from.' })}</span>
                      </div>
                    )}
                  </>
                )}

                {videoSrc === 'clip' && (() => {
                  const videoClip = pickedMedia.find((m) => isVideoAsset(m)) || null;
                  return videoClip ? (
                    <>
                      <div className="vb-body">
                        <div className="thumb-wrap thumb-wrap--media">
                          <span
                            className="thumb is-video"
                            style={(() => { const bg = resolvePosterUrl(videoClip); return bg ? { backgroundImage: `url("${bg}")` } : { backgroundColor: '#0f172a' }; })()}
                          >
                            <span className="ord">1</span>
                            <button type="button" className="x" aria-label={t('createPost.removeAttachment')} onClick={() => setPickedMedia([])}>
                              <svg width="9" height="9" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
                            </button>
                            <span className="dv-chip-play" aria-hidden="true" />
                            {formatDuration(videoClip.durationSeconds) && <span className="dv-chip-dur">{formatDuration(videoClip.durationSeconds)}</span>}
                          </span>
                          <span className="thumb-cap">{t('media.videoLabel', { defaultValue: 'Video' })}</span>
                        </div>
                      </div>
                      <div className="mg-refhelp">
                        {t(manual ? 'freePublishing.editorHelp' : 'media.clipFromMedia', { defaultValue: 'A clip you already generated in Media.' })}
                        {' '}
                        <a className="mg-confirm-a" style={{ marginLeft: 0 }} href="#0" onClick={(e) => { e.preventDefault(); setPickerMode('media'); setPickerOpen(true); }}>
                          {t('media.changeClip', { defaultValue: 'Change clip' })}
                          {' '}
                          <svg width="11" height="11" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </a>
                      </div>
                    </>
                  ) : (
                    <div className="cp-noimg">
                      <I.image size={14} />
                      <span>
                        {t('media.videoNeedsClip', { defaultValue: 'Pick a video from your Media library — or create one in Media first.' })}
                      </span>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setPickerMode('media'); setPickerOpen(true); }}>
                        {t('media.changeClip', { defaultValue: 'Change clip' })}
                      </button>
                    </div>
                  );
                })()}

                {/* Render lifecycle — inline generation states (submitting handled by
                    the CTA below; rendering/ready/failed render here). */}
                {showReel && videoPhase === 'rendering' && (
                  <>
                    <div className="vb-body">
                      <div className="thumb-wrap">
                        <span className="thumb is-rendering">
                          <span className="bk-shimmer" aria-hidden="true" />
                          <span className="bk-gspin" aria-hidden="true" />
                        </span>
                        <span className="thumb-cap">{t('media.renderingLabel', { defaultValue: 'Rendering' })}</span>
                      </div>
                    </div>
                    <div className="mg-refhelp">
                      {t('media.renderingInline', {
                        defaultValue: `Rendering — usually 2–5 minutes · ${Math.floor(videoElapsed / 60)}:${String(videoElapsed % 60).padStart(2, '0')} · safe to keep editing, it continues if you leave.`,
                        elapsed: `${Math.floor(videoElapsed / 60)}:${String(videoElapsed % 60).padStart(2, '0')}`,
                      })}
                    </div>
                  </>
                )}
                {showReel && videoPhase === 'ready' && (() => {
                  // Honesty: only claim "attached" when the attach actually
                  // happened — the list-fetch .catch / no-asset path lands here
                  // with nothing in pickedMedia.
                  const readyClip = pickedMedia.find((m) => isVideoAsset(m)) || null;
                  return (
                    <>
                      <div className="bk-notice is-success">
                        <svg width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M8.5 12.5l2.5 2.5 4.5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        {readyClip
                          ? <span><b>{t('media.videoReadyBold', { defaultValue: 'Your video is ready' })}</b> {t('media.videoAttachedSuffix', { defaultValue: '— attached to this post.' })}</span>
                          : <span>{t('media.videoReadyInMedia', { defaultValue: 'Your video is ready — find it in your Media library.' })}</span>}
                      </div>
                      {readyClip && (
                        <div className="vb-body">
                          <div className="thumb-wrap thumb-wrap--media">
                            <span
                              className="thumb is-video"
                              style={(() => { const bg = resolvePosterUrl(readyClip); return bg ? { backgroundImage: `url("${bg}")` } : { backgroundColor: '#0f172a' }; })()}
                            >
                              <span className="ord">1</span>
                              <span className="dv-chip-play" aria-hidden="true" />
                              {formatDuration(readyClip.durationSeconds) && <span className="dv-chip-dur">{formatDuration(readyClip.durationSeconds)}</span>}
                            </span>
                            <span className="thumb-cap">{t('media.videoLabel', { defaultValue: 'Video' })}</span>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                {showReel && videoPhase === 'failed' && (
                  <div className="bk-notice is-error">
                    <svg width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M12 8v5M12 16v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                    {/* videoError carries the refund note only when the op
                        actually failed (composed in the completion watch) —
                        cancelled/submit-error paths stay refund-claim-free. */}
                    <span>{videoError || t('media.videoRenderFailed', { defaultValue: 'Video render failed.' })}</span>
                    <span className="bk-notice-a"><button type="button" className="btn btn-ghost btn-sm" onClick={retryFailedVideoInline}>{t('media.tryAgain', { defaultValue: 'Try again' })}</button></span>
                  </div>
                )}
                {showReel && otherVideoRenderRunning && videoPhase === 'idle' && (
                  <div className="bk-notice">
                    <span>{t('media.oneRenderingFromMedia', { defaultValue: "A video is already rendering — one at a time. It's the one you started in Media." })}</span>
                  </div>
                )}
                {showReel && videoPhase === 'idle' && videoError && !otherVideoRenderRunning && (
                  <div className="bk-notice is-error">
                    <span>{videoError}</span>
                  </div>
                )}

                {/* Options (collapsed, describe+photos only — NOT clip). */}
                {videoSrc !== 'clip' && (
                  <>
                    <div className="nvbk-div" />
                    <button type="button" className="dv-opts-trigger" onClick={() => setVideoOptsOpen((v) => !v)} aria-expanded={videoOptsOpen}>
                      <span>{t('media.videoOptions', { defaultValue: 'Options' })}</span>
                      {/* Truthful closed-state summary (HANDBOOK §vpost) — hook AI/yours,
                          narration on/off with language, aspect. */}
                      <span className="sum">
                        {[
                          videoHook.trim() ? t('media.optsHookYours', { defaultValue: 'hook: yours' }) : t('media.optsHookAI', { defaultValue: 'hook: AI' }),
                          videoNarration
                            ? (() => {
                              // Reflect the EFFECTIVE language (per-video override wins over strategy default).
                              const effLang = videoLang || videoLanguage;
                              let langName = effLang;
                              if (effLang) { try { langName = new Intl.DisplayNames(['en'], { type: 'language' }).of(String(effLang).slice(0, 2)) || effLang; } catch (_) { /* keep raw code */ } }
                              return langName
                                ? t('media.optsVoiceoverLang', { defaultValue: `Voiceover (${langName})`, language: langName })
                                : t('media.optsVoiceover', { defaultValue: 'Voiceover' });
                            })()
                            : t('media.optsSilent', { defaultValue: 'Silent' }),
                          '9:16',
                        ].join(' · ')}
                      </span>
                      <svg className="dv-opts-cv" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
                    </button>
                    {videoOptsOpen && (
                      <div className="dv-opts">
                        <div className="dv-hook">
                          <div className="dv-hook-top">
                            <span className="dv-hook-t">{t('media.hookTitle', { defaultValue: 'Hook — the first 3 seconds' })}</span>
                            <span className="dv-hook-badge">{t('media.hookBadge', { defaultValue: '0–3s' })}</span>
                            {videoHook.trim() && (
                              <span className={`dv-hook-c ${videoHook.trim().split(/\s+/).length > 9 ? 'is-over' : ''}`} aria-live="polite">{videoHook.trim().split(/\s+/).length}/9</span>
                            )}
                          </div>
                          <input
                            type="text"
                            className="dv-hook-field"
                            value={videoHook}
                            maxLength={90}
                            onChange={(e) => setVideoHook(e.target.value)}
                            placeholder={videoNarration
                              ? t('media.hookPlaceholder', { defaultValue: 'Leave empty — AI writes a scroll-stopping hook' })
                              : t('media.hookPlaceholderSilent', { defaultValue: "Add a hook line — it's the only text on screen." })}
                          />
                          <span className="dv-hook-hint">{t('media.hookHint', { defaultValue: 'One bold line, max 9 words: a number, a mistake, a question — what makes them stop.' })}</span>
                        </div>
                        <div className="nvbk-div" />
                        <div className="mg-ctl">
                          <div className="mg-ml"><span className="mg-t">{t('media.narration', { defaultValue: 'Narration' })}</span></div>
                          <div className="bk-chips">
                            <button type="button" className={`bk-chip ${videoNarration ? 'on' : ''}`} aria-pressed={videoNarration} onClick={() => setVideoNarration(true)}>{t('media.narrationOn', { defaultValue: 'Voiceover' })}</button>
                            <button type="button" className={`bk-chip ${!videoNarration ? 'on' : ''}`} aria-pressed={!videoNarration} onClick={() => setVideoNarration(false)}>{t('media.narrationOff', { defaultValue: 'No voiceover' })}</button>
                          </div>
                          {videoNarration ? (
                            <div className="dv-narr">
                              {/* Per-video language picker — defaults to the strategy
                                  language, overridable for this render. */}
                              <div className="dv-lang">
                                <span className="dv-lang-l">{t('media.videoLanguage', { defaultValue: 'Voiceover language' })}</span>
                                <select
                                  className="dv-lang-sel"
                                  value={videoLang}
                                  onChange={(e) => setVideoLang(e.target.value)}
                                  aria-label={t('media.videoLanguage', { defaultValue: 'Voiceover language' })}
                                >
                                  <option value="">{videoDefaultLangName
                                    ? t('media.langFromStrategy', { defaultValue: `From your strategy · ${videoDefaultLangName}`, language: videoDefaultLangName })
                                    : t('media.langFromStrategyGeneric', { defaultValue: 'From your strategy' })}</option>
                                  {VIDEO_NARRATION_LANGS.map((code) => (
                                    <option key={code} value={code}>{videoLangAutonym(code)}</option>
                                  ))}
                                </select>
                                <span className="dv-lang-hint">{t('media.videoLanguageHint', { defaultValue: 'Defaults to your strategy language — change it just for this video.' })}</span>
                              </div>
                              <div className="dv-script">
                                <div className="mg-ml"><span className="mg-t">{t('media.scriptLabel', { defaultValue: 'Narration script' })}</span><span className="mg-hint-r">{t('media.scriptOptional', { defaultValue: 'Optional' })}</span></div>
                                <textarea
                                  value={videoScript}
                                  onChange={(e) => setVideoScript(e.target.value)}
                                  maxLength={500}
                                  placeholder={t('media.scriptPlaceholder', { defaultValue: 'Leave blank — AI writes it from your images and hook.' })}
                                  aria-label={t('media.scriptLabel', { defaultValue: 'Narration script' })}
                                />
                                <div className="dv-script-help">{t('media.scriptHelp', { defaultValue: 'One or two sentences per image is plenty.' })}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="dv-narr">
                              <span className="dv-voicehint">
                                {videoHook.trim()
                                  ? t('media.silentHint', { defaultValue: 'Silent video — your hook appears on screen.' })
                                  : t('media.silentHintNoHook', { defaultValue: 'Silent video — add a hook line so text appears on screen.' })}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="nvbk-div" />
                        <div className="dv-aspects">
                          <span className="dv-aspectlock">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="11" width="12" height="9" rx="2" /><path d="M9 11V8a3 3 0 0 1 6 0v3" /></svg>
                            9:16 · {t('media.vertical', { defaultValue: 'Vertical' })}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Publishes-to — derived, never re-hardcoded. Video goes to every
                    selected platform equally (same 9:16 clip) → no "primary" chip. */}
                <div className="dv-dest">
                  <span className="dv-dest-lbl">{t('media.publishesTo', { defaultValue: 'Publishes to' })}</span>
                  {destChips.empty
                    ? <span className="dv-dest-hint">{t('media.destNoAccount', { defaultValue: 'Connect an account to publish' })}</span>
                    : destChips.chips.map((c) => (
                      <span key={c.id} className="dv-dest-chip">{c.label}</span>
                    ))}
                </div>

                {/* Proactive disabled-CTA reason (BrandKitScreen ~1231 pattern) —
                    describe mode with <3 chars shows WHY Generate is off. */}
                {videoSrc === 'describe' && videoPhase === 'idle' && videoPrompt.trim().length < 3 && (
                  <div className="bk-notice is-error">
                    <span>{t('media.describeMin', { defaultValue: 'Describe the video in a few words first.' })}</span>
                  </div>
                )}

                {/* Footer — hidden entirely when videoSrc==='clip' (no generation there). */}
                {videoSrc !== 'clip' && (
                  <div className="bk-gen-foot">
                    {videoAllowance && (
                      <span className={`bk-meter ${(videoAllowance.limit != null && (videoAllowance.remaining ?? 0) <= 0) ? 'is-limit' : ''}`}>
                        {videoAllowance.limit != null && (
                          <span className="bk-meter-bar"><i style={{ width: `${Math.min(100, Math.round(((videoAllowance.used ?? 0) / Math.max(1, videoAllowance.limit)) * 100))}%` }} /></span>
                        )}
                        {videoAllowance.limit == null ? t('settings.unlimited') : t('media.videoQuota', { defaultValue: `${videoAllowance.used ?? 0}/${videoAllowance.limit} videos this month`, used: videoAllowance.used ?? 0, limit: videoAllowance.limit })}
                      </span>
                    )}
                    {videoPhase === 'ready' ? (
                      <button type="button" className="btn btn-ghost" onClick={resetVideoComposer}>{t('media.generateAnother', { defaultValue: 'Generate another' })}</button>
                    ) : (
                      <button type="button" className="btn btn-primary bk-cta" disabled={videoCtaDisabled} onClick={generateVideoInline} aria-describedby={noBrand ? 'no-brand-guard-hint-create' : undefined} title={noBrand ? t('noBrandGuard.disabledHint') : undefined}>
                        {videoPhase === 'submitting' ? (
                          <><span className="bk-gspin" aria-hidden="true" />{t('media.generatingVideo', { defaultValue: 'Generating video…' })}</>
                        ) : videoPhase === 'rendering' ? (
                          <><span className="bk-gspin" aria-hidden="true" />{t('media.renderingButton', { defaultValue: 'Rendering…' })}</>
                        ) : (
                          <><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>{t('media.generateVideo', { defaultValue: 'Generate video' })}</>
                        )}
                      </button>
                    )}
                  </div>
                )}

                {/* Proactive quota-at-limit explanation (BrandKitScreen ~1255
                    pattern — the click handler's copy can never fire on a
                    disabled CTA). Ready clip stays usable at limit — picking
                    costs nothing, so this only renders on the generating
                    sources. */}
                {showReel && videoSrc !== 'clip' && videoAtLimit && (
                  <div className="bk-notice is-error">
                    <span>{t('media.videoLimit', { defaultValue: 'Monthly video limit reached.' })}</span>
                    <span className="bk-notice-a"><button type="button" className="btn btn-ghost btn-sm" onClick={() => openUpgrade({ feature: 'default' })}>{t('media.upgradePlan', { defaultValue: 'Upgrade plan' })}</button></span>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Platforms */}
        <section
          className="card cp-card"
          ref={publishSectionRef}
          style={flashPublish ? { boxShadow: '0 0 0 2px var(--accent)', transition: 'box-shadow 0.25s ease' } : undefined}
        >
          <header className="cp-card-h">
            <div className="eyebrow"><span>{t('createPost.publishTo')}</span></div>
            <h3>{t('createPost.whichChannels')}</h3>
          </header>
          <div className="cp-plats">
            {/* Render every connected account as its own row so multi-account
                workspaces (e.g. two Telegrams) can target each one independently. */}
            {PLATFORM_OPTIONS.flatMap((p) => {
              {/* TikTok UNGATED 2026-07-20 (owner: "постинг досі заглушка"): the
                  worker is live (TIKTOK_PUBLISH_ENABLED=1 prod) and routes image
                  posts through the photo-deck DRAFT path (MEDIA_UPLOAD → the
                  user's TikTok inbox; direct-post stays behind the TikTok audit
                  flag). The old admin-only "Coming soon" tile hid a working
                  target from everyone once video GA was pulled. TikTok now falls
                  through to the normal connected-account row with an honest
                  draft hint; video stays blocked via VIDEO_UNSUPPORTED_CHANNELS. */}
              // V-R9 §7 C3 soft hint: in Video format, a selected platform that
              // doesn't take video quietly says it'll be skipped — never a hard
              // block (the server validates anyway).
              const softVideoHint = imageSource === 'my' && postFormat === 'reel' && PLATFORM_SUPPORTS_VIDEO[p.id] === false
                ? t('media.noVideoSupport', { defaultValue: "Doesn't support video — will skip" })
                : null;
              // "Upload your own video" hard-block — takes priority over the soft
              // hint above (a self-uploaded video isn't a reel-format soft skip,
              // it's simply not postable to this channel yet).
              const uploadedVideoBlocked = hasPickedVideo && ownVideoChannelUnsupported(p.id, isOwnVideoV2Lane);
              const videoHint = uploadedVideoBlocked
                ? t('media.videoChannelUnsupported', { defaultValue: "Video isn't supported on this channel yet" })
                : softVideoHint;
              const accs = accountsByPlatform.get(p.id) || [];
              if (accs.length === 0) {
                // No connected account for this network — but still SELECTABLE so
                // the user can generate a draft "as for" it. Picking it surfaces a
                // connect-to-publish notice below and the post saves as a draft
                // (forced in handleGenerate since there's no publish target).
                const on = selectedDraftPlatforms.has(p.id);
                return [(
                  <button
                    key={`${p.id}-draft`}
                    data-platform={p.id}
                    className={`cp-plat ${on ? 'is-on' : ''} ${uploadedVideoBlocked ? 'is-disabled' : ''}`}
                    type="button"
                    disabled={uploadedVideoBlocked || Boolean(manual?.disabled)}
                    onClick={() => { if (!uploadedVideoBlocked) toggleDraftPlatform(p.id); }}
                    title={uploadedVideoBlocked ? videoHint : (manual ? undefined : t('createPost.notConnectedTooltip'))}
                  >
                    <span
                      className={`ob-net-ic ob-net-ic-${p.id}`}
                      style={{ width: 28, height: 28 }}
                    >
                      {BrandIcons[p.id]?.({ size: 14 })}
                    </span>
                    <div className="cp-plat-text">
                      <div className="cp-plat-name">{p.name}</div>
                      <div className="cp-plat-sub">{t('createPost.notConnected')}</div>
                      {(uploadedVideoBlocked || (on && softVideoHint)) && <div className="cp-plat-sub is-warn">{videoHint}</div>}
                    </div>
                    <span className={`cp-plat-toggle ${on ? 'is-on' : ''}`}>
                      {on && <I.check size={11} />}
                    </span>
                  </button>
                )];
              }
              return accs.map((acc) => {
                const on = selectedAccounts.has(acc.id);
                const unavailable = uploadedVideoBlocked || Boolean(manual && (!manual.accountReady(acc) || manual.disabled));
                return (
                  <button
                    key={acc.id}
                    data-platform={p.id}
                    data-account-id={acc.id}
                    className={`cp-plat ${on ? 'is-on' : ''} ${uploadedVideoBlocked ? 'is-disabled' : ''}`}
                    onClick={() => { if (!unavailable) toggleAccount(acc.id); }}
                    type="button"
                    disabled={unavailable}
                    title={unavailable ? (videoHint || t('socialMetrics.unavailable')) : undefined}
                  >
                    <span
                      className={`ob-net-ic ob-net-ic-${p.id}`}
                      style={{ width: 28, height: 28 }}
                    >
                      {BrandIcons[p.id]?.({ size: 14 })}
                    </span>
                    <div className="cp-plat-text">
                      <div className="cp-plat-name">{p.name}</div>
                      <div className="cp-plat-sub" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{acc.displayName || acc.externalAccountId || t('createPost.connected')}</span>
                        {/* Language chip — sourced from per-account
                            publishLanguage set in Settings → Connected
                            accounts. Falls back to the org default so users
                            see which language THIS account will ship in
                            (without diving into settings). */}
                        {(acc.publishLanguage || acc.defaultPublishLanguage) && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '1px 6px',
                            fontSize: 10, fontFamily: 'var(--font-mono)',
                            letterSpacing: '0.04em', textTransform: 'uppercase',
                            background: 'var(--accent-soft)',
                            color: 'var(--accent)',
                            borderRadius: 4,
                            border: '1px solid var(--border-2)',
                          }}
                          title={acc.publishLanguage ? t('createPost.perAccountLanguage') : t('createPost.defaultLanguage')}
                          >
                            🌐 {String(acc.publishLanguage || acc.defaultPublishLanguage).toUpperCase()}
                          </span>
                        )}
                      </div>
                      {/* Blocker fix (2026-09-09): EVERY lane now routes a
                          selected TikTok target through the compliance card
                          below (see tikTokComplianceVisible) — there is no
                          more "plain image lane, no card, stays draft" path,
                          so this footnote must never promise a draft the
                          dispatcher won't send. It mirrors the card's actual
                          default: direct unless the tiktokDirectPostEnabled()
                          kill switch is off, in which case every post is
                          forced to the inbox draft path (the card hides the
                          direct-vs-draft picker entirely when the switch is
                          off). The card's own copy takes over once it's on
                          screen and reflects the user's live postMode choice. */}
                      {p.id === 'tiktok' && !manual && (
                        <div className="cp-plat-sub">{tiktokDirectPostEnabled()
                          ? t('createPost.tiktokCompliance.directPostNote')
                          : t('createPost.tiktokCompliance.draftInboxNote')}</div>
                      )}
                      {(uploadedVideoBlocked || (on && softVideoHint)) && <div className="cp-plat-sub is-warn">{videoHint}</div>}
                    </div>
                    <span className={`cp-plat-toggle ${on ? 'is-on' : ''}`}>
                      {on && <I.check size={11} />}
                    </span>
                  </button>
                );
              });
            })}
          </div>
          {connectedSocialAccounts.length === 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginTop: 10 }}>
              {t('createPost.noAccountsConnectedYet')} <Link to="/app/settings?tab=accounts">{t('createPost.connectOneInSettings')}</Link> {t('createPost.orSaveAsDraftsBelow')}
            </p>
          )}
          {selectedDraftPlatforms.size > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--accent)', marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              {I.info?.({ size: 13 })}
              <span>
                {t('createPost.draftPlatformNotice')}{' '}
                <Link
                  to="/app/settings?tab=accounts"
                  style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                >
                  {t('createPost.draftPlatformNoticeLink')}
                </Link>
              </span>
            </p>
          )}
          {/* V-R9 §7 C3 — Slides format publishes to TikTok only; one quiet hint
              under the card when it isn't selected yet (soft, not a hard block).
              Phase 4b: applies to BOTH slides sources ('my' stills + AI slides). */}
          {showReel && (imageSource === 'my' || imageSource === 'ai') && postFormat === 'slides' && !selectedPlatforms.includes('tiktok') && (
            <div className="mg-refhelp">
              <I.info size={13} />
              <span>{t('media.slidesNeedsTikTok', { defaultValue: 'Slides + music publishes to TikTok — select it above.' })}</span>
            </div>
          )}
        </section>

        {/* Volume + schedule */}
        <section className="card cp-card">
          <header className="cp-card-h">
            <div className="eyebrow"><span>{t('createPost.volumeAndSchedule')}</span></div>
            <h3>{t('createPost.howManyWhen')}</h3>
          </header>
          <div className="cp-grid-2">
            <div className="cp-field">
              <label className="cp-label">{t('createPost.numberOfPosts')}</label>
              {(isComposeMode || (showVideoFormat && postFormat === 'reel')) ? (
                // Compose AND every video reel are a single concrete post — locked, no stepper.
                <div className="cp-stepper cp-stepper--locked">
                  <div className="cp-stepper-v">
                    <span className="cp-stepper-num">1</span>
                    <span className="cp-stepper-unit">{t('createPost.postUnit', { count: 1 })}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="cp-stepper">
                    <button onClick={() => setCount((c) => Math.max(1, c - 5))} type="button">−</button>
                    <div className="cp-stepper-v">
                      <span className="cp-stepper-num">{count}</span>
                      <span className="cp-stepper-unit">{t('createPost.postUnit', { count })}</span>
                    </div>
                    <button onClick={() => setCount((c) => Math.min(maxCount, c + 5))} type="button">+</button>
                  </div>
                  <div className="cp-stepper-presets">
                    {[7, 14, 30].filter((n) => n <= maxCount).map((n) => (
                      <button key={n} className={count === n ? 'is-active' : ''} onClick={() => setCount(n)} type="button">
                        {n}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="cp-field">
              <label className="cp-label">{t('createPost.scheduleLabel')}</label>
              <DeviceTimezoneLabel />
              <div className="cp-radio-group">
                <button className={`cp-radio ${schedule === 'now' ? 'is-active' : ''}`} onClick={() => { setSchedule('now'); manual?.changeSchedule(''); }} type="button">
                  <span className="cp-radio-dot" />
                  <div>
                    <div className="cp-radio-l">{t(manual ? 'editPost.publishNow' : 'createPost.publishFirstImmediately')}</div>
                    <div className="cp-radio-s">{t(manual ? 'freePublishing.reviewPublish' : 'createPost.restSpreadsAutomatically')}</div>
                  </div>
                </button>
                <button className={`cp-radio ${schedule === 'spread' ? 'is-active' : ''}`} onClick={() => { setSchedule('spread'); if (manual) manual.changeSchedule(manual.scheduledAt); }} type="button">
                  <span className="cp-radio-dot" />
                  <div>
                    <div className="cp-radio-l">{t(manual ? 'freePublishing.schedule' : 'createPost.spreadStartingToday')}</div>
                    <div className="cp-radio-s">{t(manual ? 'freePublishing.scheduleLabel' : 'createPost.smartSlotDistribution')}</div>
                  </div>
                </button>
                <button className={`cp-radio ${schedule === 'draft' ? 'is-active' : ''}`} onClick={() => { setSchedule('draft'); manual?.changeSchedule(''); }} type="button">
                  <span className="cp-radio-dot" />
                  <div>
                    <div className="cp-radio-l">{t('createPost.saveAsDrafts')}</div>
                    <div className="cp-radio-s">{t('createPost.dontSchedulePickLater')}</div>
                  </div>
                </button>
              </div>
              {manual && schedule === 'spread' && <input className="cp-input" id="own-time" type="datetime-local" aria-label={t('freePublishing.scheduleLabel')} value={manual.scheduledAt} disabled={manual.disabled} onChange={event => manual.changeSchedule(event.target.value)} />}
            </div>
          </div>
        </section>

        {tikTokComplianceVisible && (
          <TikTokComplianceController
            accountId={selectedTikTokAccount.id}
            // 'video' for BOTH reel lanes (own-uploaded clip and the
            // AI-generated reel — imageSource doesn't matter, only the format
            // does); 'photo' for the plain Image/Carousel lane and Slides.
            // The AI-reel lane has no picked file yet at compose time — `video`
            // below naturally resolves to undefined for it, and the card
            // already hides the preview/duration bits whenever there's no
            // video (see TikTokComplianceController's `video?.url` / duration
            // checks), so no extra isPhoto-style branch is needed here.
            mediaKind={postFormat === 'reel' ? 'video' : 'photo'}
            video={pickedMedia.find((media) => isVideoAsset(media))}
            schedule={schedule}
            submitting={submitting}
            generationLocked={generationLocked}
            videoFormatBlocked={videoFormatBlocked}
            noBrandBlocked={noBrandBlocksGeneration}
            onSubmit={(options) => handleGenerate(options, true)}
          />
        )}
      </div>

      {/* Right column — brand summary + cost + CTA */}
      <aside className="cp-side">
        <section className="card cp-card cp-brand-card">
          <header className="cp-card-h">
            <div className="eyebrow"><span>{t('createPost.brandDefaults')}</span></div>
            <h3>{manual ? t('planLock.tag') : strategy && meaningfulBrandContext
              ? t('createPost.inferredFromOnboardingStrategy')
              : t(hasBrandDefaults ? 'createPost.savedBrandProfile' : 'createPost.brandDefaultsUnavailable')}</h3>
          </header>
          {manual ? <p className="cp-brand-empty"><LockKeyhole size={13} aria-hidden="true" /> {t('textLocked.lockedLabel')}</p> : hasBrandDefaults ? <ul className="cp-brand-list">
            <li>
              <span className="cp-brand-l">{t('createPost.audience')}</span>
              <span className="cp-brand-v">{brandDefaults.audience || '—'}</span>
            </li>
            <li>
              <span className="cp-brand-l">{t('createPost.tone')}</span>
              <span className="cp-brand-v">
                {Array.isArray(brandDefaults.tone)
                  ? (brandDefaults.tone.length ? brandDefaults.tone.join(' · ') : '—')
                  : (brandDefaults.tone || '—')}
              </span>
            </li>
            {/* Visual styles moved to the main-column Visual block when Brand
                Kit is on. Keep them in the sidebar ONLY for the flags-OFF form
                so existing behaviour is byte-identical. */}
            {!composeSourcesEnabled && imageSource === 'ai' && (
              <li>
                <span className="cp-brand-l">{t('createPost.visual')}</span>
                <span className="cp-brand-v cp-brand-v-vis">
                  <VisualPicker selected={visualSet} setSelected={setVisualSet} />
                </span>
              </li>
            )}
            {brandDefaults.goal && (
              <li>
                <span className="cp-brand-l">{t('createPost.goal')}</span>
                <span className="cp-brand-v">{brandDefaults.goal}</span>
              </li>
            )}
            <li>
              <span className="cp-brand-l">{t('createPost.languages')}</span>
              <span className="cp-brand-v">
                {Array.isArray(brandDefaults.languages) && brandDefaults.languages.length > 0
                  ? brandDefaults.languages.join(' · ').toUpperCase()
                  : '—'}
              </span>
            </li>
          </ul> : <p className="cp-brand-empty">{t('createPost.brandDefaultsMissing')}</p>}
          {/* "Edit brand defaults" goes straight to the strategy editor —
              that's where languages/tone/audience/goal/visual all live and
              get changed. The Plan overview page just READS them. */}
          {!manual && <Link className="cp-brand-edit" to={hasBrandDefaults ? '/app/plan/edit-strategy' : '/onboarding'}>
            {hasBrandDefaults ? <I.edit size={12} /> : <I.plus size={12} />} {t(hasBrandDefaults ? 'createPost.editBrandDefaults' : 'createPost.addBrand')}
          </Link>}
        </section>

        {/* TASK-320: absent (not just disabled) once BOTH allowances are
            confirmed zero — the level is inert on that account (still sent
            in the payload, but content-worker's zero-allowance compose path
            never reaches a paid call). Presentational only; see
            aiFullyUnavailable above for the full rationale. Fix list item
            8a: also require entitlements to have actually been fetched —
            otherwise aiFullyUnavailable reads as false (both allowances
            "unknown") for one tick on every load and the panel flashes in
            before disappearing on a Scheduler account. */}
        {generationLevelPickerVisible && Boolean(dash.fetchedAt?.entitlements) && !aiFullyUnavailable && (
          <section className="card cp-card cp-generation-level">
            <GenerationLevelPicker
              value={generationLevel}
              onChange={setGenerationLevel}
              operation="text"
              userId={user?.id}
            />
          </section>
        )}

        <section className="card cp-card cp-cost">
          {manual ? <>
            <FreePublishingAllowance capability={manual.allowance} />
            {manual.error && <p role="alert">{t(`freePublishing.${manual.error}`)}</p>}
            {!manual.error && (manual.notice || manual.saved) && <p role="status">{t(`freePublishing.${manual.notice || 'saved'}`)}</p>}
          </> : <>
          <div className="cp-cost-head">
            <span className="eyebrow"><span>{t('createPost.cost')}</span></span>
            {entitlements?.planName && (
              <span className="pill pill-success" style={{ height: 22 }}>
                <I.check size={10} /> {entitlements.planName}
              </span>
            )}
          </div>
          <div className="cp-cost-row">
            <span>
              {t('createPost.costPostsTimesAccounts', {
                posts: t('createPost.postCount', { count }),
                accounts: t('createPost.accountCount', { count: selectedAccounts.size }),
              })}
            </span>
            <span className="mono">{t('createPost.postCount', { count })}</span>
          </div>
          {remainingPosts != null && (
            <div className="cp-cost-row cp-cost-sub">
              <span>{t('createPost.postsRemainingThisMonth')}</span>
              <span className="mono">{remainingPosts}</span>
            </div>
          )}
          {exceedsPostLimit && !noBrand && (
            <>
              <div className="cp-cost-row cp-cost-sub" role="alert" style={{ color: 'var(--color-danger, #d9534f)', lineHeight: 1.4 }}>
                <span>
                  {remainingPostsNum <= 0
                    ? t('createPost.limitReachedWarning')
                    : t('createPost.limitExceedWarning', { count, remaining: remainingPostsNum })}
                </span>
              </div>
              {/* Upgrade CTA — the limit copy invites "upgrade for more", so give it a
                  real, clearly-styled action that lands on the Plan & billing tab. */}
              <div className="cp-cost-row cp-cost-sub" style={{ justifyContent: 'flex-start' }}>
                <Link
                  to="/app/settings?tab=billing"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--accent-text)', fontWeight: 600, textDecoration: 'none' }}
                >
                  <I.sparkles size={13} /> {t('createPost.upgradePlanCta')} <I.arrowRight size={12} />
                </Link>
              </div>
            </>
          )}
          {generationLocked && (
            <div className="cp-cost-row cp-cost-sub" role="status" style={{ lineHeight: 1.4 }}>
              <span>{t('createPost.generationLockedWarning')}</span>
              <Link to={`/app/batch/${activeBatch.id}`} className="mono" style={{ color: 'var(--accent-text)', whiteSpace: 'nowrap' }}>
                {t('createPost.viewActiveGeneration')}
              </Link>
            </div>
          )}
          {videoFormatBlocked && !generationLocked && (
            <div className="cp-cost-row cp-cost-sub" role="status">
              <span>{imageSource === 'ai'
                ? t('media.videoNeedsGenerate', { defaultValue: 'Generate your video first — then it attaches here.' })
                : t('media.videoNeedsClip', { defaultValue: 'Pick a video from your Media library — or create one in Media first.' })}</span>
            </div>
          )}
          </>}
          {needsBrandContext && <NoBrandGuard surface="create" compact />}
          {!tikTokComplianceVisible && generateButton}
          {manual && <>
            {manual.accounts.length > 0 && schedule !== 'draft' && <button className="btn btn-secondary cp-generate" type="button" disabled={manual.disabled || !manual.saved || !manual.accountId || (schedule === 'spread' && !manual.scheduledAt)} onClick={manual.prepareDispatch}>{t('freePublishing.reviewPublish')}</button>}
            {manual.saved && <Link className="cp-brand-edit" to={`/app/post/${encodeURIComponent(manual.raw.contentItem.id)}`}>{t('freePublishing.viewPost')}</Link>}
          </>}
        </section>
      </aside>
      <LibraryPicker
        open={pickerOpen}
        originalsForBrand={manual ? (manual.form.brandProfileId || '') : null}
        disabled={Boolean(manual && (manual.disabled || !manual.form.brandProfileId))}
        onUploadRequest={manual ? () => { setPickerOpen(false); openOriginalUpload(postFormat === 'reel'); } : null}
        onClose={() => setPickerOpen(false)}
        onConfirm={(chosen) => {
          if (pickerMode === 'guide') setPickedRefs(chosen);
          else if (pickerMode === 'videoScenes') setVideoScenes(chosen);
          else if (pickerMode === 'videoRefs') setVideoDescribeRefs(chosen);
          else {
            // A video post (reel OR an uploaded clip picked via My-media) carries
            // a SINGLE video asset — BC-3 routes contentType by asset shape when
            // exactly one video is present. The picker already enforces this AT
            // SELECTION TIME (singleClipMode for reel §7 C5; the general
            // includeVideos branch in toggle() above for "upload your own video"),
            // so this branch is now a defensive backstop (e.g. stale mixed
            // `initial` from a prior format) — if it would still drop items,
            // SAY SO via the existing trim-toast plumbing instead of silently
            // demoting.
            const vid = chosen.find((c) => isVideoAsset(c));
            if (vid && chosen.length > 1) {
              notify({ level: 'info', title: t('media.clipKept', { defaultValue: 'Kept the video — a video post uses exactly one clip.' }) });
            }
            setPickedMedia(vid ? [vid] : chosen);
          }
          setPickerOpen(false);
        }}
        max={pickerMode === 'guide' ? MAX_REFERENCE_IMAGES
          : pickerMode === 'videoScenes' ? MAX_REEL_FRAMES
          : pickerMode === 'videoRefs' ? MAX_REFERENCE_IMAGES
          : (showVideoFormat && postFormat === 'reel' ? 1 : mediaCap.max)}
        /* includeVideos is now independent of showReel — "upload your own
           video" surfaces uploaded (and, if reel ever comes back, generated)
           clips in the My-media picker. EXCEPT Slides: a slideshow is built
           from images (its cover/deck path can't take a clip), so the slides
           picker stays image-only. singleClipMode stays reel-only (untouched):
           the general video-exclusive rule lives in toggle() above. */
        includeVideos={pickerMode === 'media' && postFormat !== 'slides'}
        singleClipMode={pickerMode === 'media' && showVideoFormat && postFormat === 'reel'}
        allowVideoUpload={pickerMode === 'media' && isOwnVideoV2Lane}
        onUpgrade={(err) => openUpgrade({ feature: 'default', variant: err?.code === 'PLAN_LAPSED' ? 'renew' : 'upgrade' })}
        capHint={pickerMode === 'guide' || pickerMode === 'videoScenes' || pickerMode === 'videoRefs' || (showVideoFormat && postFormat === 'reel')
          ? null
          : (mediaCap.platform
              ? t('createPost.mediaCapHint', { n: mediaCap.max, platform: platformLabel(mediaCap.platform) })
              : t('createPost.mediaCapHintGeneric', { n: mediaCap.max }))}
        mode={pickerMode === 'videoScenes' || pickerMode === 'videoRefs' ? 'guide' : pickerMode}
        initial={pickerMode === 'guide' ? pickedRefs
          : pickerMode === 'videoScenes' ? videoScenes
          : pickerMode === 'videoRefs' ? videoDescribeRefs
          : pickedMedia}
      />
      {manual && <>
        <input ref={originalUploadRef} id="own-upload" type="file" hidden multiple={postFormat !== 'reel'} accept={postFormat === 'reel' ? 'video/mp4,video/quicktime' : 'image/jpeg,image/png,image/webp'} disabled={manual.disabled} onChange={event => manual.upload(event, uploadSelection.current)} />
        <ConfirmDialog open={Boolean(manual.confirmation)} title={t('freePublishing.reviewPublish')}
          description={<>{manual.confirmation?.label}<br />{manual.scheduledAt ? new Date(manual.scheduledAt).toLocaleString() : t('freePublishing.publishNow')}<p>{t('freePublishing.confirmPublish')}</p></>}
          confirmLabel={t(manual.scheduledAt ? 'freePublishing.schedule' : 'freePublishing.publishNow')} cancelLabel={t('common.cancel')}
          confirming={manual.busy} confirmDisabled={manual.disabled} onCancel={manual.cancelConfirmation} onConfirm={manual.dispatch} />
      </>}
    </div>
  );
}

/* ============================================================
   Done view
   ============================================================ */
function DoneView({ batch, onAgain }) {
  const { t } = useTranslation();
  const total = batch?.totalItems || batch?.total_items || batch?.count || '—';
  const batchId = batch?.id || batch?.batchId || null;
  return (
    <section className="cp-done">
      <div className="cp-done-bg" />
      <div className="cp-done-inner">
        <span className="cp-done-ic"><I.check size={22} /></span>
        <span className="pill pill-success" style={{ marginTop: 16 }}>
          <span className="dot" />{t('createPost.batchStarted', { posts: t('createPost.postCount', { count: total }) })}
        </span>
        <h2 className="cp-done-h">
          {t('createPost.doneYourPostsWillBe')}<br />
          <span className="cp-done-ac">{t('createPost.liveInTheLibrary')}</span>
        </h2>
        <p className="cp-done-p">
          {t('createPost.activityPillTracksProgress')}
        </p>
        {batch?.videoPublishV2TikTok && (
          <div className="bk-notice is-info cp-done-processing" role="status">
            <I.info size={14} /> {t('createPost.tiktokCompliance.processingNotice')}
          </div>
        )}
        <div className="cp-done-actions">
          {batchId ? (
            <Link className="btn btn-primary btn-lg" to={`/app/batch/${batchId}`}>
              <I.sparkles size={14} /> {t('createPost.watchBatchLive')}
            </Link>
          ) : (
            <Link className="btn btn-primary btn-lg" to="/app/library?filter=generating">
              <I.library size={14} /> {t('createPost.openLibrary')}
            </Link>
          )}
          <Link className="btn btn-ghost btn-lg" to="/app/library?filter=generating">
            <I.library size={14} /> {t('createPost.library')}
          </Link>
          <Link className="btn btn-ghost btn-lg" to="/app/calendar">
            <I.calendar size={14} /> {t('createPost.calendar')}
          </Link>
        </div>
        <button className="cp-done-again" onClick={onAgain} type="button">
          <I.refresh size={12} /> {t('createPost.generateAnotherBatch')}
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   Top-level CreatePost
   ============================================================ */
export const CreatePost = ({ manualDraftId, freePublishing = null, freeAccessAvailable = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('compose'); // compose | generating | done (dev-only)
  const [lastBatch, setLastBatch] = useState(null);
  const { activeBatches } = useContentFeedContext();
  const { setActivity } = useActivity();
  const researchHold = useResearchHold();
  const dash = useDashboardData() || {};
  const manual = useOwnDraftController({ id: manualDraftId, capability: freePublishing, enabled: Boolean(freePublishing), accessAvailable: freeAccessAvailable });
  const creationAllowed = Boolean(freePublishing) || creationAccessState(dash) === 'allowed';
  // Keep an in-progress form mounted during a status recheck so a transient
  // read failure cannot discard the user's draft. Account switches reset it.
  const composeScope = useRef({ accountId: dash.accountId, shown: false });
  if (composeScope.current.accountId !== dash.accountId) composeScope.current = { accountId: dash.accountId, shown: false };
  if (creationAllowed) composeScope.current.shown = true;
  const meaningfulBrandContext = dash.setupStatus?.brand === 'present'
    && hasCreateMeaningfulBrandContext(dash.setupStatus?.profile);

  // Dev-only state switcher gated by ?dev=1
  const showDevSwitch = !freePublishing && searchParams.get('dev') === '1';

  const activeBatch = useMemo(() => {
    return (activeBatches || []).find((b) => ['queued', 'generating', 'reserved'].includes(b.status));
  }, [activeBatches]);

  // Mirrors the assistant flow: after createBatch succeeds, take the user
  // straight to /app/batch/:id where they can watch progress live. The old
  // "generating" + "done" intermediate views were a misleading double-step
  // — the batch page already handles both states and is the source of truth.
  //
  // We deliberately do NOT call setActivity({ kind: 'active', current,
  // total, ... }) here. That used to fire the legacy "Generating X of Y"
  // pill in the topbar — but the new GenerationStatusPlate (driven by
  // the App.jsx bridge watching activeBatches) already surfaces the same
  // batch progress in a richer surface. Triggering both produced the
  // double indicator the user reported: small old pill + new plate
  // showing identical info on top of each other.
  // The new plate op was already registered above via
  // startContentBatchOperation(); this handler just navigates.
  const handleGenerated = (batch) => {
    setLastBatch(batch);
    if (batch?.videoPublishV2TikTok) {
      setState('done');
      return;
    }
    const id = batch?.id || batch?.batchId;
    if (id) {
      navigate(`/app/batch/${id}`);
    }
  };

  if (researchHold && !freePublishing) {
    return <ResearchHoldPanel />;
  }

  return (
    <div className="cp-page">
      <div className="cp-root">
        {!freePublishing && <FreeLimitsBanner compact />}
        <div className="cp-top">
          <div>
            <h1 className="cp-h">{t(manualDraftId ? 'freePublishing.editPost' : 'createPost.createPostsTitle')}</h1>
            <p className="cp-sub">
              {t(freePublishing ? 'freePublishing.editorHelp' : meaningfulBrandContext ? 'createPost.createPostsSubtitle' : 'createPost.createPostsSubtitleWithoutBrand')}
            </p>
          </div>
          {showDevSwitch && (
            <div className="scen-switch">
              <span className="scen-l">{t('createPost.state')}</span>
              {['compose', 'generating', 'done'].map((s) => (
                <button
                  key={s}
                  className={state === s ? 'is-active' : ''}
                  onClick={() => setState(s)}
                  type="button"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {state === 'compose' && <>
          {!creationAllowed && <CreationAccessNotice />}
          {composeScope.current.shown && <div key={dash.accountId} hidden={!creationAllowed}>
            {freePublishing && manual.loading ? <p role="status">{t('common.loading')}</p>
              : freePublishing && manual.blocked ? <p role="alert">{t(`freePublishing.${manual.error || 'notEditable'}`)} <Link to={`/app/post/${encodeURIComponent(manualDraftId || '')}`}>{t('freePublishing.viewPost')}</Link></p>
              : freePublishing && !manual.form.brandProfileId ? <button className="btn btn-primary" disabled={manual.disabled} onClick={manual.startWorkspace}>{t('freePublishing.startWorkspace')}</button>
              : <ComposeForm onGenerated={handleGenerated} manual={freePublishing ? manual : null} />}

          </div>}
        </>}

        {state === 'generating' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <ActiveJobsWidget
              job={{
                project: lastBatch?.topic || t('createPost.generatingBatch'),
                current: activeBatch?.ready_count || activeBatch?.readyCount || lastBatch?.readyCount || 0,
                total: activeBatch?.total_items || activeBatch?.totalItems || lastBatch?.totalItems || lastBatch?.count || 0,
                step: t('createPost.generatingPosts'),
              }}
              onOpenBatch={() => {
                const id = lastBatch?.id || activeBatch?.id;
                window.location.href = id ? `/app/batch/${id}` : '/app/library?filter=generating';
              }}
            />
            <p style={{ textAlign: 'center', color: 'var(--text-subtle)', fontSize: 13 }}>
              {t('createPost.youCanLeaveThisPage')}
            </p>
          </div>
        )}

        {state === 'done' && (
          <DoneView
            batch={lastBatch}
            onAgain={() => {
              setState('compose');
              setLastBatch(null);
              setActivity(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CreatePost;
